import React, { useState, useEffect, useRef, useMemo } from "react";
import { ReactFlow, ReactFlowProvider, Controls } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ComposedChart,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ScatterChart,
  Scatter,
} from "recharts";
import * as XLSX from "xlsx";

// Simple icon replacements using Unicode symbols
const icons = {
  Download: () => <span>💾</span>,
  Info: () => <span>ℹ️</span>,
  Trash2: () => <span>🗑️</span>,
  Plus: () => <span>➕</span>,
  RefreshCw: () => <span>🔄</span>,
  Moon: () => <span>🌙</span>,
  Sun: () => <span>☀️</span>,
  Home: () => <span>🏠</span>,
  Settings: () => <span>⚙️</span>,
  TrendingUp: () => <span>📈</span>,
  DollarSign: () => <span>💲</span>,
  Target: () => <span>🎯</span>,
  AlertTriangle: () => <span>⚠️</span>,
  Upload: () => <span>📤</span>,
  Check: () => <span>✅</span>,
  X: () => <span>❌</span>,
  ArrowRight: () => <span>➡️</span>,
  RotateCcw: () => <span>↩️</span>,
  BarChart3: () => <span>📊</span>,
  Calculator: () => <span>🧮</span>,
  Activity: () => <span>📊</span>,
  Save: () => <span>💾</span>,
  FolderOpen: () => <span>📂</span>,
  User: () => <span>👤</span>,
  Lock: () => <span>🔒</span>,
  Eye: () => <span>👁️</span>,
  EyeOff: () => <span>🙈</span>,
};

const EyeIcon = () => <span style={{ fontSize: "18px" }}>👁️</span>;
const EyeOffIcon = () => <span style={{ fontSize: "18px" }}>🙈</span>;
const LockIcon = () => <span style={{ fontSize: "18px" }}>🔒</span>;
const UserIcon = () => <span style={{ fontSize: "18px" }}>👤</span>;
const LeafIcon = () => <span style={{ fontSize: "24px" }}>🌱</span>;
const ShieldIcon = () => <span style={{ fontSize: "32px" }}>🛡️</span>;

const {
  Download,
  Info,
  Trash2,
  Plus,
  RefreshCw,
  Moon,
  Sun,
  Home,
  Settings,
  TrendingUp,
  DollarSign,
  Target,
  AlertTriangle,
  Upload,
  Check,
  X,
  ArrowRight,
  RotateCcw,
  BarChart3,
  Calculator,
  Activity,
  Save,
  FolderOpen,
  User,
  Lock,
  Eye,
  EyeOff,
} = icons;

// EN15804 modules definition
const EN15804 = {
  A1: "A1: Raw Material Supply",
  A2: "A2: Transport to Factory",
  A3: "A3: Manufacturing",
  A4: "A4: Transport to Site",
  A5: "A5: Construction/Installation",
  B1: "B1: Use",
  B2: "B2: Maintenance",
  B3: "B3: Repair",
  B4: "B4: Replacement",
  B5: "B5: Refurbishment",
  B6: "B6: Operational Energy",
  B7: "B7: Operational Water",
  C1: "C1: Deconstruction",
  C2: "C2: Transport",
  C3: "C3: Waste Processing",
  C4: "C4: Disposal",
  D: "D: Benefits & Loads Beyond System",
};

// Scope definitions
const SCOPES = {
  1: "Scope 1: Direct Emissions",
  2: "Scope 2: Indirect Energy Emissions",
  3: "Scope 3: Other Indirect Emissions",
};

// Start with empty EPD factors - users must upload their own database
const sampleFactors = {};

// Enhanced file parsing function (Fixed async/promise anti-pattern)
const parseEmissionFactorsFile = async (file) => {
  const fileName = file.name.toLowerCase();

  if (fileName.endsWith(".csv")) {
    // Handle CSV files
    const text = await file.text();
    const lines = text.split("\n").filter((line) => line.trim());

    if (lines.length < 2) {
      throw new Error(
        "CSV file must have at least a header row and one data row"
      );
    }

    // Parse header row
    const headers = lines[0]
      .split(",")
      .map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));

    // Find label and emission factor columns
    let labelIndex = -1;
    let factorIndex = -1;

    // Look for common header names
    const labelHeaders = ["label", "material", "name", "product", "item"];
    const factorHeaders = [
      "emission factor",
      "emissions",
      "factor",
      "co2",
      "carbon",
      "ef",
      "emission_factor",
    ];

    headers.forEach((header, index) => {
      if (labelHeaders.some((lh) => header.includes(lh))) {
        labelIndex = index;
      }
      if (factorHeaders.some((fh) => header.includes(fh))) {
        factorIndex = index;
      }
    });

    if (labelIndex === -1 || factorIndex === -1) {
      throw new Error(
        `Could not find required columns. Found headers: ${headers.join(
          ", "
        )}\nNeed: label/material column and emission factor column`
      );
    }

    // Parse data rows
    const epdData = {};
    let validRows = 0;

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i]
        .split(",")
        .map((v) => v.trim().replace(/['"]/g, ""));

      if (values.length > Math.max(labelIndex, factorIndex)) {
        const label = values[labelIndex];
        const factor = parseFloat(values[factorIndex]);

        if (label && !isNaN(factor) && factor >= 0) {
          epdData[label] = factor;
          validRows++;
        }
      }
    }

    if (validRows === 0) {
      throw new Error(
        "No valid data rows found. Check that your CSV has proper label and numeric emission factor columns."
      );
    }

    // Preserve exact original delay and alert formatting
    await new Promise((resolve) => setTimeout(resolve, 1000));

    alert(`📊 CSV File Processed Successfully!
          
🎯 File: "${file.name}"
📁 Size: ${(file.size / 1024).toFixed(1)} KB
📋 Headers found: ${headers.join(", ")}
✅ Loaded ${validRows} emission factors

📊 Sample entries:
${Object.entries(epdData)
  .slice(0, 5)
  .map(([material, factor]) => `• ${material}: ${factor} kg CO₂e/unit`)
  .join("\n")}${
      validRows > 5 ? `\n... and ${validRows - 5} more materials` : ""
    }`);

    return epdData;
  } else if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
    // Handle Excel files
    const arrayBuffer = await file.arrayBuffer();

    const workbook = XLSX.read(arrayBuffer, { type: "array" });

    // Get the first worksheet
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // Convert to JSON
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (jsonData.length < 2) {
      throw new Error(
        "Excel file must have at least a header row and one data row"
      );
    }

    // Parse header row
    const headers = jsonData[0].map((h) =>
      h ? h.toString().trim().toLowerCase() : ""
    );

    // Find label and emission factor columns
    let labelIndex = -1;
    let factorIndex = -1;

    const labelHeaders = ["label", "material", "name", "product", "item"];
    const factorHeaders = [
      "emission factor",
      "emissions",
      "factor",
      "co2",
      "carbon",
      "ef",
      "emission_factor",
    ];

    headers.forEach((header, index) => {
      if (labelHeaders.some((lh) => header.includes(lh))) {
        labelIndex = index;
      }
      if (factorHeaders.some((fh) => header.includes(fh))) {
        factorIndex = index;
      }
    });

    if (labelIndex === -1 || factorIndex === -1) {
      throw new Error(
        `Could not find required columns. Found headers: ${headers.join(
          ", "
        )}\nNeed: label/material column and emission factor column`
      );
    }

    // Parse data rows
    const epdData = {};
    let validRows = 0;

    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];

      if (row.length > Math.max(labelIndex, factorIndex)) {
        const label = row[labelIndex] ? row[labelIndex].toString().trim() : "";
        const factor = parseFloat(row[factorIndex]);

        if (label && !isNaN(factor) && factor >= 0) {
          epdData[label] = factor;
          validRows++;
        }
      }
    }

    if (validRows === 0) {
      throw new Error(
        "No valid data rows found. Check that your Excel file has proper label and numeric emission factor columns."
      );
    }

    // Preserve exact original delay and alert formatting
    await new Promise((resolve) => setTimeout(resolve, 1500));

    alert(`📊 Excel File Processed Successfully!
          
🎯 File: "${file.name}"
📁 Size: ${(file.size / 1024).toFixed(1)} KB
📋 Worksheet: "${firstSheetName}"
📋 Headers found: ${headers.join(", ")}
✅ Loaded ${validRows} emission factors

📊 Sample entries:
${Object.entries(epdData)
  .slice(0, 5)
  .map(([material, factor]) => `• ${material}: ${factor} kg CO₂e/unit`)
  .join("\n")}${
      validRows > 5 ? `\n... and ${validRows - 5} more materials` : ""
    }`);

    return epdData;
  } else {
    throw new Error(
      "Unsupported file format. Please upload a CSV or Excel (.xlsx/.xls) file."
    );
  }
};

// App Guide Component
const AppGuide = ({ showGuide, setShowGuide, darkMode }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // FIX: Safe close function to protect Windows focus trap
  const closeGuideSafely = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();
    setShowGuide(false);
  };

  const guideSteps = [
    {
      title: "Welcome to NZ LCA Platform",
      icon: "🌱",
      gradient: "from-green-500 to-emerald-600",
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="relative">
              <div
                className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-3xl shadow-lg transform transition-transform hover:scale-110`}
              >
                🌿
              </div>
              <div className="absolute inset-0 w-20 h-20 mx-auto bg-gradient-to-br from-green-400 to-emerald-500 rounded-full animate-ping opacity-20"></div>
            </div>
            <h3
              className={`text-2xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Life Cycle Assessment Made Simple
            </h3>
            <p
              className={`text-lg ${darkMode ? "text-white" : "text-gray-600"}`}
            >
              Professional carbon footprint analysis following EN15804 standards
            </p>
          </div>

          <div
            className={`rounded-xl p-6 border ${
              darkMode
                ? "bg-gray-800/90 backdrop-blur-sm border-gray-600"
                : "bg-blue-50 border-blue-200"
            } shadow-lg`}
          >
            <h4
              className={`font-bold text-lg mb-4 ${
                darkMode ? "text-white" : "text-blue-900"
              }`}
            >
              🎯 Platform Capabilities
            </h4>
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  icon: "📊",
                  title: "Calculate Footprints",
                  desc: "EN15804 compliant calculations",
                },
                {
                  icon: "🔍",
                  title: "Scope Analysis",
                  desc: "Detailed scope 1, 2, 3 breakdowns",
                },
                {
                  icon: "💰",
                  title: "Economic Modeling",
                  desc: "Carbon pricing & financial analysis",
                },
                {
                  icon: "📈",
                  title: "Reduction Strategies",
                  desc: "Alternative scenario modeling",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${
                    darkMode
                      ? "bg-gray-700 border border-gray-600"
                      : "bg-white border border-gray-200"
                  } shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <h5
                        className={`font-semibold ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {item.title}
                      </h5>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-white" : "text-gray-600"
                        }`}
                      >
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Upload EPD Database",
      icon: "📤",
      gradient: "from-blue-500 to-cyan-600",
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="relative">
              <div
                className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center text-3xl shadow-lg transform transition-transform hover:scale-110`}
              >
                📊
              </div>
              <div className="absolute inset-0 w-20 h-20 mx-auto bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full animate-ping opacity-20"></div>
            </div>
            <p
              className={`text-lg font-medium ${
                darkMode ? "text-white" : "text-blue-800"
              }`}
            >
              Start by uploading your emission factors database
            </p>
          </div>

          <div
            className={`rounded-xl p-6 border-l-4 border-blue-500 ${
              darkMode ? "bg-gray-800/90 backdrop-blur-sm" : "bg-blue-50"
            } shadow-lg`}
          >
            <h4
              className={`font-bold text-lg mb-4 ${
                darkMode ? "text-white" : "text-blue-900"
              }`}
            >
              📁 File Requirements
            </h4>
            <div className="grid grid-cols-3 gap-4">
              {[
                {
                  title: "Format",
                  items: ["Excel (.xlsx, .xls)", "CSV files", "UTF-8 encoding"],
                },
                {
                  title: "Columns",
                  items: [
                    "Material names",
                    "Emission factors",
                    "Units (kg CO₂e)",
                  ],
                },
                {
                  title: "Features",
                  items: [
                    "Smart column mapping",
                    "Header recognition",
                    "Data validation",
                  ],
                },
              ].map((section, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${
                    darkMode
                      ? "bg-gray-700 border border-gray-600"
                      : "bg-white border border-gray-200"
                  } shadow-sm`}
                >
                  <h5
                    className={`font-semibold mb-3 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {section.title}
                  </h5>
                  <ul className="space-y-1">
                    {section.items.map((item, idx) => (
                      <li
                        key={idx}
                        className={`text-sm flex items-center gap-2 ${
                          darkMode ? "text-white" : "text-gray-600"
                        }`}
                      >
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`rounded-xl p-6 ${
              darkMode
                ? "bg-gray-700/90 backdrop-blur-sm border border-gray-600"
                : "bg-gray-50 border border-gray-200"
            } shadow-lg`}
          >
            <h4
              className={`font-bold text-lg mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              🚀 Upload Process
            </h4>
            <div className="flex items-center justify-between">
              {[
                { step: "1", text: "Click Upload", icon: "📤", color: "blue" },
                { step: "2", text: "Select File", icon: "📁", color: "indigo" },
                {
                  step: "3",
                  text: "Auto-Process",
                  icon: "⚙️",
                  color: "purple",
                },
                { step: "4", text: "Ready!", icon: "✅", color: "green" },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center relative"
                >
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-3 bg-gradient-to-br shadow-lg transform transition-transform hover:scale-110 ${
                      item.color === "blue"
                        ? "from-blue-500 to-blue-600"
                        : item.color === "indigo"
                        ? "from-indigo-500 to-indigo-600"
                        : item.color === "purple"
                        ? "from-purple-500 to-purple-600"
                        : "from-green-500 to-green-600"
                    }`}
                  >
                    {item.icon}
                  </div>
                  <p
                    className={`text-sm font-medium text-center ${
                      darkMode ? "text-white" : "text-gray-700"
                    }`}
                  >
                    {item.step}. {item.text}
                  </p>
                  {index < 3 && (
                    <div
                      className={`absolute top-8 left-16 text-2xl ${
                        darkMode ? "text-white" : "text-gray-400"
                      }`}
                    >
                      →
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Add Processes",
      icon: "🏗️",
      gradient: "from-purple-500 to-pink-600",
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="relative">
              <div
                className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-3xl shadow-lg transform transition-transform hover:scale-110`}
              >
                ⚙️
              </div>
              <div className="absolute inset-0 w-20 h-20 mx-auto bg-gradient-to-br from-purple-400 to-pink-500 rounded-full animate-ping opacity-20"></div>
            </div>
            <p
              className={`text-lg ${darkMode ? "text-white" : "text-gray-600"}`}
            >
              Build your assessment using EN15804 lifecycle modules
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              {
                title: "A1-A3: Product Stage",
                desc: "Raw materials, transport, manufacturing",
                color: "from-red-500 to-red-600",
                border: "border-red-500",
              },
              {
                title: "B1-B7: Use Stage",
                desc: "Installation, maintenance, operation",
                color: "from-yellow-500 to-orange-500",
                border: "border-yellow-500",
              },
              {
                title: "C1-C4: End of Life",
                desc: "Demolition, transport, disposal",
                color: "from-purple-500 to-purple-600",
                border: "border-purple-500",
              },
              {
                title: "D: Benefits & Loads",
                desc: "Recycling, energy recovery",
                color: "from-green-500 to-green-600",
                border: "border-green-500",
              },
            ].map((module, index) => (
              <div
                key={index}
                className={`p-4 rounded-xl border-l-4 ${module.border} ${
                  darkMode ? "bg-gray-800/90 backdrop-blur-sm" : "bg-white"
                } shadow-lg hover:shadow-xl transition-shadow`}
              >
                <div
                  className={`w-12 h-12 bg-gradient-to-br ${module.color} rounded-lg flex items-center justify-center text-xl mb-3 shadow-md`}
                >
                  {index === 0
                    ? "🏭"
                    : index === 1
                    ? "🔧"
                    : index === 2
                    ? "🗑️"
                    : "♻️"}
                </div>
                <h5
                  className={`font-bold text-lg mb-2 ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  {module.title}
                </h5>
                <p
                  className={`text-sm ${
                    darkMode ? "text-white" : "text-gray-600"
                  }`}
                >
                  {module.desc}
                </p>
              </div>
            ))}
          </div>

          <div
            className={`rounded-xl p-6 ${
              darkMode
                ? "bg-gray-700/90 backdrop-blur-sm border border-gray-600"
                : "bg-blue-50 border border-blue-200"
            } shadow-lg`}
          >
            <h4
              className={`font-bold text-lg mb-4 ${
                darkMode ? "text-white" : "text-blue-900"
              }`}
            >
              ➕ Process Creation Workflow
            </h4>
            <div className="space-y-3">
              {[
                "Click any module button (A1, A2, B1, etc.)",
                "Enter process details (title, description, mass)",
                "Select GHG Protocol scope (1, 2, or 3)",
                "Choose EPD factor or enter custom value",
                "Group by operations (optional)",
                "Save to see in interactive flow diagram",
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-md`}
                  >
                    {index + 1}
                  </div>
                  <p className={`${darkMode ? "text-white" : "text-gray-700"}`}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Analyze Results",
      icon: "📊",
      gradient: "from-green-500 to-teal-600",
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="relative">
              <div
                className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center text-3xl shadow-lg transform transition-transform hover:scale-110`}
              >
                📈
              </div>
              <div className="absolute inset-0 w-20 h-20 mx-auto bg-gradient-to-br from-green-400 to-teal-500 rounded-full animate-ping opacity-20"></div>
            </div>
            <p
              className={`text-lg ${darkMode ? "text-white" : "text-gray-600"}`}
            >
              Powerful visualization and analysis tools at your fingertips
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              {
                icon: "🏠",
                title: "Life Cycle Assessment",
                subtitle: "Interactive Flow",
                desc: "Visual process flow with real-time calculations",
                color: "from-blue-500 to-blue-600",
                border: "border-blue-500",
              },
              {
                icon: "🥧",
                title: "Pie Chart Analysis",
                subtitle: "Process Breakdown",
                desc: "Color-coded emissions by process",
                color: "from-purple-500 to-purple-600",
                border: "border-purple-500",
              },
              {
                icon: "🔄",
                title: "Reduction Analysis",
                subtitle: "Scenario Comparison",
                desc: "Test alternatives in separate tabs",
                color: "from-orange-500 to-red-500",
                border: "border-orange-500",
              },
              {
                icon: "💰",
                title: "Economic Modeling",
                subtitle: "Financial Analysis",
                desc: "NPV, IRR with carbon pricing",
                color: "from-green-500 to-green-600",
                border: "border-green-500",
              },
            ].map((tool, index) => (
              <div
                key={index}
                className={`p-5 rounded-xl border-2 ${tool.border} ${
                  darkMode
                    ? "bg-gray-800/90 backdrop-blur-sm hover:bg-gray-700/90"
                    : "bg-white hover:bg-gray-50"
                } shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`w-12 h-12 bg-gradient-to-br ${tool.color} rounded-lg flex items-center justify-center text-2xl shadow-md`}
                  >
                    {tool.icon}
                  </div>
                  <div>
                    <h5
                      className={`font-bold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {tool.title}
                    </h5>
                    <p
                      className={`text-sm ${
                        darkMode
                          ? "text-white"
                          : tool.color.includes("blue")
                          ? "text-blue-600"
                          : tool.color.includes("purple")
                          ? "text-purple-600"
                          : tool.color.includes("orange")
                          ? "text-orange-600"
                          : "text-green-600"
                      }`}
                    >
                      {tool.subtitle}
                    </p>
                  </div>
                </div>
                <p
                  className={`text-sm ${
                    darkMode ? "text-white" : "text-gray-600"
                  }`}
                >
                  {tool.desc}
                </p>
              </div>
            ))}
          </div>

          <div
            className={`rounded-xl p-6 ${
              darkMode
                ? "bg-gradient-to-r from-gray-800/90 to-gray-700/90 backdrop-blur-sm border border-gray-600"
                : "bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200"
            } shadow-lg`}
          >
            <h4
              className={`font-bold text-lg mb-4 ${
                darkMode ? "text-white" : "text-blue-900"
              }`}
            >
              🎯 Key Insights & Analytics
            </h4>
            <div className="grid grid-cols-3 gap-4">
              {[
                {
                  icon: "📊",
                  title: "Hotspot Analysis",
                  desc: "Identify major contributors",
                },
                {
                  icon: "🔍",
                  title: "Scope Breakdown",
                  desc: "Detailed scope 1,2,3 analysis",
                },
                {
                  icon: "💡",
                  title: "Optimization",
                  desc: "Reduction opportunities",
                },
              ].map((insight, index) => (
                <div key={index} className="text-center">
                  <div
                    className={`w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-2xl shadow-lg`}
                  >
                    {insight.icon}
                  </div>
                  <h6
                    className={`font-semibold mb-1 ${
                      darkMode ? "text-white" : "text-blue-800"
                    }`}
                  >
                    {insight.title}
                  </h6>
                  <p
                    className={`text-sm ${
                      darkMode ? "text-white" : "text-blue-600"
                    }`}
                  >
                    {insight.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Project Management",
      icon: "💼",
      gradient: "from-indigo-500 to-purple-600",
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="relative">
              <div
                className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-3xl shadow-lg transform transition-transform hover:scale-110`}
              >
                💾
              </div>
              <div className="absolute inset-0 w-20 h-20 mx-auto bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full animate-ping opacity-20"></div>
            </div>
            <p
              className={`text-lg ${darkMode ? "text-white" : "text-gray-600"}`}
            >
              Professional project management and collaboration tools
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {[
              {
                icon: "💾",
                title: "Save Project",
                color: "from-green-500 to-green-600",
                border: "border-green-500",
                features: [
                  "Complete backup",
                  "All processes & EPD",
                  "Carbon pricing settings",
                  "JSON format",
                ],
              },
              {
                icon: "📂",
                title: "Load Project",
                color: "from-blue-500 to-blue-600",
                border: "border-blue-500",
                features: [
                  "Instant restoration",
                  "Complete state recovery",
                  "Team collaboration",
                  "Version control",
                ],
              },
              {
                icon: "📄",
                title: "Export Report",
                color: "from-orange-500 to-red-500",
                border: "border-orange-500",
                features: [
                  "Professional PDF",
                  "EN15804 compliant",
                  "Executive summaries",
                  "Stakeholder ready",
                ],
              },
            ].map((feature, index) => (
              <div
                key={index}
                className={`p-5 rounded-xl border-l-4 ${feature.border} ${
                  darkMode ? "bg-gray-800/90 backdrop-blur-sm" : "bg-white"
                } shadow-lg hover:shadow-xl transition-shadow`}
              >
                <div className="text-center mb-4">
                  <div
                    className={`w-16 h-16 mx-auto mb-3 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center text-3xl shadow-lg`}
                  >
                    {feature.icon}
                  </div>
                  <h5
                    className={`font-bold ${
                      darkMode ? "text-white" : "text-gray-800"
                    }`}
                  >
                    {feature.title}
                  </h5>
                </div>
                <ul className="space-y-2">
                  {feature.features.map((item, idx) => (
                    <li
                      key={idx}
                      className={`text-sm flex items-center gap-2 ${
                        darkMode ? "text-white" : "text-gray-600"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          feature.color.includes("green")
                            ? "bg-green-500"
                            : feature.color.includes("blue")
                            ? "bg-blue-500"
                            : "bg-orange-500"
                        }`}
                      ></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "You're Ready!",
      icon: "🚀",
      gradient: "from-yellow-500 to-orange-600",
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="relative">
              <div
                className={`w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center text-4xl shadow-lg transform transition-transform hover:scale-110 animate-pulse`}
              >
                🎯
              </div>
              <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-ping opacity-30"></div>
            </div>
            <h3
              className={`text-2xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Ready to Start Your LCA Journey!
            </h3>
            <p
              className={`text-lg ${darkMode ? "text-white" : "text-gray-600"}`}
            >
              Follow this proven workflow for successful projects
            </p>
          </div>

          <div
            className={`rounded-xl p-6 ${
              darkMode
                ? "bg-gradient-to-br from-gray-800/90 to-gray-700/90 backdrop-blur-sm border border-gray-600"
                : "bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200"
            } shadow-lg`}
          >
            <h4
              className={`font-bold text-xl mb-6 text-center ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              🔥 Recommended Workflow
            </h4>
            <div className="space-y-4">
              {[
                {
                  icon: "📤",
                  title: "Upload EPD Database",
                  desc: "Start with comprehensive emission factors",
                  priority: "HIGH",
                  color: "from-red-500 to-red-600",
                },
                {
                  icon: "⚙️",
                  title: "Add Key Processes",
                  desc: "Begin with highest impact modules (A1-A3)",
                  priority: "HIGH",
                  color: "from-red-500 to-red-600",
                },
                {
                  icon: "📊",
                  title: "Review Pie Chart",
                  desc: "Identify major emission contributors",
                  priority: "MED",
                  color: "from-yellow-500 to-orange-500",
                },
                {
                  icon: "🔄",
                  title: "Test Alternatives",
                  desc: "Explore reduction strategies",
                  priority: "MED",
                  color: "from-yellow-500 to-orange-500",
                },
                {
                  icon: "💰",
                  title: "Economic Analysis",
                  desc: "Model business case with carbon pricing",
                  priority: "MED",
                  color: "from-yellow-500 to-orange-500",
                },
                {
                  icon: "💾",
                  title: "Save & Report",
                  desc: "Preserve work and generate reports",
                  priority: "HIGH",
                  color: "from-red-500 to-red-600",
                },
              ].map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-4 p-4 rounded-xl ${
                    darkMode
                      ? "bg-gray-700/90 backdrop-blur-sm border border-gray-600"
                      : "bg-white border border-gray-200"
                  } shadow-sm hover:shadow-md transition-shadow border-l-4 ${
                    step.priority === "HIGH"
                      ? "border-red-500"
                      : "border-yellow-500"
                  }`}
                >
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl bg-gradient-to-br ${step.color} shadow-lg`}
                  >
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <h5
                      className={`font-bold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {index + 1}. {step.title}
                    </h5>
                    <p
                      className={`text-sm ${
                        darkMode ? "text-white" : "text-gray-600"
                      }`}
                    >
                      {step.desc}
                    </p>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-bold ${
                      step.priority === "HIGH"
                        ? darkMode
                          ? "bg-red-800 text-red-200"
                          : "bg-red-100 text-red-800"
                        : darkMode
                        ? "bg-yellow-800 text-yellow-200"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {step.priority}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div
              className={`p-6 rounded-xl ${
                darkMode
                  ? "bg-green-800/90 backdrop-blur-sm border border-green-600"
                  : "bg-green-50 border border-green-200"
              } text-center shadow-lg`}
            >
              <div className="text-3xl mb-3">💡</div>
              <h5
                className={`font-bold mb-2 ${
                  darkMode ? "text-white" : "text-green-800"
                }`}
              >
                Pro Tip
              </h5>
              <p
                className={`text-sm ${
                  darkMode ? "text-white" : "text-green-700"
                }`}
              >
                Start small with 3-5 key processes, then expand progressively
                for better accuracy!
              </p>
            </div>
            <div
              className={`p-6 rounded-xl ${
                darkMode
                  ? "bg-blue-800/90 backdrop-blur-sm border border-blue-600"
                  : "bg-blue-50 border border-blue-200"
              } text-center shadow-lg`}
            >
              <div className="text-3xl mb-3">🎯</div>
              <h5
                className={`font-bold mb-2 ${
                  darkMode ? "text-white" : "text-blue-800"
                }`}
              >
                Success Focus
              </h5>
              <p
                className={`text-sm ${
                  darkMode ? "text-white" : "text-blue-700"
                }`}
              >
                A1-A3 modules typically represent 60-80% of total environmental
                impact!
              </p>
            </div>
          </div>

          <div
            className={`text-center p-6 rounded-xl ${
              darkMode
                ? "bg-gradient-to-r from-purple-800/90 to-blue-800/90 backdrop-blur-sm border border-purple-600"
                : "bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200"
            } shadow-lg`}
          >
            <div className="text-4xl mb-3">🌟</div>
            <h4
              className={`font-bold text-lg mb-2 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Ready to Create Environmental Impact
            </h4>
            <p className={`${darkMode ? "text-white" : "text-gray-700"}`}>
              You now have everything needed to conduct professional-grade life
              cycle assessments. Let's build a sustainable future together!
            </p>
          </div>
        </div>
      ),
    },
  ];

  if (!showGuide) return null;

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border ${
          darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-300"
        }`}
        style={{
          backgroundColor: darkMode ? undefined : "#ffffff",
          boxShadow: darkMode
            ? "0 25px 50px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)"
            : "0 25px 50px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Header */}
        <div
          className={`p-6 border-b ${
            darkMode
              ? "border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900"
              : "border-gray-300 bg-gradient-to-r from-gray-50 to-gray-100"
          }`}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div
                className={`text-4xl transform transition-transform hover:scale-110`}
              >
                {guideSteps[currentStep].icon}
              </div>
              <div>
                <h2
                  className={`text-2xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {guideSteps[currentStep].title}
                </h2>
                <p
                  className={`text-sm mt-1 ${
                    darkMode ? "text-white" : "text-gray-600"
                  }`}
                >
                  Step {currentStep + 1} of {guideSteps.length} • Professional
                  LCA Guide
                </p>
              </div>
            </div>
            <button
              onClick={closeGuideSafely}
              className={`p-3 rounded-full transition-all duration-300 transform hover:scale-110 ${
                darkMode
                  ? "text-white hover:bg-gray-700"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"
              }`}
            >
              <X size={24} />
            </button>
          </div>

          {/* Enhanced Progress bar */}
          <div
            className={`mt-6 w-full rounded-full h-3 ${
              darkMode ? "bg-gray-800" : "bg-gray-200"
            } overflow-hidden`}
          >
            <div
              className={`h-3 rounded-full transition-all duration-700 ease-out bg-gradient-to-r ${guideSteps[currentStep].gradient} shadow-lg`}
              style={{
                width: `${((currentStep + 1) / guideSteps.length) * 100}%`,
              }}
            />
          </div>
          <div className="flex justify-between text-xs mt-2">
            <span className={darkMode ? "text-white" : "text-gray-600"}>
              Getting Started
            </span>
            <span className={darkMode ? "text-white" : "text-gray-600"}>
              Ready to Go!
            </span>
          </div>
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 200px)" }}
        >
          <div>{guideSteps[currentStep].content}</div>
        </div>

        {/* Footer */}
        <div
          className={`p-6 border-t ${
            darkMode
              ? "border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900"
              : "border-gray-300 bg-gradient-to-r from-gray-50 to-gray-100"
          }`}
        >
          <div className="flex justify-between items-center">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className={`px-6 py-3 border-2 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                currentStep === 0
                  ? "opacity-50 cursor-not-allowed"
                  : darkMode
                  ? "border-gray-600 text-white hover:bg-gray-700 hover:border-gray-500"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400"
              }`}
            >
              ← Previous
            </button>

            <div className="flex items-center gap-3">
              {guideSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-4 h-4 rounded-full transition-all duration-300 transform hover:scale-125 ${
                    index === currentStep
                      ? `bg-gradient-to-r ${guideSteps[index].gradient} scale-125 shadow-lg`
                      : index < currentStep
                      ? "bg-green-500 shadow-md"
                      : darkMode
                      ? "bg-gray-600 hover:bg-gray-500"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>

            {currentStep < guideSteps.length - 1 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 bg-gradient-to-r ${guideSteps[currentStep].gradient} text-white shadow-lg hover:shadow-xl`}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={closeGuideSafely}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl hover:from-green-700 hover:to-blue-700 font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                🚀 Start Using App
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Dialog Component
const NZLCALockScreen = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    setLoginError("");

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    if (
      credentials.username === "hari" &&
      credentials.password === "propertyofhari"
    ) {
      // FIX: Drop focus before unmounting so Windows doesn't trap the cursor
      if (document.activeElement) document.activeElement.blur();
      window.focus();

      onLogin(true);
    } else {
      setLoginError(
        "Invalid credentials. Please check your username and password."
      );
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (
      e.key === "Enter" &&
      !isLoading &&
      credentials.username &&
      credentials.password
    ) {
      // FIX: Drop focus if they submitted by pressing Enter in the text input
      if (document.activeElement) document.activeElement.blur();
      handleLogin();
    }
  };

  if (isUnlocked) {
    return (
      <div className="lock-screen unlocked">
        <div className="unlock-animation">
          <div className="success-icon">✅</div>
          <h2>System Unlocked</h2>
          <p>Welcome to NZ LCA Software</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lock-screen">
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .lock-screen {
          min-height: 100vh;
          background: linear-gradient(135deg, #0a0a0f 0%, #1a1625 50%, #211f37 100%);
          position: relative;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .lock-screen::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, rgba(31, 41, 55, 0.1) 0%, rgba(55, 65, 81, 0.1) 100%);
          backdrop-filter: blur(20px);
        }

        .background-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0.1;
          background-image: 
            radial-gradient(circle at 25% 25%, rgba(108, 92, 231, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.2) 0%, transparent 50%);
          animation: float 20s ease-in-out infinite, neonPulse 4s ease-in-out infinite;
        }

        @keyframes neonPulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.2; }
        }

        .neon-dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease;
        }

        .neon-dialog {
          background: linear-gradient(135deg, #1a1625 0%, #211f37 100%);
          border: 2px solid #6C5CE7;
          border-radius: 20px;
          padding: 40px 30px;
          max-width: 400px;
          width: 90%;
          text-align: center;
          box-shadow: 
            0 0 30px rgba(108, 92, 231, 0.5),
            0 0 60px rgba(139, 92, 246, 0.3),
            0 0 100px rgba(168, 85, 247, 0.2),
            inset 0 0 30px rgba(108, 92, 231, 0.1);
          animation: dialogSlideIn 0.5s ease;
          position: relative;
          overflow: hidden;
        }

        .neon-dialog::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, rgba(108, 92, 231, 0.1) 0%, transparent 100%);
          pointer-events: none;
        }

        .neon-dialog-content {
          position: relative;
          z-index: 1;
        }

        .neon-dialog-icon {
          font-size: 4rem;
          margin-bottom: 20px;
          animation: bounce 1s ease, iconGlow 2s ease-in-out infinite;
          filter: drop-shadow(0 0 20px rgba(108, 92, 231, 0.8));
        }

        .neon-dialog-title {
          color: white;
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 15px;
          text-shadow: 
            0 0 20px rgba(108, 92, 231, 0.8),
            0 0 40px rgba(139, 92, 246, 0.6);
        }

        .neon-dialog-message {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1.1rem;
          margin-bottom: 30px;
          line-height: 1.5;
        }

        .neon-dialog-button {
          background: linear-gradient(135deg, #6C5CE7 0%, #8B5CF6 50%, #A855F7 100%);
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 
            0 4px 15px rgba(108, 92, 231, 0.4),
            0 0 20px rgba(139, 92, 246, 0.3);
        }

        .neon-dialog-button:hover {
          transform: translateY(-2px);
          box-shadow: 
            0 8px 25px rgba(108, 92, 231, 0.5),
            0 0 30px rgba(139, 92, 246, 0.5),
            0 0 50px rgba(168, 85, 247, 0.3);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes dialogSlideIn {
          from {
            opacity: 0;
            transform: translateY(-30px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes iconGlow {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(108, 92, 231, 0.8)); }
          50% { filter: drop-shadow(0 0 30px rgba(139, 92, 246, 1)); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(1deg); }
        }

        .main-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 20px;
          position: relative;
          z-index: 10;
        }

        .login-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.15),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            0 0 30px rgba(108, 92, 231, 0.2);
          width: 100%;
          max-width: 420px;
          padding: 0;
          overflow: hidden;
          transform: translateY(0);
          transition: all 0.3s ease;
        }

        .login-card:hover {
          transform: translateY(-5px);
          box-shadow: 
            0 35px 70px rgba(0, 0, 0, 0.2),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            0 0 50px rgba(108, 92, 231, 0.3),
            0 0 80px rgba(139, 92, 246, 0.2);
        }

        .card-header {
          background: linear-gradient(135deg, #6C5CE7 0%, #8B5CF6 50%, #A855F7 100%);
          padding: 40px 32px;
          text-align: center;
          position: relative;
          box-shadow: 0 0 30px rgba(108, 92, 231, 0.5), 0 0 60px rgba(139, 92, 246, 0.3);
        }

        .card-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%);
        }

        .logo-container {
          position: relative;
          z-index: 1;
        }

        .main-logo {
          width: 90px;
          height: 90px;
          background: #0f0f0f;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          border: 3px solid #6C5CE7;
          position: relative;
          box-shadow: 
            0 0 0 2px #0f0f0f,
            0 8px 25px rgba(0, 0, 0, 0.4),
            0 0 20px rgba(108, 92, 231, 0.6),
            0 0 40px rgba(108, 92, 231, 0.4);
        }

        .logo-content {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .logo-text-left {
          position: absolute;
          left: 12px;
          color: white;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .logo-text-right {
          position: absolute;
          right: 8px;
          color: white;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .logo-fern {
          color: white;
          font-size: 24px;
          position: relative;
          z-index: 1;
        }

        .logo-diamond {
          position: absolute;
          color: white;
          font-size: 8px;
          opacity: 0.8;
        }

        .diamond-top {
          top: 8px;
          left: 50%;
          transform: translateX(-50%);
        }

        .diamond-bottom {
          bottom: 8px;
          left: 50%;
          transform: translateX(-50%);
        }

        .diamond-left {
          left: 8px;
          top: 50%;
          transform: translateY(-50%);
        }

        .diamond-right {
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
        }

        .company-name {
          color: white;
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 8px;
          text-shadow: 
            0 2px 10px rgba(0,0,0,0.2),
            0 0 20px rgba(108, 92, 231, 0.8),
            0 0 40px rgba(139, 92, 246, 0.6);
          letter-spacing: 1px;
        }

        .company-tagline {
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.95rem;
          font-weight: 400;
          letter-spacing: 0.5px;
        }

        .card-body {
          padding: 40px 32px;
        }

        .welcome-text {
          text-align: center;
          margin-bottom: 32px;
        }

        .welcome-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
        }

        .welcome-subtitle {
          color: #6b7280;
          font-size: 0.95rem;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          color: #374151;
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .input-container {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          z-index: 1;
        }

        .form-input {
          width: 100%;
          padding: 16px 16px 16px 50px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 1rem;
          background: #ffffff;
          transition: all 0.2s ease;
          outline: none;
        }

        .form-input:focus {
          border-color: #6C5CE7;
          box-shadow: 
            0 0 0 3px rgba(108, 92, 231, 0.2),
            0 0 15px rgba(108, 92, 231, 0.4),
            0 0 30px rgba(139, 92, 246, 0.2);
        }

        .form-input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .password-toggle {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: color 0.2s ease;
        }

        .password-toggle:hover {
          color: #6b7280;
        }

        .error-message {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.875rem;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 0 15px rgba(220, 38, 38, 0.2);
        }

        .login-button {
          width: 100%;
          background: linear-gradient(135deg, #6C5CE7 0%, #8B5CF6 50%, #A855F7 100%);
          color: white;
          border: none;
          padding: 16px;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 
            0 4px 15px rgba(108, 92, 231, 0.4),
            0 0 20px rgba(139, 92, 246, 0.3);
        }

        .login-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 
            0 10px 25px rgba(108, 92, 231, 0.4),
            0 0 30px rgba(139, 92, 246, 0.5),
            0 0 50px rgba(168, 85, 247, 0.3);
        }

        .login-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .attribution-notice {
          position: absolute;
          bottom: 15px;
          left: 50%;
          transform: translateX(-50%);
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.75rem;
          text-align: center;
          max-width: 90%;
        }

        .attribution-main {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }

        .license-text {
          font-size: 0.65rem;
          color: rgba(255, 255, 255, 0.7);
          font-style: italic;
          line-height: 1.3;
        }

        .unlocked {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #6C5CE7 0%, #8B5CF6 50%, #A855F7 100%);
        }

        .unlock-animation {
          text-align: center;
          color: white;
          animation: fadeInUp 0.8s ease;
        }

        .success-icon {
          font-size: 4rem;
          margin-bottom: 20px;
          animation: bounce 1s ease;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translateY(0);
          }
          40%, 43% {
            transform: translateY(-20px);
          }
          70% {
            transform: translateY(-10px);
          }
          90% {
            transform: translateY(-5px);
          }
        }

        @media (max-width: 480px) {
          .current-time {
            font-size: 3rem;
          }
          
          .login-card {
            margin: 20px;
            max-width: none;
          }
          
          .card-header,
          .card-body {
            padding: 24px 20px;
          }
          
          .company-name {
            font-size: 1.5rem;
          }
        }
      `}</style>

      <div className="background-pattern"></div>

      <div className="main-container">
        <div className="login-card">
          <div className="card-header">
            <div className="logo-container">
              <div className="main-logo">
                <div className="logo-content">
                  <div className="logo-text-left">NZ</div>
                  <div className="logo-fern">🌿</div>
                  <div className="logo-text-right">LCA</div>
                  <div className="logo-diamond diamond-top">◆</div>
                  <div className="logo-diamond diamond-bottom">◆</div>
                  <div className="logo-diamond diamond-left">◆</div>
                  <div className="logo-diamond diamond-right">◆</div>
                </div>
              </div>
              <h1 className="company-name">NZ LCA</h1>
              <p className="company-tagline">Life Cycle Assessment Platform</p>
            </div>
          </div>

          <div className="card-body">
            <div className="welcome-text">
              <h2 className="welcome-title">System Locked</h2>
              <p className="welcome-subtitle">
                Enter your credentials to unlock the platform
              </p>
            </div>

            <div>
              <div className="form-group">
                <div className="form-label">Username</div>
                <div className="input-container">
                  <div className="input-icon">
                    <UserIcon />
                  </div>
                  <input
                    type="text"
                    className={`form-input ${loginError ? "error" : ""}`}
                    value={credentials.username}
                    onChange={(e) =>
                      setCredentials((prev) => ({
                        ...prev,
                        username: e.target.value,
                      }))
                    }
                    onKeyPress={handleKeyPress}
                    placeholder="Enter your username"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="form-label">Password</div>
                <div className="input-container">
                  <div className="input-icon">
                    <LockIcon />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`form-input ${loginError ? "error" : ""}`}
                    value={credentials.password}
                    onChange={(e) =>
                      setCredentials((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    onKeyPress={handleKeyPress}
                    placeholder="Enter your password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {loginError && (
                <div className="error-message">
                  <span>⚠️</span>
                  {loginError}
                </div>
              )}

              <button
                onClick={handleLogin}
                className="login-button"
                disabled={
                  isLoading || !credentials.username || !credentials.password
                }
              >
                <div className="button-content">
                  {isLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      <span>Unlocking System...</span>
                    </>
                  ) : (
                    <>
                      <span>🔓</span>
                      <span>Unlock System</span>
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="attribution-notice">
        <div className="attribution-main">
          <LeafIcon />
          <span>
            Property of University of Auckland • Harisankar Menon • Satya
            Narayana Gattu
          </span>
        </div>
        <div className="license-text">
          Licensed under MIT License • React (MIT) • CSS3 & HTML5 Standards
        </div>
      </div>

      {showSuccessDialog && (
        <div className="neon-dialog-overlay">
          <div className="neon-dialog">
            <div className="neon-dialog-content">
              <div className="neon-dialog-icon">🌱</div>
              <h2 className="neon-dialog-title">Welcome to NZ LCA Software!</h2>
              <p className="neon-dialog-message">
                You have successfully unlocked the system.
                <br />
                Access granted to Life Cycle Assessment Platform.
              </p>
              <button
                className="neon-dialog-button"
                onClick={() => {
                  // FIX: Safe focus drop
                  if (document.activeElement) document.activeElement.blur();
                  window.focus();
                  setShowSuccessDialog(false);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// MiniMap, FlowControls, Background Components
const MiniMap = () => (
  <div
    className="react-flow-minimap"
    style={{
      position: "absolute",
      bottom: "10px",
      right: "10px",
      background: "white",
      padding: "10px",
      borderRadius: "5px",
    }}
  />
);

const FlowControls = () => (
  <div
    className="react-flow-controls"
    style={{
      position: "absolute",
      top: "10px",
      right: "10px",
      background: "white",
      padding: "10px",
      borderRadius: "5px",
    }}
  />
);

const Background = () => (
  <div
    className="react-flow-background"
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'url(\'data:image/svg+xml;charset=utf-8,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23e5e7eb" stroke-width="1"/></pattern></defs><rect width="100%" fill="url(%23grid)" /></svg>\')',
      backgroundSize: "20px 20px",
    }}
  />
);

// Carbon Pricing Selection Dialog Component
// Carbon Pricing Selection Dialog Component
const CarbonPricingDialog = ({
  showDialog,
  setShowDialog,
  onSelectPricing,
  darkMode,
}) => {
  const [selectedOption, setSelectedOption] = useState("");

  const pricingOptions = [
    {
      id: "ets",
      title: "Emissions Trading System (ETS)",
      description: "Cap-and-trade system with allowance trading",
      icon: "🏭",
      details: [
        "• Scope 1 emissions coverage",
        "• Free allocation available",
        "• Market-based pricing",
        "• Trading flexibility",
      ],
    },
    {
      id: "carbonTax",
      title: "Carbon Tax",
      description: "Direct tax on carbon emissions",
      icon: "💰",
      details: [
        "• Scope 1 & 2 emissions coverage",
        "• Fixed rate per ton CO₂e",
        "• Predictable cost escalation",
        "• Direct regulatory compliance",
      ],
    },
    {
      id: "both",
      title: "Hybrid System",
      description: "Both ETS and Carbon Tax apply",
      icon: "⚖️",
      details: [
        "• ETS for large emitters (Scope 1)",
        "• Carbon tax for smaller sources (Scope 2)",
        "• Complex regulatory environment",
        "• Higher compliance costs",
      ],
    },
  ];

  const handleClose = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();
    setShowDialog(false);
  };

  const handleConfirm = () => {
    if (selectedOption) {
      if (document.activeElement) document.activeElement.blur();
      window.focus();
      onSelectPricing(selectedOption);
      setShowDialog(false);
      setSelectedOption("");
    }
  };

  if (!showDialog) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border ${
          darkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"
        }`}
      >
        <div
          className={`p-6 border-b ${
            darkMode ? "border-gray-600" : "border-gray-200"
          }`}
        >
          <div className="flex justify-between items-center">
            <div>
              <h3
                className={`text-2xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                🌍 Carbon Pricing Mechanism Selection
              </h3>
              <p
                className={`text-sm mt-2 ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Choose the carbon pricing system that applies to your
                organization
              </p>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              ❌
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {pricingOptions.map((option) => (
              <div
                key={option.id}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                  selectedOption === option.id
                    ? darkMode
                      ? "border-blue-500 bg-blue-900 bg-opacity-30"
                      : "border-blue-500 bg-blue-50"
                    : darkMode
                    ? "border-gray-600 bg-gray-700 hover:border-gray-500"
                    : "border-gray-200 bg-gray-50 hover:border-gray-300"
                }`}
                onClick={() => setSelectedOption(option.id)}
              >
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">{option.icon}</div>
                  <h4
                    className={`text-lg font-bold mb-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {option.title}
                  </h4>
                  <p
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {option.description}
                  </p>
                </div>
                <div
                  className={`text-xs space-y-1 ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {option.details.map((detail, index) => (
                    <div key={index}>{detail}</div>
                  ))}
                </div>
                {selectedOption === option.id && (
                  <div className="mt-4 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      ✓ Selected
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div
            className={`p-4 rounded-lg border ${
              darkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <h5
              className={`font-medium mb-2 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              💡 Selection Guide:
            </h5>
            <div
              className={`text-sm space-y-1 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <p>
                <strong>Choose ETS if:</strong> You're a large industrial
                emitter with allocated allowances
              </p>
              <p>
                <strong>Choose Carbon Tax if:</strong> You face direct carbon
                taxation on fuel/energy use
              </p>
              <p>
                <strong>Choose Hybrid if:</strong> Your organization faces both
                systems simultaneously
              </p>
            </div>
          </div>
        </div>

        <div
          className={`p-6 border-t ${
            darkMode
              ? "border-gray-600 bg-gray-800"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex justify-between items-center">
            <div
              className={`text-sm ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {selectedOption
                ? `Selected: ${
                    pricingOptions.find((o) => o.id === selectedOption)?.title
                  }`
                : "Please select a carbon pricing mechanism"}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className={`px-6 py-2 border rounded-lg font-medium transition-colors ${
                  darkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!selectedOption}
                className={`px-8 py-2 rounded-lg font-medium transition-colors ${
                  selectedOption
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function EconomicModeling({
  entries = [],
  factors = {},
  darkMode = false,
  totalEmissions = 0,
  onUndo,
  selectedPricingSystem,
  setSelectedPricingSystem,
  carbonPricing,
  setCarbonPricing,
  showPricingDialog,
  setShowPricingDialog,
  economicInputs,
  setEconomicInputs,
  setProjectCashFlows,
}) {
  const [economicTab, setEconomicTab] = useState("overview");
  const [showInfo, setShowInfo] = useState(false);
  const [npvResult, setNpvResult] = useState(0);
  const [irrResult, setIrrResult] = useState(0);
  const [paybackResult, setPaybackResult] = useState(Infinity);
  const [sensitivityData, setSensitivityData] = useState([]);
  const [localCashFlows, setLocalCashFlows] = useState([]);

  const emissionsBreakdown = useMemo(() => {
    return {
      scope1: entries
        .filter((e) => e.scope === "1")
        .reduce((sum, e) => sum + e.total, 0),
      scope2: entries
        .filter((e) => e.scope === "2")
        .reduce((sum, e) => sum + e.total, 0),
      scope3: entries
        .filter((e) => e.scope === "3")
        .reduce((sum, e) => sum + e.total, 0),
    };
  }, [entries]);

  const calculateCarbonCosts = (year = 0) => {
    const costs = { carbonTax: 0, etsAllowances: 0, offsets: 0, total: 0 };
    const totalEmissionsInTons = totalEmissions / 1000;
    let remainingScope1 = emissionsBreakdown.scope1 / 1000;
    let remainingScope2 = emissionsBreakdown.scope2 / 1000;

    if (carbonPricing.offsets.enabled) {
      const offsetTons =
        totalEmissionsInTons * (carbonPricing.offsets.maxPercentage / 100);
      costs.offsets = offsetTons * carbonPricing.offsets.price;
      const totalScope12 = remainingScope1 + remainingScope2;
      if (totalScope12 > 0) {
        const scope1Ratio = remainingScope1 / totalScope12;
        const scope2Ratio = remainingScope2 / totalScope12;
        remainingScope1 = Math.max(
          0,
          remainingScope1 - offsetTons * scope1Ratio
        );
        remainingScope2 = Math.max(
          0,
          remainingScope2 - offsetTons * scope2Ratio
        );
      }
    }

    if (
      carbonPricing.carbonTax.enabled &&
      (carbonPricing.system === "carbonTax" || carbonPricing.system === "both")
    ) {
      const taxableEmissions =
        carbonPricing.system === "both"
          ? remainingScope2
          : remainingScope1 + remainingScope2;
      const adjustedRate =
        carbonPricing.carbonTax.rate *
        Math.pow(1 + carbonPricing.carbonTax.escalationRate / 100, year);
      costs.carbonTax = taxableEmissions * adjustedRate;
    }

    if (
      carbonPricing.ets.enabled &&
      (carbonPricing.system === "ets" || carbonPricing.system === "both")
    ) {
      const coveredEmissions = remainingScope1;
      const netPurchases = Math.max(
        0,
        coveredEmissions - carbonPricing.ets.freeAllocation / 1000
      );
      const adjustedPrice =
        carbonPricing.ets.currentPrice * Math.pow(1 + 0.05, year);
      costs.etsAllowances = netPurchases * adjustedPrice;
    }

    costs.total = costs.carbonTax + costs.etsAllowances + costs.offsets;
    return costs;
  };

  const calculateFinancialMetrics = () => {
    const {
      initialInvestment,
      projectLifespan,
      discountRate,
      annualSavings,
      annualOperatingCost,
      residualValue,
      carbonSavingsPerYear,
    } = economicInputs;
    const baseCarbonCosts = calculateCarbonCosts(0);
    const totalEmissionsInTons = totalEmissions / 1000;
    const effectiveRegulatoryRate =
      totalEmissionsInTons > 0
        ? baseCarbonCosts.total / totalEmissionsInTons
        : 0;

    const cashFlowsRaw = [-initialInvestment];
    const detailedCashFlows = [
      { year: 0, cashFlow: -initialInvestment, cumulative: -initialInvestment },
    ];
    let cumCF = -initialInvestment;

    for (let year = 1; year <= projectLifespan; year++) {
      const currentYearCosts = calculateCarbonCosts(year - 1);
      const currentRegulatoryRate =
        totalEmissionsInTons > 0
          ? currentYearCosts.total / totalEmissionsInTons
          : effectiveRegulatoryRate;
      const carbonSavingsValue = carbonSavingsPerYear * currentRegulatoryRate;
      let netCashFlow =
        annualSavings + carbonSavingsValue - annualOperatingCost;
      if (year === projectLifespan) netCashFlow += residualValue;

      cashFlowsRaw.push(netCashFlow);
      cumCF += netCashFlow;
      detailedCashFlows.push({
        year: year,
        cashFlow: netCashFlow,
        cumulative: cumCF,
      });
    }

    const npv = cashFlowsRaw.reduce(
      (acc, cf, year) => acc + cf / Math.pow(1 + discountRate / 100, year),
      0
    );

    let irr = 0;
    const hasPositiveCashFlow = cashFlowsRaw.some((cf) => cf > 0);
    const hasNegativeCashFlow = cashFlowsRaw.some((cf) => cf < 0);

    if (hasPositiveCashFlow && hasNegativeCashFlow) {
      let guess = 0.1;
      let converged = false;
      for (let i = 0; i < 100; i++) {
        let npvAtRate = 0;
        let derivativeAtRate = 0;
        for (let year = 0; year < cashFlowsRaw.length; year++) {
          npvAtRate += cashFlowsRaw[year] / Math.pow(1 + guess, year);
          if (year > 0)
            derivativeAtRate -=
              (year * cashFlowsRaw[year]) / Math.pow(1 + guess, year + 1);
        }
        if (Math.abs(npvAtRate) < 0.01) {
          converged = true;
          irr = guess;
          break;
        }
        if (Math.abs(derivativeAtRate) < 0.00001) break;
        guess = guess - npvAtRate / derivativeAtRate;
      }
      if (!converged || isNaN(irr) || !isFinite(irr)) irr = 0;
    } else if (!hasNegativeCashFlow) {
      irr = Infinity;
    } else {
      irr = -1;
    }

    let payback = Infinity;
    let tempCum = cashFlowsRaw[0];
    for (let year = 1; year < cashFlowsRaw.length; year++) {
      tempCum += cashFlowsRaw[year];
      if (tempCum >= 0) {
        payback =
          year -
          1 +
          Math.abs(tempCum - cashFlowsRaw[year]) / cashFlowsRaw[year];
        break;
      }
    }

    setNpvResult(npv);
    setIrrResult(irr * 100);
    setPaybackResult(payback);
    setLocalCashFlows(detailedCashFlows);

    if (setProjectCashFlows) {
      setProjectCashFlows({
        npv,
        irr: irr * 100,
        payback,
        cashFlows: detailedCashFlows,
      });
    }

    const sensitivity = [];
    for (let rate = 2; rate <= 15; rate += 0.5) {
      const testNpv = cashFlowsRaw.reduce(
        (acc, cf, year) => acc + cf / Math.pow(1 + rate / 100, year),
        0
      );
      sensitivity.push({ discountRate: rate, npv: testNpv });
    }
    setSensitivityData(sensitivity);
  };

  const updateEconomicInputs = (field, value) =>
    setEconomicInputs((prev) => ({ ...prev, [field]: parseFloat(value) || 0 }));
  const updateCarbonPricing = (mechanism, field, value) => {
    setCarbonPricing((prev) => ({
      ...prev,
      [mechanism]: {
        ...prev[mechanism],
        [field]: field === "enabled" ? value : parseFloat(value) || 0,
      },
    }));
  };

  const handleTabSwitch = (tab) => {
    if (document.activeElement) document.activeElement.blur();
    setEconomicTab(tab);
  };

  useEffect(
    () => calculateFinancialMetrics(),
    [economicInputs, carbonPricing, totalEmissions, emissionsBreakdown]
  );

  const carbonCosts = calculateCarbonCosts();
  const effectiveRate =
    totalEmissions > 0 ? carbonCosts.total / (totalEmissions / 1000) : 0;
  const pieData = [
    { name: "Scope 1", value: emissionsBreakdown.scope1, fill: "#ef4444" },
    { name: "Scope 2", value: emissionsBreakdown.scope2, fill: "#f97316" },
    { name: "Scope 3", value: emissionsBreakdown.scope3, fill: "#eab308" },
  ];
  const barData = [
    { name: "Carbon Tax", cost: carbonCosts.carbonTax, fill: "#ef4444" },
    {
      name: "ETS Allowances",
      cost: carbonCosts.etsAllowances,
      fill: "#f97316",
    },
    { name: "Carbon Offsets", cost: carbonCosts.offsets, fill: "#eab308" },
  ];

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 w-full ${
        darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
      }`}
    >
      <CarbonPricingDialog
        showDialog={showPricingDialog}
        setShowDialog={setShowPricingDialog}
        onSelectPricing={(system) => {
          setSelectedPricingSystem(system);
          setCarbonPricing((prev) => ({
            ...prev,
            system: system,
            carbonTax: {
              ...prev.carbonTax,
              enabled: system === "carbonTax" || system === "both",
            },
            ets: {
              ...prev.ets,
              enabled: system === "ets" || system === "both",
            },
          }));
        }}
        darkMode={darkMode}
      />
      <div className="flex justify-between items-center mb-4 shrink-0">
        <h2
          className={`text-xl font-bold ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Economic Modeling
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowPricingDialog(true)}
            className={`px-3 py-2 rounded border flex items-center gap-2 text-sm ${
              selectedPricingSystem
                ? "bg-green-600 text-white border-green-600"
                : darkMode
                ? "border-gray-600 bg-gray-700 text-white hover:bg-gray-600"
                : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
            }`}
          >
            🌍{" "}
            {selectedPricingSystem
              ? selectedPricingSystem === "carbonTax"
                ? "Carbon Tax"
                : selectedPricingSystem === "ets"
                ? "ETS"
                : "Hybrid System"
              : "Select Pricing"}
          </button>
          <button
            onClick={onUndo}
            className={`p-2 rounded ${
              darkMode
                ? "hover:bg-gray-700 text-gray-300"
                : "hover:bg-gray-100 text-gray-600"
            }`}
            title="Undo last action"
          >
            <RotateCcw />
          </button>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className={`p-2 rounded ${
              darkMode
                ? "hover:bg-gray-700 text-gray-300"
                : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            <Info />
          </button>
        </div>
      </div>

      {selectedPricingSystem && (
        <div
          className={`mb-4 p-3 shrink-0 rounded-lg border-l-4 ${
            selectedPricingSystem === "carbonTax"
              ? "border-red-500 bg-red-50 dark:bg-red-900"
              : selectedPricingSystem === "ets"
              ? "border-orange-500 bg-orange-50 dark:bg-orange-900"
              : "border-purple-500 bg-purple-50 dark:bg-purple-900"
          }`}
        >
          <div
            className={`text-sm font-medium ${
              selectedPricingSystem === "carbonTax"
                ? "text-red-800 dark:text-red-200"
                : selectedPricingSystem === "ets"
                ? "text-orange-800 dark:text-orange-200"
                : "text-purple-800 dark:text-purple-200"
            }`}
          >
            📋 Active Carbon Pricing:{" "}
            {selectedPricingSystem === "carbonTax"
              ? "Carbon Tax System"
              : selectedPricingSystem === "ets"
              ? "Emissions Trading System (ETS)"
              : "Hybrid System (Both ETS & Carbon Tax)"}
          </div>
        </div>
      )}

      {showInfo && (
        <div
          className={`p-4 mb-4 shrink-0 rounded-lg ${
            darkMode ? "bg-blue-900 text-white" : "bg-blue-50 text-blue-900"
          }`}
        >
          <h3 className="text-lg font-semibold mb-2">Economic Analysis</h3>
          <p>
            <strong>NPV:</strong> Net Present Value based on selected carbon
            pricing mechanism.
          </p>
          <p>
            <strong>IRR:</strong> Internal Rate of Return considering regulatory
            carbon costs.
          </p>
          <p>
            <strong>System:</strong>{" "}
            {selectedPricingSystem || "Please select a carbon pricing system"}
          </p>
        </div>
      )}

      {!selectedPricingSystem && (
        <div
          className={`p-6 mb-4 rounded-lg border-2 border-dashed text-center ${
            darkMode
              ? "border-gray-600 bg-gray-800"
              : "border-gray-300 bg-gray-50"
          }`}
        >
          <div className="text-4xl mb-4">🌍</div>
          <h3
            className={`text-lg font-semibold mb-2 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Carbon Pricing System Required
          </h3>
          <p
            className={`text-sm mb-4 ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Please select your carbon pricing mechanism to enable economic
            analysis
          </p>
          <button
            onClick={() => setShowPricingDialog(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            🚀 Select Carbon Pricing System
          </button>
        </div>
      )}

      {selectedPricingSystem && (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex mb-4 shrink-0">
            {["overview", "analysis", "cashflow", "pricing"].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabSwitch(tab)}
                className={`px-4 py-2 rounded-t border-b-2 ${
                  economicTab === tab
                    ? tab === "overview"
                      ? darkMode
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-blue-500 text-white border-blue-500"
                      : tab === "analysis"
                      ? darkMode
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-green-500 text-white border-green-500"
                      : tab === "cashflow"
                      ? darkMode
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-indigo-500 text-white border-indigo-500"
                      : darkMode
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-purple-500 text-white border-purple-500"
                    : darkMode
                    ? "bg-gray-700 text-gray-300 border-transparent hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 border-transparent hover:bg-gray-200"
                }`}
              >
                {tab === "overview"
                  ? "Overview"
                  : tab === "analysis"
                  ? "Financial Analysis"
                  : tab === "cashflow"
                  ? "Cash Flow"
                  : "Carbon Pricing"}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {economicTab === "overview" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
                <div
                  className={`p-6 rounded-xl shadow-lg border transition-all duration-300 ${
                    darkMode
                      ? "bg-gray-800 border-gray-700 shadow-gray-900/50"
                      : "bg-white border-gray-200 shadow-gray-200/50"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-6 flex items-center gap-2 transition-colors ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span className="text-xl">💳</span> Carbon Cost Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[
                      {
                        label: "Total Emissions",
                        value: `${(totalEmissions / 1000).toFixed(
                          2
                        )} tons CO₂e`,
                        color: "red",
                        icon: "🏭",
                      },
                      {
                        label: "Annual Carbon Cost",
                        value: `$${carbonCosts.total.toLocaleString()}`,
                        color: "orange",
                        icon: "💰",
                      },
                      {
                        label: "Effective Carbon Rate",
                        value: `$${effectiveRate.toFixed(2)}/ton`,
                        color: "green",
                        icon: "📊",
                      },
                      {
                        label: "NPV",
                        value: `$${npvResult.toLocaleString()}`,
                        color: "blue",
                        icon: "📈",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-lg transition-all duration-200 hover:scale-105 border ${
                          item.color === "red"
                            ? darkMode
                              ? "bg-red-900/20 border-red-700/50 hover:bg-red-900/30"
                              : "bg-red-50 border-red-200 hover:bg-red-100"
                            : item.color === "orange"
                            ? darkMode
                              ? "bg-orange-900/20 border-orange-700/50 hover:bg-orange-900/30"
                              : "bg-orange-50 border-orange-200 hover:bg-orange-100"
                            : item.color === "green"
                            ? darkMode
                              ? "bg-green-900/20 border-green-700/50 hover:bg-green-900/30"
                              : "bg-green-50 border-green-200 hover:bg-green-100"
                            : darkMode
                            ? "bg-blue-900/20 border-blue-700/50 hover:bg-blue-900/30"
                            : "bg-blue-50 border-blue-200 hover:bg-blue-100"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{item.icon}</span>
                          <p
                            className={`text-xs font-medium transition-colors ${
                              item.color === "red"
                                ? darkMode
                                  ? "text-red-300"
                                  : "text-red-700"
                                : item.color === "orange"
                                ? darkMode
                                  ? "text-orange-300"
                                  : "text-orange-700"
                                : item.color === "green"
                                ? darkMode
                                  ? "text-green-300"
                                  : "text-green-700"
                                : darkMode
                                ? "text-blue-300"
                                : "text-blue-700"
                            }`}
                          >
                            {item.label}
                          </p>
                        </div>
                        <p
                          className={`text-lg font-bold transition-colors ${
                            item.color === "red"
                              ? darkMode
                                ? "text-red-200"
                                : "text-red-800"
                              : item.color === "orange"
                              ? darkMode
                                ? "text-orange-200"
                                : "text-orange-800"
                              : item.color === "green"
                              ? darkMode
                                ? "text-green-200"
                                : "text-green-800"
                              : darkMode
                              ? "text-blue-200"
                              : "text-blue-800"
                          }`}
                        >
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart
                        margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                      >
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={85}
                          innerRadius={0}
                          labelLine={false}
                          label={({
                            cx,
                            cy,
                            midAngle,
                            innerRadius,
                            outerRadius,
                            percent,
                          }) => {
                            if (percent < 0.03) return null; // Hide labels for tiny slices
                            const radius =
                              innerRadius + (outerRadius - innerRadius) * 0.5;
                            const x =
                              cx +
                              radius * Math.cos(-midAngle * (Math.PI / 180));
                            const y =
                              cy +
                              radius * Math.sin(-midAngle * (Math.PI / 180));
                            return (
                              <text
                                x={x}
                                y={y}
                                fill="white"
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize={13}
                                fontWeight="bold"
                                style={{
                                  textShadow: "0px 1px 2px rgba(0,0,0,0.8)",
                                }}
                              >
                                {`${(percent * 100).toFixed(0)}%`}
                              </text>
                            );
                          }}
                        />
                        <Tooltip
                          formatter={(value) => [
                            `${value.toFixed(2)} kg CO₂e`,
                            null,
                          ]}
                          contentStyle={{
                            backgroundColor: darkMode ? "#374151" : "#ffffff",
                            border: `1px solid ${
                              darkMode ? "#6b7280" : "#d1d5db"
                            }`,
                            borderRadius: "8px",
                            color: darkMode ? "#f9fafb" : "#1f2937",
                            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div
                      className={`flex flex-col justify-center space-y-3 p-4 rounded-lg border ${
                        darkMode
                          ? "bg-gray-700/50 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      } overflow-y-auto`}
                    >
                      <h4
                        className={`text-sm font-bold border-b pb-2 ${
                          darkMode
                            ? "border-gray-600 text-white"
                            : "border-gray-300 text-gray-900"
                        }`}
                      >
                        Pricing Breakdown & Coverage
                      </h4>
                      {(selectedPricingSystem === "carbonTax" ||
                        selectedPricingSystem === "both") &&
                        carbonPricing.carbonTax.enabled && (
                          <div className="flex justify-between items-center text-sm">
                            <div>
                              <span className="font-semibold text-red-500">
                                Carbon Tax
                              </span>
                              <div
                                className={`text-xs mt-0.5 ${
                                  darkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                Covers{" "}
                                {selectedPricingSystem === "both"
                                  ? "Scope 2"
                                  : "Scope 1 & 2"}
                              </div>
                            </div>
                            <span className="font-mono font-bold">
                              $
                              {carbonCosts.carbonTax.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}
                            </span>
                          </div>
                        )}
                      {(selectedPricingSystem === "ets" ||
                        selectedPricingSystem === "both") &&
                        carbonPricing.ets.enabled && (
                          <div className="flex justify-between items-center text-sm">
                            <div>
                              <span className="font-semibold text-orange-500">
                                ETS Trading
                              </span>
                              <div
                                className={`text-xs mt-0.5 ${
                                  darkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                Covers Scope 1 (post-allocation)
                              </div>
                            </div>
                            <span className="font-mono font-bold">
                              $
                              {carbonCosts.etsAllowances.toLocaleString(
                                undefined,
                                { maximumFractionDigits: 0 }
                              )}
                            </span>
                          </div>
                        )}
                      {carbonPricing.offsets.enabled && (
                        <div className="flex justify-between items-center text-sm">
                          <div>
                            <span className="font-semibold text-yellow-500">
                              Voluntary Offsets
                            </span>
                            <div
                              className={`text-xs mt-0.5 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Applied proportionally (max{" "}
                              {carbonPricing.offsets.maxPercentage}%)
                            </div>
                          </div>
                          <span className="font-mono font-bold">
                            $
                            {carbonCosts.offsets.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </span>
                        </div>
                      )}
                      <div
                        className={`flex justify-between items-center text-sm pt-2 mt-auto border-t ${
                          darkMode
                            ? "border-gray-600 text-white"
                            : "border-gray-300 text-gray-900"
                        }`}
                      >
                        <span className="font-bold">Total Annual Cost</span>
                        <span className="font-mono font-bold text-lg">
                          $
                          {carbonCosts.total.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-6 rounded-xl shadow-lg border transition-all duration-300 ${
                    darkMode
                      ? "bg-gray-800 border-gray-700 shadow-gray-900/50"
                      : "bg-white border-gray-200 shadow-gray-200/50"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-6 flex items-center gap-2 transition-colors ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span className="text-xl">📊</span> Investment Analysis
                    Dashboard
                  </h3>
                  <div
                    className={`mb-6 p-4 rounded-lg border-l-4 transition-all duration-300 ${
                      npvResult > 0 && irrResult > economicInputs.discountRate
                        ? darkMode
                          ? "border-green-400 bg-green-900/20 backdrop-blur-sm"
                          : "border-green-500 bg-green-50"
                        : npvResult > 0
                        ? darkMode
                          ? "border-yellow-400 bg-yellow-900/20 backdrop-blur-sm"
                          : "border-yellow-500 bg-yellow-50"
                        : darkMode
                        ? "border-red-400 bg-red-900/20 backdrop-blur-sm"
                        : "border-red-500 bg-red-50"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-2xl">
                        {npvResult > 0 &&
                        irrResult > economicInputs.discountRate
                          ? "🎯"
                          : npvResult > 0
                          ? "⚠️"
                          : "❌"}
                      </div>
                      <div>
                        <h4
                          className={`font-bold text-lg transition-colors ${
                            npvResult > 0 &&
                            irrResult > economicInputs.discountRate
                              ? darkMode
                                ? "text-green-300"
                                : "text-green-800"
                              : npvResult > 0
                              ? darkMode
                                ? "text-yellow-300"
                                : "text-yellow-800"
                              : darkMode
                              ? "text-red-300"
                              : "text-red-800"
                          }`}
                        >
                          {npvResult > 0 &&
                          irrResult > economicInputs.discountRate
                            ? "Excellent Investment Opportunity"
                            : npvResult > 0
                            ? "Marginal Investment"
                            : "Investment Not Recommended"}
                        </h4>
                        <p
                          className={`text-sm transition-colors ${
                            npvResult > 0 &&
                            irrResult > economicInputs.discountRate
                              ? darkMode
                                ? "text-green-400"
                                : "text-green-700"
                              : npvResult > 0
                              ? darkMode
                                ? "text-yellow-400"
                                : "text-yellow-700"
                              : darkMode
                              ? "text-red-400"
                              : "text-red-700"
                          }`}
                        >
                          {npvResult > 0 &&
                          irrResult > economicInputs.discountRate
                            ? `Strong returns with ${irrResult.toFixed(
                                1
                              )}% IRR exceeding ${
                                economicInputs.discountRate
                              }% required return`
                            : npvResult > 0
                            ? `Positive but low returns. IRR (${irrResult.toFixed(
                                1
                              )}%) barely exceeds required return (${
                                economicInputs.discountRate
                              }%)`
                            : `Negative returns. Investment destroys value.`}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div
                      className={`p-4 rounded-lg border transition-all duration-200 hover:scale-105 ${
                        darkMode
                          ? "bg-gray-700/50 border-gray-600 hover:bg-gray-700"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">💰</span>
                          <span
                            className={`text-sm font-medium transition-colors ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            Net Present Value
                          </span>
                        </div>
                        <div
                          className={`px-2 py-1 rounded text-xs font-bold ${
                            npvResult > 100000
                              ? darkMode
                                ? "bg-green-800/50 text-green-200"
                                : "bg-green-100 text-green-800"
                              : npvResult > 0
                              ? darkMode
                                ? "bg-yellow-800/50 text-yellow-200"
                                : "bg-yellow-100 text-yellow-800"
                              : darkMode
                              ? "bg-red-800/50 text-red-200"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {npvResult > 100000
                            ? "STRONG"
                            : npvResult > 0
                            ? "WEAK"
                            : "POOR"}
                        </div>
                      </div>
                      <div
                        className={`text-2xl font-bold mb-2 ${
                          npvResult > 0
                            ? darkMode
                              ? "text-green-400"
                              : "text-green-600"
                            : darkMode
                            ? "text-red-400"
                            : "text-red-600"
                        }`}
                      >
                        ${npvResult.toLocaleString()}
                      </div>
                      <div
                        className={`text-xs transition-colors ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {npvResult > 0
                          ? `💡 Project creates $${npvResult.toLocaleString()} in value`
                          : "⚠️ Project destroys value"}
                      </div>
                      <div className="mt-3">
                        <div
                          className={`w-full h-2 rounded-full transition-colors ${
                            darkMode ? "bg-gray-600" : "bg-gray-200"
                          }`}
                        >
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              npvResult > 100000
                                ? "bg-green-500"
                                : npvResult > 0
                                ? "bg-yellow-500"
                                : "bg-red-500"
                            }`}
                            style={{
                              width: `${Math.min(
                                100,
                                Math.max(
                                  0,
                                  ((npvResult + 100000) / 600000) * 100
                                )
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      className={`p-4 rounded-lg border transition-all duration-200 hover:scale-105 ${
                        darkMode
                          ? "bg-gray-700/50 border-gray-600 hover:bg-gray-700"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">📈</span>
                          <span
                            className={`text-sm font-medium transition-colors ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            Return Rate (IRR)
                          </span>
                        </div>
                        <div
                          className={`px-2 py-1 rounded text-xs font-bold ${
                            irrResult > economicInputs.discountRate * 2
                              ? darkMode
                                ? "bg-green-800/50 text-green-200"
                                : "bg-green-100 text-green-800"
                              : irrResult > economicInputs.discountRate
                              ? darkMode
                                ? "bg-yellow-800/50 text-yellow-200"
                                : "bg-yellow-100 text-yellow-800"
                              : darkMode
                              ? "bg-red-800/50 text-red-200"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {irrResult > economicInputs.discountRate * 2
                            ? "EXCELLENT"
                            : irrResult > economicInputs.discountRate
                            ? "ACCEPTABLE"
                            : "POOR"}
                        </div>
                      </div>
                      <div
                        className={`text-2xl font-bold mb-2 ${
                          irrResult > economicInputs.discountRate
                            ? darkMode
                              ? "text-green-400"
                              : "text-green-600"
                            : darkMode
                            ? "text-red-400"
                            : "text-red-600"
                        }`}
                      >
                        {irrResult.toFixed(1)}%
                      </div>
                      <div
                        className={`text-xs transition-colors ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Required: {economicInputs.discountRate}% |{" "}
                        {irrResult > economicInputs.discountRate
                          ? ` +${(
                              irrResult - economicInputs.discountRate
                            ).toFixed(1)}% above target`
                          : ` ${(
                              irrResult - economicInputs.discountRate
                            ).toFixed(1)}% below target`}
                      </div>
                      <div className="mt-3 relative">
                        <div className="flex justify-center">
                          <div className="relative w-24 h-12 overflow-hidden">
                            <div
                              className={`absolute inset-0 rounded-t-full border-4 transition-colors ${
                                darkMode ? "border-gray-600" : "border-gray-200"
                              }`}
                            ></div>
                            <div
                              className={`absolute inset-0 rounded-t-full border-4 transition-all duration-500 ${
                                irrResult > economicInputs.discountRate * 2
                                  ? "border-green-500"
                                  : irrResult > economicInputs.discountRate
                                  ? "border-yellow-500"
                                  : "border-red-500"
                              }`}
                              style={{
                                clipPath: `polygon(0 100%, 50% 50%, ${Math.min(
                                  100,
                                  Math.max(0, (irrResult / 30) * 100)
                                )}% 100%)`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`p-4 rounded-lg border transition-all duration-200 hover:scale-105 ${
                        darkMode
                          ? "bg-gray-700/50 border-gray-600 hover:bg-gray-700"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">⏱️</span>
                          <span
                            className={`text-sm font-medium transition-colors ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            Payback Period
                          </span>
                        </div>
                        <div
                          className={`px-2 py-1 rounded text-xs font-bold ${
                            paybackResult < 5
                              ? darkMode
                                ? "bg-green-800/50 text-green-200"
                                : "bg-green-100 text-green-800"
                              : paybackResult < 10
                              ? darkMode
                                ? "bg-yellow-800/50 text-yellow-200"
                                : "bg-yellow-100 text-yellow-800"
                              : darkMode
                              ? "bg-red-800/50 text-red-200"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {paybackResult < 5
                            ? "FAST"
                            : paybackResult < 10
                            ? "MODERATE"
                            : "SLOW"}
                        </div>
                      </div>
                      <div
                        className={`text-2xl font-bold mb-2 ${
                          paybackResult < 10
                            ? darkMode
                              ? "text-green-400"
                              : "text-green-600"
                            : darkMode
                            ? "text-red-400"
                            : "text-red-600"
                        }`}
                      >
                        {paybackResult === Infinity
                          ? "No Payback"
                          : `${paybackResult.toFixed(1)} years`}
                      </div>
                      <div
                        className={`text-xs transition-colors ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {paybackResult < 5
                          ? "🚀 Quick return on investment"
                          : paybackResult < 10
                          ? "📅 Reasonable timeline"
                          : "⏳ Long time to recover costs"}
                      </div>
                      <div className="mt-3">
                        <div className="flex items-center gap-1">
                          {[...Array(20)].map((_, i) => (
                            <div
                              key={i}
                              className={`h-2 flex-1 rounded-sm transition-colors ${
                                paybackResult === Infinity
                                  ? "bg-red-400"
                                  : i < paybackResult
                                  ? "bg-red-400"
                                  : i < economicInputs.projectLifespan
                                  ? "bg-green-400"
                                  : darkMode
                                  ? "bg-gray-600"
                                  : "bg-gray-200"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`mb-6 p-4 rounded-lg border transition-colors ${
                      darkMode
                        ? "bg-gray-700/30 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <h4
                      className={`font-semibold mb-3 flex items-center gap-2 transition-colors ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <span className="text-xl">🎯</span> Risk Assessment
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        {
                          label: "Financial Risk",
                          risk: npvResult < 0 ? "High" : "Low",
                          high: npvResult < 0,
                        },
                        {
                          label: "Return Margin",
                          risk:
                            irrResult - economicInputs.discountRate < 2
                              ? "High"
                              : "Low",
                          high: irrResult - economicInputs.discountRate < 2,
                        },
                        {
                          label: "Timeline Risk",
                          risk: paybackResult > 10 ? "High" : "Low",
                          high: paybackResult > 10,
                        },
                      ].map((item, index) => (
                        <div key={index} className="text-center">
                          <div
                            className={`text-lg font-bold transition-colors ${
                              item.high
                                ? darkMode
                                  ? "text-red-400"
                                  : "text-red-600"
                                : darkMode
                                ? "text-green-400"
                                : "text-green-600"
                            }`}
                          >
                            {item.risk}
                          </div>
                          <div
                            className={`text-xs transition-colors ${
                              darkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {item.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="h-80">
                    <h4
                      className={`font-semibold mb-3 flex items-center gap-2 transition-colors ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <span className="text-xl">📊</span> Investment Performance
                      vs Industry Benchmarks
                    </h4>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          {
                            name: "Your Project",
                            NPV: npvResult / 1000,
                            IRR: irrResult,
                            Target: economicInputs.discountRate,
                            Industry: 12,
                          },
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={darkMode ? "#4b5563" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="name"
                          tick={{ fill: darkMode ? "#e5e7eb" : "#374151" }}
                        />
                        <YAxis
                          yAxisId="left"
                          label={{
                            value: "NPV ($000s)",
                            angle: -90,
                            position: "insideLeft",
                            fill: darkMode ? "#e5e7eb" : "#374151",
                          }}
                          tick={{ fill: darkMode ? "#e5e7eb" : "#374151" }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          label={{
                            value: "Return Rate (%)",
                            angle: 90,
                            position: "insideRight",
                            fill: darkMode ? "#e5e7eb" : "#374151",
                          }}
                          tick={{ fill: darkMode ? "#e5e7eb" : "#374151" }}
                        />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "NPV")
                              return [
                                `$${(value * 1000).toLocaleString()}`,
                                "Net Present Value",
                              ];
                            return [`${value.toFixed(1)}%`, name];
                          }}
                          contentStyle={{
                            backgroundColor: darkMode ? "#374151" : "#ffffff",
                            border: `1px solid ${
                              darkMode ? "#6b7280" : "#d1d5db"
                            }`,
                            borderRadius: "4px",
                            color: darkMode ? "#f9fafb" : "#1f2937",
                          }}
                        />
                        <Bar
                          yAxisId="left"
                          dataKey="NPV"
                          fill="#3b82f6"
                          name="NPV"
                        >
                          <LabelList
                            dataKey="NPV"
                            position="top"
                            formatter={(value) =>
                              `$${(value * 1000).toLocaleString()}`
                            }
                          />
                        </Bar>
                        <Bar
                          yAxisId="right"
                          dataKey="IRR"
                          fill="#10b981"
                          name="Your IRR"
                        >
                          <LabelList
                            dataKey="IRR"
                            position="top"
                            formatter={(value) => `${value.toFixed(1)}%`}
                          />
                        </Bar>
                        <Bar
                          yAxisId="right"
                          dataKey="Target"
                          fill="#f59e0b"
                          name="Required Return"
                        >
                          <LabelList
                            dataKey="Target"
                            position="top"
                            formatter={(value) => `${value}% (Target)`}
                          />
                        </Bar>
                        <Bar
                          yAxisId="right"
                          dataKey="Industry"
                          fill="#8b5cf6"
                          name="Industry Average"
                        >
                          <LabelList
                            dataKey="Industry"
                            position="top"
                            formatter={(value) => `${value}% (Industry)`}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div
                    className={`mt-6 p-4 rounded-lg border transition-colors ${
                      darkMode
                        ? "bg-blue-900/20 border-blue-700/50 backdrop-blur-sm"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
                    <h4
                      className={`font-semibold mb-3 flex items-center gap-2 transition-colors ${
                        darkMode ? "text-blue-200" : "text-blue-800"
                      }`}
                    >
                      <span className="text-xl">🎯</span> Recommended Actions
                    </h4>
                    <div
                      className={`space-y-2 text-sm transition-colors ${
                        darkMode ? "text-blue-300" : "text-blue-700"
                      }`}
                    >
                      {npvResult > 100000 &&
                      irrResult > economicInputs.discountRate * 1.5 ? (
                        <>
                          <p className="flex items-start gap-2">
                            <span className="text-green-500 mt-0.5">✅</span>
                            <span>
                              <strong>PROCEED with investment</strong> - Strong
                              financial returns
                            </span>
                          </p>
                          <p className="flex items-start gap-2">
                            <span className="text-blue-500 mt-0.5">📈</span>
                            <span>Consider scaling up the project size</span>
                          </p>
                          <p className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">⚡</span>
                            <span>
                              Fast-track implementation to capture benefits
                              sooner
                            </span>
                          </p>
                        </>
                      ) : npvResult > 0 &&
                        irrResult > economicInputs.discountRate ? (
                        <>
                          <p className="flex items-start gap-2">
                            <span className="text-yellow-500 mt-0.5">⚠️</span>
                            <span>
                              <strong>PROCEED with caution</strong> - Marginal
                              returns
                            </span>
                          </p>
                          <p className="flex items-start gap-2">
                            <span className="text-orange-500 mt-0.5">🔍</span>
                            <span>
                              Review assumptions and look for cost optimizations
                            </span>
                          </p>
                          <p className="flex items-start gap-2">
                            <span className="text-blue-500 mt-0.5">📊</span>
                            <span>
                              Monitor carbon price trends for better timing
                            </span>
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="flex items-start gap-2">
                            <span className="text-red-500 mt-0.5">❌</span>
                            <span>
                              <strong>DO NOT PROCEED</strong> - Poor financial
                              returns
                            </span>
                          </p>
                          <p className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">🔄</span>
                            <span>Redesign project to improve economics</span>
                          </p>
                          <p className="flex items-start gap-2">
                            <span className="text-yellow-500 mt-0.5">💡</span>
                            <span>
                              Consider alternative carbon reduction strategies
                            </span>
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {economicTab === "analysis" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6">
                <div
                  className={`p-4 rounded-lg shadow border ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Investment Parameters
                  </h3>
                  <div className="space-y-4">
                    {Object.entries({
                      "Initial Investment ($)": "initialInvestment",
                      "Project Lifespan (years)": "projectLifespan",
                      "Discount Rate (%)": "discountRate",
                      "Annual Savings ($)": "annualSavings",
                      "Annual Operating Cost ($)": "annualOperatingCost",
                      "Carbon Savings (tons CO₂e/year)": "carbonSavingsPerYear",
                      "Residual Value ($)": "residualValue",
                    }).map(([label, field]) => (
                      <div key={field}>
                        <label
                          className={`block text-sm font-medium mb-1 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {label}
                        </label>
                        <input
                          type="number"
                          step={field.includes("Rate") ? "0.1" : "1"}
                          value={economicInputs[field]}
                          onChange={(e) =>
                            updateEconomicInputs(field, e.target.value)
                          }
                          className={`w-full p-2 border rounded ${
                            darkMode
                              ? "border-gray-600 bg-gray-700 text-white"
                              : "border-gray-300 bg-white text-gray-900"
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg shadow border ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Investment Recommendation
                  </h3>
                  <div
                    className={`mt-4 p-3 rounded border ${
                      darkMode
                        ? "bg-yellow-900 border-yellow-600"
                        : "bg-yellow-50 border-yellow-200"
                    }`}
                  >
                    <h4
                      className={`font-semibold mb-2 ${
                        darkMode ? "text-yellow-300" : "text-yellow-800"
                      }`}
                    >
                      Analysis Results ({selectedPricingSystem} System)
                    </h4>
                    <p
                      className={`text-sm ${
                        darkMode ? "text-yellow-200" : "text-yellow-700"
                      }`}
                    >
                      {npvResult > 0 && irrResult > economicInputs.discountRate
                        ? "✅ Project is financially viable with positive returns"
                        : "❌ Project may not meet financial criteria"}
                    </p>
                    <div
                      className={`mt-2 text-sm ${
                        darkMode ? "text-yellow-200" : "text-yellow-700"
                      }`}
                    >
                      <p>Expected NPV: ${npvResult.toLocaleString()}</p>
                      <p>Expected IRR: {irrResult.toFixed(1)}%</p>
                      <p>
                        Payback Period:{" "}
                        {paybackResult === Infinity
                          ? "No Payback"
                          : paybackResult.toFixed(1) + " years"}
                      </p>
                      <p>
                        Effective Carbon Rate: ${effectiveRate.toFixed(2)}/ton
                      </p>
                      <p>
                        Annual Carbon Savings Value: $
                        {(
                          economicInputs.carbonSavingsPerYear * effectiveRate
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {economicTab === "cashflow" && (
              <div className="space-y-6 pb-6">
                <div
                  className={`p-6 rounded-xl shadow-lg border ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-6 flex items-center gap-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span className="text-xl">📈</span> Cash Flow Projection
                  </h3>
                  <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={localCashFlows}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={darkMode ? "#4b5563" : "#e5e7eb"}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="year"
                          tick={{ fill: darkMode ? "#9ca3af" : "#6b7280" }}
                          label={{
                            value: "Project Year",
                            position: "insideBottom",
                            offset: -10,
                            fill: darkMode ? "#9ca3af" : "#6b7280",
                          }}
                        />
                        <YAxis
                          tick={{ fill: darkMode ? "#9ca3af" : "#6b7280" }}
                          tickFormatter={(val) =>
                            `$${(val / 1000).toFixed(0)}k`
                          }
                        />
                        <Tooltip
                          formatter={(value) =>
                            `$${value.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}`
                          }
                          labelFormatter={(label) => `Year ${label}`}
                          contentStyle={{
                            backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                            borderColor: darkMode ? "#374151" : "#e5e7eb",
                            color: darkMode ? "#f3f4f6" : "#111827",
                          }}
                        />
                        <Legend verticalAlign="top" height={36} />
                        <Bar
                          dataKey="cashFlow"
                          name="Annual Cash Flow"
                          fill={darkMode ? "#60a5fa" : "#3b82f6"}
                          radius={[4, 4, 0, 0]}
                        />
                        <Line
                          type="monotone"
                          dataKey="cumulative"
                          name="Cumulative Cash Flow"
                          stroke="#10b981"
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div
                  className={`p-6 rounded-xl shadow-lg border overflow-hidden ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Data Table
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead
                        className={`text-xs uppercase ${
                          darkMode
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-50 text-gray-700"
                        }`}
                      >
                        <tr>
                          <th className="px-6 py-3 rounded-tl-lg">Year</th>
                          <th className="px-6 py-3 text-right">
                            Net Cash Flow
                          </th>
                          <th className="px-6 py-3 text-right rounded-tr-lg">
                            Cumulative Cash Flow
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {localCashFlows.map((row) => (
                          <tr
                            key={row.year}
                            className={`border-b ${
                              darkMode
                                ? "border-gray-700 hover:bg-gray-700/50"
                                : "border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <td
                              className={`px-6 py-4 font-medium ${
                                darkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              Year {row.year}
                            </td>
                            <td
                              className={`px-6 py-4 text-right font-mono ${
                                row.cashFlow < 0
                                  ? "text-red-500"
                                  : "text-green-500"
                              }`}
                            >
                              {row.cashFlow < 0 ? "-" : ""}$
                              {Math.abs(row.cashFlow).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 0 }
                              )}
                            </td>
                            <td
                              className={`px-6 py-4 text-right font-mono font-bold ${
                                row.cumulative < 0
                                  ? "text-red-500"
                                  : "text-green-500"
                              }`}
                            >
                              {row.cumulative < 0 ? "-" : ""}$
                              {Math.abs(row.cumulative).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 0 }
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {economicTab === "pricing" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6">
                <div
                  className={`p-4 rounded-lg shadow border ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Carbon Pricing Configuration
                  </h3>
                  {(selectedPricingSystem === "carbonTax" ||
                    selectedPricingSystem === "both") && (
                    <div
                      className={`mb-4 p-4 rounded-lg border-l-4 border-red-500 ${
                        darkMode
                          ? "bg-red-900"
                          : "bg-red-50 border border-red-200"
                      }`}
                    >
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          id="carbonTax"
                          checked={carbonPricing.carbonTax.enabled}
                          onChange={(e) =>
                            updateCarbonPricing(
                              "carbonTax",
                              "enabled",
                              e.target.checked
                            )
                          }
                          className="mr-2"
                        />
                        <label
                          htmlFor="carbonTax"
                          className={`font-semibold ${
                            darkMode ? "text-red-300" : "text-red-800"
                          }`}
                        >
                          Carbon Tax
                        </label>
                      </div>
                      {carbonPricing.carbonTax.enabled && (
                        <div className="ml-6 space-y-2">
                          <div>
                            <label
                              className={`block text-sm mb-1 ${
                                darkMode ? "text-red-200" : "text-red-700"
                              }`}
                            >
                              Rate ($/ton CO₂e)
                            </label>
                            <input
                              type="number"
                              value={carbonPricing.carbonTax.rate}
                              onChange={(e) =>
                                updateCarbonPricing(
                                  "carbonTax",
                                  "rate",
                                  e.target.value
                                )
                              }
                              className={`w-full p-2 border rounded ${
                                darkMode
                                  ? "border-gray-600 bg-gray-700 text-white"
                                  : "border-gray-300 bg-white text-gray-900"
                              }`}
                            />
                          </div>
                          <div>
                            <label
                              className={`block text-sm mb-1 ${
                                darkMode ? "text-red-200" : "text-red-700"
                              }`}
                            >
                              Escalation Rate (%)
                            </label>
                            <input
                              type="number"
                              value={carbonPricing.carbonTax.escalationRate}
                              onChange={(e) =>
                                updateCarbonPricing(
                                  "carbonTax",
                                  "escalationRate",
                                  e.target.value
                                )
                              }
                              className={`w-full p-2 border rounded ${
                                darkMode
                                  ? "border-gray-600 bg-gray-700 text-white"
                                  : "border-gray-300 bg-white text-gray-900"
                              }`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {(selectedPricingSystem === "ets" ||
                    selectedPricingSystem === "both") && (
                    <div
                      className={`mb-4 p-4 rounded-lg border-l-4 border-orange-500 ${
                        darkMode
                          ? "bg-orange-900"
                          : "bg-orange-50 border border-orange-200"
                      }`}
                    >
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          id="ets"
                          checked={carbonPricing.ets.enabled}
                          onChange={(e) =>
                            updateCarbonPricing(
                              "ets",
                              "enabled",
                              e.target.checked
                            )
                          }
                          className="mr-2"
                        />
                        <label
                          htmlFor="ets"
                          className={`font-semibold ${
                            darkMode ? "text-orange-300" : "text-orange-800"
                          }`}
                        >
                          ETS Trading
                        </label>
                      </div>
                      {carbonPricing.ets.enabled && (
                        <div className="ml-6 space-y-2">
                          <div>
                            <label
                              className={`block text-sm mb-1 ${
                                darkMode ? "text-orange-200" : "text-orange-700"
                              }`}
                            >
                              Current Price ($/ton CO₂e)
                            </label>
                            <input
                              type="number"
                              value={carbonPricing.ets.currentPrice}
                              onChange={(e) =>
                                updateCarbonPricing(
                                  "ets",
                                  "currentPrice",
                                  e.target.value
                                )
                              }
                              className={`w-full p-2 border rounded ${
                                darkMode
                                  ? "border-gray-600 bg-gray-700 text-white"
                                  : "border-gray-300 bg-white text-gray-900"
                              }`}
                            />
                          </div>
                          <div>
                            <label
                              className={`block text-sm mb-1 ${
                                darkMode ? "text-orange-200" : "text-orange-700"
                              }`}
                            >
                              Free Allocation (tons/year)
                            </label>
                            <input
                              type="number"
                              value={carbonPricing.ets.freeAllocation}
                              onChange={(e) =>
                                updateCarbonPricing(
                                  "ets",
                                  "freeAllocation",
                                  e.target.value
                                )
                              }
                              className={`w-full p-2 border rounded ${
                                darkMode
                                  ? "border-gray-600 bg-gray-700 text-white"
                                  : "border-gray-300 bg-white text-gray-900"
                              }`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    className={`mb-4 p-4 rounded-lg border-l-4 border-green-500 ${
                      darkMode
                        ? "bg-green-900"
                        : "bg-green-50 border border-green-200"
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="offsets"
                        checked={carbonPricing.offsets.enabled}
                        onChange={(e) =>
                          updateCarbonPricing(
                            "offsets",
                            "enabled",
                            e.target.checked
                          )
                        }
                        className="mr-2"
                      />
                      <label
                        htmlFor="offsets"
                        className={`font-semibold ${
                          darkMode ? "text-green-300" : "text-green-800"
                        }`}
                      >
                        Carbon Offsets
                      </label>
                    </div>
                    {carbonPricing.offsets.enabled && (
                      <div className="ml-6 space-y-2">
                        <div>
                          <label
                            className={`block text-sm mb-1 ${
                              darkMode ? "text-green-200" : "text-green-700"
                            }`}
                          >
                            Max Percentage
                          </label>
                          <input
                            type="number"
                            value={carbonPricing.offsets.maxPercentage}
                            onChange={(e) =>
                              updateCarbonPricing(
                                "offsets",
                                "maxPercentage",
                                e.target.value
                              )
                            }
                            className={`w-full p-2 border rounded ${
                              darkMode
                                ? "border-gray-600 bg-gray-700 text-white"
                                : "border-gray-300 bg-white text-gray-900"
                            }`}
                          />
                        </div>
                        <div>
                          <label
                            className={`block text-sm mb-1 ${
                              darkMode ? "text-green-200" : "text-green-700"
                            }`}
                          >
                            Price ($/ton CO₂e)
                          </label>
                          <input
                            type="number"
                            value={carbonPricing.offsets.price}
                            onChange={(e) =>
                              updateCarbonPricing(
                                "offsets",
                                "price",
                                e.target.value
                              )
                            }
                            className={`w-full p-2 border rounded ${
                              darkMode
                                ? "border-gray-600 bg-gray-700 text-white"
                                : "border-gray-300 bg-white text-gray-900"
                            }`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg shadow border ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Cost Impact Analysis
                  </h3>
                  <div className="h-64 mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={barData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={darkMode ? "#4b5563" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="name"
                          tick={{ fill: darkMode ? "#e5e7eb" : "#374151" }}
                        />
                        <YAxis
                          label={{
                            value: "$ per year",
                            angle: -90,
                            position: "insideLeft",
                            fill: darkMode ? "#e5e7eb" : "#374151",
                          }}
                          tick={{ fill: darkMode ? "#e5e7eb" : "#374151" }}
                        />
                        <Tooltip
                          formatter={(value) => [
                            `${value.toLocaleString()}`,
                            "Annual Cost",
                          ]}
                          contentStyle={{
                            backgroundColor: darkMode ? "#374151" : "#ffffff",
                            border: `1px solid ${
                              darkMode ? "#6b7280" : "#d1d5db"
                            }`,
                            borderRadius: "4px",
                            color: darkMode ? "#f9fafb" : "#1f2937",
                          }}
                        />
                        <Bar dataKey="cost">
                          <LabelList
                            dataKey="cost"
                            position="top"
                            formatter={(value) => `${value.toLocaleString()}`}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div
                    className={`p-3 rounded border ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <h4
                      className={`font-semibold mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {selectedPricingSystem.charAt(0).toUpperCase() +
                        selectedPricingSystem.slice(1)}{" "}
                      System Impact
                    </h4>
                    <div
                      className={`space-y-1 text-sm ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <div className="flex justify-between">
                        <span>Total Annual Cost:</span>
                        <span className="font-semibold text-lg">
                          ${carbonCosts.total.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Effective Rate:</span>
                        <span className="font-semibold">
                          ${effectiveRate.toFixed(2)}/ton CO₂e
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Annual Carbon Savings Value:</span>
                        <span className="font-semibold text-green-600">
                          $
                          {(
                            economicInputs.carbonSavingsPerYear * effectiveRate
                          ).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Carbon Dashboard with Reduction Analysis
function CarbonDashboard({
  entries = [],
  setEntries,
  factors = {},
  darkMode = false,
  onUndo,
  processTabs,
  setProcessTabs,
  activeProcessTab,
  setActiveProcessTab,
  primaryTab,
  setPrimaryTab,
  originalBaselineEntries,
}) {
  const [activeTab2, setActiveTab2] = useState("analysis");
  const [alternativeSelections, setAlternativeSelections] = useState({});
  const [showAlternativeFor, setShowAlternativeFor] = useState(null);
  const [alternativeForm, setAlternativeForm] = useState({
    title: "",
    desc: "",
    mass: "",
    label: "",
    customEpd: "",
    scope: "",
    operation: "",
    newOperation: "",
    isNewOperation: false,
  });

  const [isRenamingTab, setIsRenamingTab] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const getCurrentTabEntries = () => {
    const currentTab = processTabs.find((tab) => tab.id === activeProcessTab);
    if (currentTab?.isOriginalBaseline) {
      return originalBaselineEntries;
    }
    return currentTab ? currentTab.entries : [];
  };

  const updateCurrentTabEntries = (newEntries) => {
    const currentTab = processTabs.find((tab) => tab.id === activeProcessTab);
    if (currentTab?.isOriginalBaseline || currentTab?.isLocked) {
      return;
    }
    setProcessTabs((prev) =>
      prev.map((tab) =>
        tab.id === activeProcessTab ? { ...tab, entries: newEntries } : tab
      )
    );
  };

  const toggleTabLock = (tabId) => {
    setProcessTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId ? { ...tab, isLocked: !tab.isLocked } : tab
      )
    );
  };

  const isCurrentTabLocked = () => {
    const currentTab = processTabs.find((tab) => tab.id === activeProcessTab);
    return currentTab?.isLocked || false;
  };

  const duplicateCurrentTab = () => {
    const currentTab = processTabs.find((tab) => tab.id === activeProcessTab);
    if (!currentTab) return;

    const newId = `duplicate_${Date.now()}`;
    const duplicateNumber =
      processTabs.filter((tab) => tab.name.includes("Duplicate")).length + 1;

    const newTab = {
      id: newId,
      name: `Duplicate ${duplicateNumber}`,
      entries: [...currentTab.entries],
      isOriginal: false,
      isLocked: false,
    };

    setProcessTabs((prev) => [...prev, newTab]);
    setActiveProcessTab(newId);
  };

  const makeTabPrimary = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    const currentTab = processTabs.find((tab) => tab.id === activeProcessTab);
    if (!currentTab) return;

    if (currentTab.isOriginalBaseline) {
      alert(
        "Cannot make the Original Process tab primary. This tab preserves your baseline data for reference."
      );
      return;
    }

    setEntries([...currentTab.entries]);

    setProcessTabs((prev) =>
      prev.map((tab) =>
        tab.id === "original"
          ? { ...tab, entries: [...currentTab.entries] }
          : tab
      )
    );

    setPrimaryTab(activeProcessTab);
    alert("✅ Changes applied to main application successfully!");
  };

  const startRenaming = (tabId, currentName) => {
    setIsRenamingTab(tabId);
    setRenameValue(currentName);
  };

  const confirmRename = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    if (!renameValue.trim()) return;
    setProcessTabs((prev) =>
      prev.map((tab) =>
        tab.id === isRenamingTab ? { ...tab, name: renameValue.trim() } : tab
      )
    );
    setIsRenamingTab(null);
    setRenameValue("");
  };

  const cancelRename = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    setIsRenamingTab(null);
    setRenameValue("");
  };

  const deleteTab = (tabId) => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    if (processTabs.length <= 1) return;

    const tabToDelete = processTabs.find((tab) => tab.id === tabId);
    if (tabToDelete?.isOriginalBaseline) {
      alert(
        "Cannot delete the Original Process tab - it preserves your baseline data."
      );
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this tab?"
    );
    if (!confirmDelete) return;

    if (primaryTab === tabId) {
      setPrimaryTab("original");
    }

    setProcessTabs((prev) => prev.filter((tab) => tab.id !== tabId));

    if (activeProcessTab === tabId) {
      const remainingTabs = processTabs.filter((tab) => tab.id !== tabId);
      setActiveProcessTab(remainingTabs[0]?.id || "original");
    }
  };

  const calculatePercentageChange = (originalEF, newEF) => {
    if (originalEF === 0) return 0;
    return ((newEF - originalEF) / originalEF) * 100;
  };

  const handleAlternativeFormSubmit = (globalIdx) => {
    const {
      mass,
      label,
      customEpd,
      scope,
      operation,
      newOperation,
      isNewOperation,
    } = alternativeForm;
    if (!mass || (!label && !customEpd) || !scope) return;

    let operationName = "";
    if (isNewOperation && newOperation.trim()) {
      operationName = newOperation.trim();
    } else if (!isNewOperation && operation) {
      operationName = operation;
    }

    const m = parseFloat(mass);
    const ef = customEpd ? parseFloat(customEpd) : factors[label] || 0;
    const currentEntries = getCurrentTabEntries();
    const originalEF = currentEntries[globalIdx].ef;
    const percentageChange = calculatePercentageChange(originalEF, ef);

    setAlternativeSelections((prev) => ({
      ...prev,
      [globalIdx]: {
        title: alternativeForm.title || currentEntries[globalIdx].title,
        desc: alternativeForm.desc || currentEntries[globalIdx].desc,
        label: label || `Custom: ${customEpd}`,
        ef: ef,
        mass: m,
        scope: scope,
        operation: operationName,
        percentageChange,
        newTotal: ef * m,
        isCustom: !!customEpd,
      },
    }));
  };

  const applyAlternative = (globalIdx) => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    const alternative = alternativeSelections[globalIdx];
    if (!alternative) return;

    const currentEntries = getCurrentTabEntries();
    const updatedEntries = [...currentEntries];
    updatedEntries[globalIdx] = {
      ...updatedEntries[globalIdx],
      title: alternative.title,
      desc: alternative.desc,
      label: alternative.label,
      ef: alternative.ef,
      mass: alternative.mass,
      scope: alternative.scope,
      operation: alternative.operation || updatedEntries[globalIdx].operation,
      total: alternative.newTotal,
      isAlternative: true,
      isCustom: alternative.isCustom,
    };

    updateCurrentTabEntries(updatedEntries);

    setAlternativeSelections((prev) => {
      const newSelections = { ...prev };
      delete newSelections[globalIdx];
      return newSelections;
    });

    setShowAlternativeFor(null);
    setAlternativeForm({
      title: "",
      desc: "",
      mass: "",
      label: "",
      customEpd: "",
      scope: "",
      operation: "",
      newOperation: "",
      isNewOperation: false,
    });
  };

  const rejectAlternative = (globalIdx) => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    setAlternativeSelections((prev) => {
      const newSelections = { ...prev };
      delete newSelections[globalIdx];
      return newSelections;
    });
    setShowAlternativeFor(null);
    setAlternativeForm({
      title: "",
      desc: "",
      mass: "",
      label: "",
      customEpd: "",
      scope: "",
      operation: "",
      newOperation: "",
      isNewOperation: false,
    });
  };

  const showAlternativeDialog = (globalIdx) => {
    if (isCurrentTabLocked()) {
      alert("This tab is locked. Please unlock it first to make changes.");
      return;
    }

    const currentEntries = getCurrentTabEntries();
    const process = currentEntries[globalIdx];
    setAlternativeForm({
      title: process.title,
      desc: process.desc,
      mass: process.mass.toString(),
      label: "",
      customEpd: "",
      scope: process.scope || "",
      operation: process.operation || "",
      newOperation: "",
      isNewOperation: false,
    });
    setShowAlternativeFor(globalIdx);
  };

  const currentTabEntries = getCurrentTabEntries();
  const currentTab = processTabs.find((tab) => tab.id === activeProcessTab);

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 w-full ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      <div className="flex justify-between items-center mb-4 shrink-0">
        <div className="flex space-x-2">
          <button
            onClick={duplicateCurrentTab}
            className={`px-3 py-2 rounded border flex items-center gap-1 text-sm ${
              darkMode
                ? "border-gray-600 bg-gray-700 text-white hover:bg-gray-600"
                : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
            }`}
            title="Duplicate current tab"
          >
            <span>📋</span> Duplicate Process
          </button>
          {!currentTab?.isOriginalBaseline && (
            <button
              onClick={() => toggleTabLock(activeProcessTab)}
              className={`px-3 py-2 rounded border flex items-center gap-1 text-sm ${
                isCurrentTabLocked()
                  ? darkMode
                    ? "border-red-600 bg-red-700 text-white hover:bg-red-600"
                    : "border-red-300 bg-red-100 text-red-900 hover:bg-red-200"
                  : darkMode
                  ? "border-gray-600 bg-gray-700 text-white hover:bg-gray-600"
                  : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
              }`}
              title={
                isCurrentTabLocked()
                  ? "Unlock tab to make changes"
                  : "Lock tab to prevent changes"
              }
            >
              <span>{isCurrentTabLocked() ? "🔒" : "🔓"}</span>
              {isCurrentTabLocked() ? "Locked" : "Unlocked"}
            </button>
          )}

          <button
            onClick={makeTabPrimary}
            className={`px-3 py-2 rounded flex items-center gap-1 text-sm ${
              currentTab?.isOriginalBaseline ||
              JSON.stringify(entries) === JSON.stringify(currentTab?.entries)
                ? "bg-green-800 text-white cursor-default opacity-50"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
            title={
              currentTab?.isOriginalBaseline
                ? "Original baseline cannot be made primary"
                : JSON.stringify(entries) ===
                  JSON.stringify(currentTab?.entries)
                ? "This tab is currently in sync with the main application"
                : "Apply changes to main application"
            }
            disabled={
              currentTab?.isOriginalBaseline ||
              JSON.stringify(entries) === JSON.stringify(currentTab?.entries)
            }
          >
            <span>⭐</span>
            {currentTab?.isOriginalBaseline
              ? "Baseline Tab"
              : JSON.stringify(entries) === JSON.stringify(currentTab?.entries)
              ? "In Sync"
              : "Sync to Main"}
          </button>
          <button
            onClick={onUndo}
            className={`p-2 rounded ${
              darkMode
                ? "hover:bg-gray-700 text-gray-300"
                : "hover:bg-gray-100 text-gray-600"
            }`}
            title="Undo last action"
          >
            <RotateCcw />
          </button>
        </div>
      </div>

      {activeTab2 === "analysis" && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex flex-wrap gap-1 mb-4 border-b pb-2 shrink-0">
            {processTabs.map((tab) => (
              <div key={tab.id} className="flex items-center">
                <button
                  onClick={() => {
                    if (document.activeElement) document.activeElement.blur();
                    window.focus();
                    setActiveProcessTab(tab.id);
                  }}
                  className={`px-3 py-2 rounded-t border-b-2 text-sm flex items-center gap-2 ${
                    activeProcessTab === tab.id
                      ? primaryTab === tab.id
                        ? darkMode
                          ? "bg-green-700 text-white border-green-700"
                          : "bg-green-600 text-white border-green-600"
                        : tab.isOriginalBaseline
                        ? darkMode
                          ? "bg-gray-600 text-white border-gray-600"
                          : "bg-gray-500 text-white border-gray-500"
                        : darkMode
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-indigo-500 text-white border-indigo-500"
                      : darkMode
                      ? primaryTab === tab.id
                        ? "bg-green-800 text-green-200 border-transparent hover:bg-green-700"
                        : "bg-gray-700 text-gray-300 border-transparent hover:bg-gray-600"
                      : primaryTab === tab.id
                      ? "bg-green-100 text-green-800 border-transparent hover:bg-green-200"
                      : tab.isOriginalBaseline
                      ? "bg-gray-200 text-gray-700 border-transparent hover:bg-gray-300"
                      : "bg-gray-100 text-gray-700 border-transparent hover:bg-gray-200"
                  }`}
                >
                  {isRenamingTab === tab.id ? (
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") confirmRename();
                        if (e.key === "Escape") cancelRename();
                      }}
                      onBlur={confirmRename}
                      className={`text-sm px-1 rounded ${
                        darkMode
                          ? "bg-gray-600 text-white"
                          : "bg-white text-gray-900"
                      }`}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span>
                      {tab.isLocked && "🔒 "}
                      {primaryTab === tab.id &&
                        !tab.isOriginalBaseline &&
                        "⭐ "}
                      {tab.name}
                    </span>
                  )}

                  {!tab.isOriginalBaseline && !tab.isOriginal && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startRenaming(tab.id, tab.name);
                        }}
                        className="text-xs hover:bg-white hover:bg-opacity-20 rounded p-1"
                        title="Rename tab"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTab(tab.id);
                        }}
                        className="text-xs hover:bg-red-500 hover:bg-opacity-20 rounded p-1"
                        title="Delete tab"
                      >
                        ❌
                      </button>
                    </div>
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto pr-2">
            <div
              className={`mb-4 p-3 rounded-lg border ${
                currentTab?.isOriginalBaseline
                  ? darkMode
                    ? "bg-gray-900 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                  : primaryTab === activeProcessTab
                  ? darkMode
                    ? "bg-green-900 border-green-700"
                    : "bg-green-50 border-green-200"
                  : darkMode
                  ? "bg-blue-900 border-blue-700"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <h4
                className={`font-semibold mb-1 ${
                  currentTab?.isOriginalBaseline
                    ? darkMode
                      ? "text-gray-200"
                      : "text-gray-900"
                    : primaryTab === activeProcessTab
                    ? darkMode
                      ? "text-green-200"
                      : "text-green-900"
                    : darkMode
                    ? "text-blue-200"
                    : "text-blue-900"
                }`}
              >
                Current Tab: {currentTab?.name}
                {currentTab?.isLocked && " 🔒"}
                {primaryTab === activeProcessTab &&
                  !currentTab?.isOriginalBaseline &&
                  " ⭐ (Primary)"}
              </h4>
              <p
                className={`text-sm ${
                  currentTab?.isOriginalBaseline
                    ? darkMode
                      ? "text-gray-300"
                      : "text-gray-700"
                    : primaryTab === activeProcessTab
                    ? darkMode
                      ? "text-green-300"
                      : "text-green-700"
                    : darkMode
                    ? "text-blue-300"
                    : "text-blue-700"
                }`}
              >
                {currentTab?.isOriginalBaseline
                  ? "This is your original baseline data, preserved and locked for reference."
                  : primaryTab === activeProcessTab
                  ? "This is the primary tab. Changes here are reflected in the main application."
                  : currentTab?.isOriginal
                  ? "This is the working analysis tab. It reflects the current main application state."
                  : "This is a duplicate tab. Changes here are isolated until you click 'Sync to Main'."}
                {currentTab?.isLocked &&
                  !currentTab?.isOriginalBaseline &&
                  " This tab is locked - click 'Unlock' to make changes."}
              </p>
              <p
                className={`text-xs mt-1 ${
                  currentTab?.isOriginalBaseline
                    ? darkMode
                      ? "text-gray-400"
                      : "text-gray-600"
                    : primaryTab === activeProcessTab
                    ? darkMode
                      ? "text-green-400"
                      : "text-green-600"
                    : darkMode
                    ? "text-blue-400"
                    : "text-blue-600"
                }`}
              >
                Processes: {currentTabEntries.length} | Total Emissions:{" "}
                {currentTabEntries
                  .reduce((sum, e) => sum + e.total, 0)
                  .toFixed(2)}{" "}
                kg CO₂e
              </p>
            </div>

            <div className="space-y-4 pb-6">
              {isCurrentTabLocked() && (
                <div
                  className={`p-3 rounded-lg border-2 border-dashed ${
                    darkMode
                      ? "border-red-500 bg-red-900 bg-opacity-20"
                      : "border-red-400 bg-red-50"
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 ${
                      darkMode ? "text-red-300" : "text-red-700"
                    }`}
                  >
                    <span className="text-lg">🔒</span>
                    <div>
                      <h5 className="font-semibold">Tab is Locked</h5>
                      <p className="text-sm">
                        {currentTab?.isOriginalBaseline
                          ? "This original baseline tab is permanently locked to preserve your reference data."
                          : "This tab is locked to prevent accidental changes. Click 'Unlock' above to enable modifications."}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {Object.keys(EN15804).map((mod) => {
                const procs = currentTabEntries
                  .map((e, i) => ({ ...e, globalIdx: i }))
                  .filter((e) => e.module === mod);

                if (!procs.length) return null;

                return (
                  <div key={mod}>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {EN15804[mod]}
                    </h4>
                    <div className="space-y-2 mt-2">
                      {procs.map((proc) => {
                        const globalIdx = proc.globalIdx;
                        const hasAlternative = alternativeSelections[globalIdx];
                        const isShowingAlternative =
                          showAlternativeFor === globalIdx;

                        return (
                          <div
                            key={globalIdx}
                            className="border border-gray-300 dark:border-gray-600 p-3 rounded bg-white dark:bg-gray-800"
                          >
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {proc.title}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  Current: {proc.label} - {proc.ef} kg CO₂e/unit
                                  {proc.scope && (
                                    <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                                      {SCOPES[proc.scope]}
                                    </span>
                                  )}
                                  {proc.operation && (
                                    <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded">
                                      Operation: {proc.operation}
                                    </span>
                                  )}
                                </div>
                                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {proc.total} kg CO₂e
                                  {proc.isAlternative && (
                                    <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                                      Alternative Applied
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                {!isShowingAlternative && (
                                  <button
                                    onClick={() => {
                                      if (document.activeElement)
                                        document.activeElement.blur();
                                      window.focus();
                                      showAlternativeDialog(globalIdx);
                                    }}
                                    disabled={isCurrentTabLocked()}
                                    className={`px-3 py-1 rounded flex items-center gap-1 ${
                                      isCurrentTabLocked()
                                        ? darkMode
                                          ? "bg-gray-700 text-gray-500 cursor-not-allowed border border-gray-600"
                                          : "bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300"
                                        : "bg-blue-600 text-white hover:bg-blue-700"
                                    }`}
                                    title={
                                      isCurrentTabLocked()
                                        ? "Unlock tab to make changes"
                                        : "Create alternative process"
                                    }
                                  >
                                    <ArrowRight /> Alternative
                                  </button>
                                )}
                              </div>
                            </div>

                            {isShowingAlternative && (
                              <div
                                className={`mt-3 p-3 rounded border ${
                                  darkMode
                                    ? "bg-gray-700 border-gray-600"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <h5
                                  className={`font-medium mb-2 ${
                                    darkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  Alternative Process Configuration:
                                </h5>

                                <input
                                  className={`w-full mb-2 p-2 border rounded ${
                                    darkMode
                                      ? "border-gray-600 bg-gray-600 text-white placeholder-gray-400"
                                      : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                                  }`}
                                  placeholder="Process Title"
                                  value={alternativeForm.title}
                                  onChange={(e) =>
                                    setAlternativeForm((f) => ({
                                      ...f,
                                      title: e.target.value,
                                    }))
                                  }
                                />

                                <textarea
                                  className={`w-full mb-2 p-2 border rounded ${
                                    darkMode
                                      ? "border-gray-600 bg-gray-600 text-white placeholder-gray-400"
                                      : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                                  }`}
                                  placeholder="Description"
                                  value={alternativeForm.desc}
                                  onChange={(e) =>
                                    setAlternativeForm((f) => ({
                                      ...f,
                                      desc: e.target.value,
                                    }))
                                  }
                                />

                                <div className="mb-2">
                                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                                    Operation Grouping
                                  </label>
                                  {(() => {
                                    const moduleOperations = [
                                      ...new Set(
                                        currentTabEntries
                                          .filter(
                                            (e) =>
                                              e.module === proc.module &&
                                              e.operation
                                          )
                                          .map((e) => e.operation)
                                      ),
                                    ];

                                    return (
                                      <>
                                        {moduleOperations.length > 0 && (
                                          <div className="mb-2">
                                            <label className="flex items-center mb-2">
                                              <input
                                                type="radio"
                                                name={`operationType_${globalIdx}`}
                                                checked={
                                                  !alternativeForm.isNewOperation
                                                }
                                                onChange={() =>
                                                  setAlternativeForm((f) => ({
                                                    ...f,
                                                    isNewOperation: false,
                                                    newOperation: "",
                                                  }))
                                                }
                                                className="mr-2"
                                              />
                                              <span className="text-sm text-gray-900 dark:text-white">
                                                Existing Operation
                                              </span>
                                            </label>

                                            {!alternativeForm.isNewOperation && (
                                              <select
                                                className={`w-full p-2 border rounded ${
                                                  darkMode
                                                    ? "border-gray-600 bg-gray-600 text-white"
                                                    : "border-gray-300 bg-white text-gray-900"
                                                }`}
                                                value={
                                                  alternativeForm.operation
                                                }
                                                onChange={(e) =>
                                                  setAlternativeForm((f) => ({
                                                    ...f,
                                                    operation: e.target.value,
                                                  }))
                                                }
                                              >
                                                <option value="">
                                                  Select existing operation...
                                                </option>
                                                {moduleOperations.map((op) => (
                                                  <option key={op} value={op}>
                                                    {op}
                                                  </option>
                                                ))}
                                              </select>
                                            )}
                                          </div>
                                        )}

                                        <label className="flex items-center mb-2">
                                          <input
                                            type="radio"
                                            name={`operationType_${globalIdx}`}
                                            checked={
                                              alternativeForm.isNewOperation
                                            }
                                            onChange={() =>
                                              setAlternativeForm((f) => ({
                                                ...f,
                                                isNewOperation: true,
                                                operation: "",
                                              }))
                                            }
                                            className="mr-2"
                                          />
                                          <span className="text-sm text-gray-900 dark:text-white">
                                            {moduleOperations.length > 0
                                              ? "New Operation"
                                              : "Create Operation"}
                                          </span>
                                        </label>

                                        {alternativeForm.isNewOperation && (
                                          <input
                                            className={`w-full p-2 border rounded ${
                                              darkMode
                                                ? "border-gray-600 bg-gray-600 text-white placeholder-gray-400"
                                                : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                                            }`}
                                            placeholder="Enter new operation name..."
                                            value={alternativeForm.newOperation}
                                            onChange={(e) =>
                                              setAlternativeForm((f) => ({
                                                ...f,
                                                newOperation: e.target.value,
                                              }))
                                            }
                                          />
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>

                                <select
                                  className={`w-full mb-2 p-2 border rounded ${
                                    darkMode
                                      ? "border-gray-600 bg-gray-600 text-white"
                                      : "border-gray-300 bg-white text-gray-900"
                                  }`}
                                  value={alternativeForm.scope}
                                  onChange={(e) =>
                                    setAlternativeForm((f) => ({
                                      ...f,
                                      scope: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="">Select Scope</option>
                                  {Object.entries(SCOPES).map(
                                    ([key, value]) => (
                                      <option key={key} value={key}>
                                        {value}
                                      </option>
                                    )
                                  )}
                                </select>

                                <select
                                  className={`w-full mb-2 p-2 border rounded ${
                                    darkMode
                                      ? "border-gray-600 bg-gray-600 text-white"
                                      : "border-gray-300 bg-white text-gray-900"
                                  }`}
                                  value={alternativeForm.label}
                                  onChange={(e) =>
                                    setAlternativeForm((f) => ({
                                      ...f,
                                      label: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="">
                                    Select EPD Alternative...
                                  </option>
                                  {Object.keys(factors).map((label) => (
                                    <option
                                      key={label}
                                      value={label}
                                      className={
                                        darkMode
                                          ? "bg-gray-600 text-white"
                                          : "bg-white text-gray-900"
                                      }
                                    >
                                      {label} ({factors[label]} kg CO₂e/unit)
                                    </option>
                                  ))}
                                </select>

                                <input
                                  type="number"
                                  className={`w-full mb-2 p-2 border rounded ${
                                    darkMode
                                      ? "border-gray-600 bg-gray-600 text-white placeholder-gray-400"
                                      : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                                  }`}
                                  placeholder="Mass (kg)"
                                  value={alternativeForm.mass}
                                  onChange={(e) =>
                                    setAlternativeForm((f) => ({
                                      ...f,
                                      mass: e.target.value,
                                    }))
                                  }
                                />

                                <input
                                  type="number"
                                  className={`w-full mb-4 p-2 border rounded ${
                                    darkMode
                                      ? "border-gray-600 bg-gray-600 text-white placeholder-gray-400"
                                      : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                                  }`}
                                  placeholder="Custom EPD (kgCO₂e/unit)"
                                  value={alternativeForm.customEpd}
                                  onChange={(e) =>
                                    setAlternativeForm((f) => ({
                                      ...f,
                                      customEpd: e.target.value,
                                    }))
                                  }
                                />

                                <div className="flex space-x-2 mb-3">
                                  <button
                                    onClick={() => {
                                      if (document.activeElement)
                                        document.activeElement.blur();
                                      window.focus();
                                      handleAlternativeFormSubmit(globalIdx);
                                    }}
                                    className="flex-1 py-2 bg-blue-600 text-white rounded"
                                  >
                                    Calculate Alternative
                                  </button>
                                </div>

                                {hasAlternative && (
                                  <div className="space-y-2">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="font-medium text-gray-900 dark:text-white">
                                          Current:
                                        </span>
                                        <div className="text-gray-700 dark:text-gray-300">
                                          {proc.label}: {proc.total} kg CO₂e
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                          {SCOPES[proc.scope]}
                                        </div>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-900 dark:text-white">
                                          Alternative:
                                        </span>
                                        <div className="text-gray-700 dark:text-gray-300">
                                          {hasAlternative.label}:{" "}
                                          {hasAlternative.newTotal.toFixed(2)}{" "}
                                          kg CO₂e
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                          {SCOPES[hasAlternative.scope]}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="text-center">
                                      <span
                                        className={`font-bold text-lg ${
                                          hasAlternative.percentageChange > 0
                                            ? "text-red-600"
                                            : "text-green-600"
                                        }`}
                                      >
                                        {hasAlternative.percentageChange > 0
                                          ? "+"
                                          : ""}
                                        {hasAlternative.percentageChange.toFixed(
                                          1
                                        )}
                                        %
                                        {hasAlternative.percentageChange > 0
                                          ? " increase"
                                          : " reduction"}
                                      </span>
                                    </div>

                                    <div className="flex space-x-2 mt-3">
                                      <button
                                        onClick={() =>
                                          applyAlternative(globalIdx)
                                        }
                                        className="flex-1 py-2 bg-green-600 text-white rounded flex items-center justify-center gap-1"
                                      >
                                        <Check /> Apply Alternative
                                      </button>
                                      <button
                                        onClick={() =>
                                          rejectAlternative(globalIdx)
                                        }
                                        className="flex-1 py-2 bg-red-600 text-white rounded flex items-center justify-center gap-1"
                                      >
                                        <X /> Reject
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {!hasAlternative && (
                                  <button
                                    onClick={() => {
                                      if (document.activeElement)
                                        document.activeElement.blur();
                                      window.focus();
                                      setShowAlternativeFor(null);
                                    }}
                                    className="w-full py-2 bg-gray-600 text-white rounded"
                                  >
                                    Cancel
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Process Dialog Component with Fixed Scroll and Focus Protection
function ProcessDialog({
  showDialog,
  setShowDialog,
  form,
  setForm,
  factors,
  activeIndex,
  onSave,
  onDelete,
  title = "Add Process",
  darkMode = false,
  entries = [],
  activeModule = null,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const handleClose = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();
    setShowDialog(false);
  };

  useEffect(() => {
    if (showDialog) {
      setSearchTerm(form.label || "");
    } else {
      setSearchTerm("");
      setShowDropdown(false);
    }
  }, [showDialog, form.label]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showDropdown]);

  if (!showDialog) return null;

  const getOperationsByModule = (module) => {
    if (!module || !entries) return [];
    return [
      ...new Set(
        entries
          .filter((e) => e.module === module && e.operation)
          .map((e) => e.operation)
      ),
    ];
  };

  const existingOperations = getOperationsByModule(activeModule);
  const filteredFactors = Object.keys(factors).filter((label) =>
    label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEpdSelect = (label) => {
    setForm((f) => ({ ...f, label }));
    setSearchTerm(label);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setForm((f) => ({ ...f, label: value }));
    setShowDropdown(value.length > 0);
  };

  const handleSearchFocus = () => {
    if (searchTerm.length > 0) {
      setShowDropdown(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto border"
        style={{
          backgroundColor: darkMode ? "#1f2937" : "#ffffff",
          borderColor: darkMode ? "#374151" : "#e5e7eb",
        }}
      >
        <div
          className={`p-6 border-b ${
            darkMode ? "border-gray-600" : "border-gray-200"
          }`}
        >
          <div className="flex justify-between items-center">
            <div>
              <h3
                className={`text-2xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {title}
              </h3>
              <p
                className={`text-sm mt-1 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {activeModule
                  ? `Adding to ${EN15804[activeModule]}`
                  : "Process Configuration"}
              </p>
            </div>
            <button
              onClick={handleClose}
              className={`p-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label
                className={`block text-sm font-medium mb-2 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Process Title *
              </label>
              <input
                className={`w-full p-4 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                  darkMode
                    ? "border-gray-600 bg-gray-700 text-white placeholder-gray-400"
                    : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                }`}
                placeholder="Enter a descriptive title for this process"
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
              />
            </div>

            <div className="md:col-span-2">
              <label
                className={`block text-sm font-medium mb-2 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Description
              </label>
              <textarea
                className={`w-full p-4 border rounded-xl h-24 resize-none transition-all focus:ring-2 focus:ring-blue-500 ${
                  darkMode
                    ? "border-gray-600 bg-gray-700 text-white placeholder-gray-400"
                    : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                }`}
                placeholder="Optional description or notes about this process"
                value={form.desc}
                onChange={(e) =>
                  setForm((f) => ({ ...f, desc: e.target.value }))
                }
              />
            </div>
          </div>

          <div
            className={`p-4 rounded-xl border ${
              darkMode
                ? "border-gray-600 bg-gray-800"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <label
              className={`block text-sm font-medium mb-3 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Operation Grouping
            </label>

            {existingOperations.length > 0 && (
              <div className="mb-4">
                <label className="flex items-center mb-3 cursor-pointer">
                  <input
                    type="radio"
                    name="operationType"
                    checked={!form.isNewOperation}
                    onChange={() =>
                      setForm((f) => ({
                        ...f,
                        isNewOperation: false,
                        newOperation: "",
                      }))
                    }
                    className="mr-3 h-4 w-4 text-blue-600"
                  />
                  <span
                    className={`text-sm font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Use Existing Operation
                  </span>
                </label>

                {!form.isNewOperation && (
                  <select
                    className={`w-full p-3 border rounded-lg transition-all focus:ring-2 focus:ring-blue-500 ${
                      darkMode
                        ? "border-gray-600 bg-gray-700 text-white"
                        : "border-gray-300 bg-white text-gray-900"
                    }`}
                    value={form.operation}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, operation: e.target.value }))
                    }
                  >
                    <option value="">Select existing operation...</option>
                    {existingOperations.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <label className="flex items-center mb-3 cursor-pointer">
              <input
                type="radio"
                name="operationType"
                checked={form.isNewOperation}
                onChange={() =>
                  setForm((f) => ({
                    ...f,
                    isNewOperation: true,
                    operation: "",
                  }))
                }
                className="mr-3 h-4 w-4 text-blue-600"
              />
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {existingOperations.length > 0
                  ? "Create New Operation"
                  : "Create Operation"}
              </span>
            </label>

            {form.isNewOperation && (
              <input
                className={`w-full p-3 border rounded-lg transition-all focus:ring-2 focus:ring-blue-500 ${
                  darkMode
                    ? "border-gray-600 bg-gray-700 text-white placeholder-gray-400"
                    : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                }`}
                placeholder="Enter new operation name..."
                value={form.newOperation}
                onChange={(e) =>
                  setForm((f) => ({ ...f, newOperation: e.target.value }))
                }
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                GHG Protocol Scope *
              </label>
              <select
                className={`w-full p-4 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                  darkMode
                    ? "border-gray-600 bg-gray-700 text-white"
                    : "border-gray-300 bg-white text-gray-900"
                }`}
                value={form.scope}
                onChange={(e) =>
                  setForm((f) => ({ ...f, scope: e.target.value }))
                }
              >
                <option value="">Select Scope</option>
                {Object.entries(SCOPES).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Mass (kg) *
              </label>
              <input
                type="number"
                className={`w-full p-4 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                  darkMode
                    ? "border-gray-600 bg-gray-700 text-white placeholder-gray-400"
                    : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                }`}
                placeholder="Enter mass in kilograms"
                value={form.mass}
                onChange={(e) =>
                  setForm((f) => ({ ...f, mass: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="relative">
            <label
              className={`block text-sm font-medium mb-2 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Emission Factor Selection *
            </label>

            {Object.keys(factors).length === 0 ? (
              <div
                className={`p-4 border-2 border-dashed rounded-xl text-center ${
                  darkMode
                    ? "border-gray-600 bg-gray-800"
                    : "border-gray-300 bg-gray-50"
                }`}
              >
                <div
                  className={`text-sm ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  📤 No EPD database loaded. Please upload an Excel/CSV file
                  with emission factors first.
                </div>
              </div>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <input
                  className={`w-full p-4 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                    darkMode
                      ? "border-gray-600 bg-gray-700 text-white placeholder-gray-400"
                      : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                  }`}
                  placeholder="Type to search EPD factors..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                />

                {showDropdown && filteredFactors.length > 0 && (
                  <div
                    className={`absolute left-0 right-0 mt-2 border rounded-xl shadow-2xl z-50 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-gray-300"
                    }`}
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      WebkitOverflowScrolling: "touch",
                      scrollbarWidth: "thin",
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {filteredFactors.map((label) => (
                      <div
                        key={label}
                        className={`p-4 cursor-pointer transition-colors border-b last:border-b-0 hover:${
                          darkMode ? "bg-gray-600" : "bg-gray-50"
                        } ${
                          darkMode
                            ? "text-white border-gray-600"
                            : "text-gray-900 border-gray-100"
                        }`}
                        onClick={() => handleEpdSelect(label)}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="font-medium text-sm mb-1">{label}</div>
                        <div
                          className={`text-xs ${
                            darkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {factors[label]} kg CO₂e/unit
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {showDropdown &&
                  searchTerm.length > 0 &&
                  filteredFactors.length === 0 && (
                    <div
                      className={`absolute left-0 right-0 mt-2 p-4 border rounded-xl shadow-lg text-center z-50 ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-gray-400"
                          : "bg-white border-gray-300 text-gray-600"
                      }`}
                    >
                      🔍 No EPD factors found matching "{searchTerm}"
                    </div>
                  )}
              </div>
            )}
          </div>

          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Custom Emission Factor (kgCO₂e/unit)
            </label>
            <input
              type="number"
              className={`w-full p-4 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 ${
                darkMode
                  ? "border-gray-600 bg-gray-700 text-white placeholder-gray-400"
                  : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
              }`}
              placeholder="Override with custom emission factor"
              value={form.customEpd}
              onChange={(e) =>
                setForm((f) => ({ ...f, customEpd: e.target.value }))
              }
            />
            <p
              className={`text-xs mt-2 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Leave empty to use selected EPD factor above
            </p>
          </div>
        </div>

        <div
          className={`p-6 border-t ${
            darkMode
              ? "border-gray-600 bg-gray-800"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex justify-between gap-4">
            {activeIndex !== null && onDelete && (
              <button
                onClick={onDelete}
                className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-medium flex items-center gap-2"
              >
                <Trash2 size={18} />
                Delete
              </button>
            )}
            <div className="flex gap-3 ml-auto">
              <button
                onClick={handleClose}
                className={`px-6 py-3 border rounded-xl font-medium transition-colors ${
                  darkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                className="px-8 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium flex items-center gap-2"
              >
                <Check size={18} />
                {activeIndex !== null ? "Update Process" : "Add Process"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main App Component
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [factors, setFactors] = useState(sampleFactors);
  const [darkMode, setDarkMode] = useState(true);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [projectCashFlows, setProjectCashFlows] = useState([]);

  useEffect(() => {
    if (isAuthenticated && !showGuide) {
      setTimeout(() => {
        window.focus();
        if (document.activeElement) document.activeElement.blur();
        document.body.focus();
      }, 100);
    }
  }, [isAuthenticated, showGuide]);

  // --- WORKSPACE (WINDOW) STATE ARCHITECTURE ---
  const defaultEconomicInputs = {
    initialInvestment: 500000,
    projectLifespan: 20,
    discountRate: 8,
    inflationRate: 3,
    annualSavings: 50000,
    annualOperatingCost: 10000,
    residualValue: 50000,
    carbonSavingsPerYear: 100,
    carbonPriceGrowthRate: 5,
  };

  const defaultCarbonPricing = {
    system: "",
    carbonTax: {
      enabled: false,
      rate: 50,
      coverage: ["scope1", "scope2"],
      escalationRate: 10,
    },
    ets: {
      enabled: false,
      currentPrice: 75,
      freeAllocation: 400,
      coverage: ["scope1"],
      priceVolatility: 0.15,
    },
    offsets: { enabled: true, maxPercentage: 50, price: 35 },
  };

  const createNewWorkspace = (id, name) => ({
    id,
    name,
    entries: [],
    processTabs: [
      {
        id: "original",
        name: "Process Analysis",
        entries: [],
        isOriginal: true,
        isLocked: false,
      },
    ],
    activeProcessTab: "original",
    primaryTab: "original",
    originalBaselineEntries: [],
    economicInputs: { ...defaultEconomicInputs },
    carbonPricing: JSON.parse(JSON.stringify(defaultCarbonPricing)),
    selectedPricingSystem: "",
    financialResults: { npv: 0, irr: 0, payback: Infinity, cashFlows: [] },
  });

  const [workspaces, setWorkspaces] = useState([
    createNewWorkspace("window_1", "Project 1"),
  ]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState("window_1");
  const [isRenamingWorkspace, setIsRenamingWorkspace] = useState(null);
  const [workspaceRenameValue, setWorkspaceRenameValue] = useState("");

  // View States
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({
    title: "",
    desc: "",
    mass: "",
    label: "",
    customEpd: "",
    scope: "",
    operation: "",
    newOperation: "",
    isNewOperation: false,
  });
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeModule, setActiveModule] = useState(null);
  const [showPie, setShowPie] = useState(false);
  const [showReduction, setShowReduction] = useState(false);
  const [showEconomicModeling, setShowEconomicModeling] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [pieChartZoom, setPieChartZoom] = useState(1);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [actionHistory, setActionHistory] = useState([]);

  const fileInputRef = useRef(null);
  const loadFileInputRef = useRef(null);

  // --- WORKSPACE HELPERS ---
  const currentWorkspace =
    workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0];

  const updateWorkspace = (id, updates) => {
    setWorkspaces((prev) =>
      prev.map((w) => (w.id === id ? { ...w, ...updates } : w))
    );
  };

  const createWorkspace = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    saveStateForUndo();
    const newId = `window_${Date.now()}`;
    const newName = `Project ${workspaces.length + 1}`;
    setWorkspaces((prev) => [...prev, createNewWorkspace(newId, newName)]);
    setActiveWorkspaceId(newId);
    showLifeCycleAssessment();
  };

  const deleteWorkspace = (id, e) => {
    e.stopPropagation();
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    if (workspaces.length === 1) {
      alert("You must have at least one active window.");
      return;
    }
    const isConfirm = window.confirm(
      "Are you sure you want to close this project window?"
    );
    if (!isConfirm) return;

    saveStateForUndo();
    const newWorkspaces = workspaces.filter((w) => w.id !== id);
    setWorkspaces(newWorkspaces);
    if (activeWorkspaceId === id) {
      setActiveWorkspaceId(newWorkspaces[0].id);
    }
  };

  const startRenamingWorkspace = (id, name, e) => {
    e.stopPropagation();
    setIsRenamingWorkspace(id);
    setWorkspaceRenameValue(name);
  };

  const confirmWorkspaceRename = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    if (!workspaceRenameValue.trim()) {
      setIsRenamingWorkspace(null);
      return;
    }
    updateWorkspace(isRenamingWorkspace, { name: workspaceRenameValue.trim() });
    setIsRenamingWorkspace(null);
    setWorkspaceRenameValue("");
  };

  const cancelWorkspaceRename = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    setIsRenamingWorkspace(null);
    setWorkspaceRenameValue("");
  };

  useEffect(() => {
    document.title = "NZ LCA - Life Cycle Assessment Platform";
  }, []);

  useEffect(() => {
    const current = workspaces.find((w) => w.id === activeWorkspaceId);
    if (!current) return;

    const updatedTabs = current.processTabs.map((tab) =>
      tab.id === "original" ? { ...tab, entries: [...current.entries] } : tab
    );

    if (JSON.stringify(updatedTabs) !== JSON.stringify(current.processTabs)) {
      updateWorkspace(current.id, { processTabs: updatedTabs });
    }
  }, [workspaces, activeWorkspaceId]);

  const saveStateForUndo = () => {
    setActionHistory((prev) => [
      ...prev.slice(-9),
      {
        workspaces: JSON.parse(JSON.stringify(workspaces)),
        factors: { ...factors },
        activeWorkspaceId,
      },
    ]);
  };

  const handleUndo = () => {
    if (actionHistory.length === 0) {
      alert("No actions to undo");
      return;
    }
    const lastState = actionHistory[actionHistory.length - 1];
    setWorkspaces(lastState.workspaces);
    setFactors(lastState.factors);
    setActiveWorkspaceId(lastState.activeWorkspaceId);
    setActionHistory((prev) => prev.slice(0, -1));
  };

  const handleSaveProject = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    try {
      const projectData = {
        version: "2.0.0",
        timestamp: new Date().toISOString(),
        data: { factors, darkMode, workspaces, activeWorkspaceId },
      };

      const dataStr = JSON.stringify(projectData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const fileName = `LCA_MultiProject_${
        new Date().toISOString().split("T")[0]
      }.json`;

      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(dataBlob);
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);

      alert(`✅ Workspaces saved successfully as ${fileName}`);
    } catch (error) {
      alert("Error saving project. Please try again.");
    }
  };

  const handleLoadProject = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const projectData = JSON.parse(fileContent);
      if (!projectData.data) throw new Error("Invalid project file format");

      saveStateForUndo();

      if (projectData.data.factors) setFactors(projectData.data.factors);
      if (typeof projectData.data.darkMode === "boolean")
        setDarkMode(projectData.data.darkMode);

      if (projectData.data.workspaces) {
        setWorkspaces(projectData.data.workspaces);
        setActiveWorkspaceId(
          projectData.data.activeWorkspaceId ||
            projectData.data.workspaces[0].id
        );
      } else if (projectData.data.entries) {
        const newId = `window_legacy_${Date.now()}`;
        const w = createNewWorkspace(newId, "Imported Project");

        w.entries = projectData.data.entries.map((entry) => ({
          ...entry,
          isUsingUploadedEPD:
            entry.isUsingUploadedEPD !== undefined
              ? entry.isUsingUploadedEPD
              : !entry.isCustom &&
                projectData.data.factors &&
                projectData.data.factors[entry.label],
          isAlternative: entry.isAlternative || false,
          isCustom: entry.isCustom || false,
          scope: entry.scope || "1",
          operation: entry.operation || "",
        }));

        w.processTabs = [
          {
            id: "original_baseline",
            name: "Original Process",
            entries: [...w.entries],
            isOriginalBaseline: true,
            isLocked: true,
          },
          {
            id: "original",
            name: "Process Analysis",
            entries: [...w.entries],
            isOriginal: true,
            isLocked: false,
          },
        ];

        if (projectData.data.economicInputs)
          w.economicInputs = projectData.data.economicInputs;
        if (projectData.data.carbonPricing)
          w.carbonPricing = projectData.data.carbonPricing;
        if (projectData.data.selectedPricingSystem)
          w.selectedPricingSystem = projectData.data.selectedPricingSystem;

        setWorkspaces((prev) => [...prev, w]);
        setActiveWorkspaceId(newId);
      }

      setShowDialog(false);
      showLifeCycleAssessment();
      alert(`✅ Project loaded successfully!`);
    } catch (error) {
      alert("Error loading project file.");
    } finally {
      if (loadFileInputRef.current) loadFileInputRef.current.value = "";
    }
  };

  const triggerLoadProject = () => {
    // FIX: Drop focus before opening Windows File Dialog
    if (document.activeElement) document.activeElement.blur();
    window.focus();
    loadFileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploadingFile(true);
    try {
      saveStateForUndo();
      const epdData = await parseEmissionFactorsFile(file);
      setFactors(epdData);
    } catch (error) {
      alert(`Error parsing file: ${error.message}`);
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const triggerFileUpload = () => {
    // FIX: Drop focus before opening Windows File Dialog
    if (document.activeElement) document.activeElement.blur();
    window.focus();
    fileInputRef.current?.click();
  };

  const exportPdf = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    try {
      if (!currentWorkspace.entries || currentWorkspace.entries.length === 0) {
        alert("No data to export. Please add some processes first.");
        return;
      }

      const totalEmissionsByScope = {
        scope1: currentWorkspace.entries
          .filter((e) => e.scope === "1")
          .reduce((sum, e) => sum + (e.total || 0), 0),
        scope2: currentWorkspace.entries
          .filter((e) => e.scope === "2")
          .reduce((sum, e) => sum + (e.total || 0), 0),
        scope3: currentWorkspace.entries
          .filter((e) => e.scope === "3")
          .reduce((sum, e) => sum + (e.total || 0), 0),
      };

      const total = currentWorkspace.entries.reduce(
        (sum, entry) => sum + (entry.total || 0),
        0
      );
      const eco = currentWorkspace.economicInputs;
      const results = currentWorkspace.financialResults;
      const pricing = currentWorkspace.carbonPricing;

      let systemDetails = "No Carbon Pricing Selected";
      if (currentWorkspace.selectedPricingSystem === "carbonTax") {
        systemDetails = `Carbon Tax @ $${pricing.carbonTax.rate}/ton (Escalating at ${pricing.carbonTax.escalationRate}%/yr)`;
      } else if (currentWorkspace.selectedPricingSystem === "ets") {
        systemDetails = `ETS Trading @ $${pricing.ets.currentPrice}/ton (Free Allocation: ${pricing.ets.freeAllocation} tons)`;
      } else if (currentWorkspace.selectedPricingSystem === "both") {
        systemDetails = `Hybrid: ETS ($${pricing.ets.currentPrice}/t) + Tax ($${pricing.carbonTax.rate}/t)`;
      }

      const financialHtml = currentWorkspace.selectedPricingSystem
        ? `
        <div class="section" style="page-break-before: always;">
          <h2>Economic Analysis & Assumptions</h2>
          
          <div style="display: flex; justify-content: space-between; flex-wrap: wrap;">
            <div class="scope" style="width: 45%;">
              <h3>Financial Assumptions</h3>
              <table class="table">
                <tr><td>Initial Investment</td><td><strong>$${eco.initialInvestment.toLocaleString()}</strong></td></tr>
                <tr><td>Project Lifespan</td><td><strong>${
                  eco.projectLifespan
                } Years</strong></td></tr>
                <tr><td>Discount Rate (Hurdle)</td><td><strong>${
                  eco.discountRate
                }%</strong></td></tr>
                <tr><td>Annual OpEx</td><td><strong>$${eco.annualOperatingCost.toLocaleString()}/yr</strong></td></tr>
                <tr><td>Annual Gross Savings</td><td><strong>$${eco.annualSavings.toLocaleString()}/yr</strong></td></tr>
                <tr><td>Residual Value</td><td><strong>$${eco.residualValue.toLocaleString()}</strong></td></tr>
              </table>
            </div>

            <div class="scope" style="width: 45%;">
              <h3>Carbon Liability Assumptions</h3>
              <table class="table">
                <tr><td>Regulatory System</td><td><strong>${systemDetails}</strong></td></tr>
                <tr><td>Offsets Enabled</td><td><strong>${
                  pricing.offsets.enabled
                    ? `Yes (Max ${pricing.offsets.maxPercentage}%, $${pricing.offsets.price}/t)`
                    : "No"
                }</strong></td></tr>
                <tr><td>Est. Carbon Savings</td><td><strong>${
                  eco.carbonSavingsPerYear
                } tons/yr</strong></td></tr>
              </table>
            </div>
          </div>

          <div class="summary" style="background: ${
            results.npv > 0 && results.irr > eco.discountRate
              ? "#e6ffe6"
              : "#ffe6e6"
          }; border: 1px solid ${results.npv > 0 ? "#4CAF50" : "#f44336"};">
            <h3 style="margin-top: 0;">Investment Performance Results</h3>
            <div style="display: flex; justify-content: space-between;">
              <p><strong>Net Present Value (NPV):</strong> <span class="total" style="color: ${
                results.npv > 0 ? "#2E7D32" : "#c62828"
              }">$${results.npv.toLocaleString()}</span></p>
              <p><strong>Internal Rate of Return (IRR):</strong> <span class="total" style="color: ${
                results.irr > eco.discountRate ? "#2E7D32" : "#c62828"
              }">${results.irr.toFixed(1)}%</span></p>
              <p><strong>Payback Period:</strong> <span class="total">${
                results.payback === Infinity
                  ? "No Payback"
                  : results.payback.toFixed(1) + " Years"
              }</span></p>
            </div>
          </div>

          ${
            results.cashFlows && results.cashFlows.length > 0
              ? `
          <h3>Cash Flow Projection Timeline</h3>
          <table class="table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Net Cash Flow ($)</th>
                <th>Cumulative Cash Flow ($)</th>
              </tr>
            </thead>
            <tbody>
              ${results.cashFlows
                .map(
                  (cf) => `
                <tr>
                  <td>Year ${cf.year}</td>
                  <td style="color: ${cf.cashFlow < 0 ? "#dc2626" : "#16a34a"}">
                    ${cf.cashFlow < 0 ? "-" : ""}$${Math.abs(
                    cf.cashFlow
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                  </td>
                  <td style="color: ${
                    cf.cumulative < 0 ? "#dc2626" : "#16a34a"
                  }">
                    <strong>${cf.cumulative < 0 ? "-" : ""}$${Math.abs(
                    cf.cumulative
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}</strong>
                  </td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>`
              : ""
          }
        </div>
      `
        : "";

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${currentWorkspace.name} - LCA Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .summary { background: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 5px; }
            .section { margin: 20px 0; }
            .table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            .table th { background-color: #f2f2f2; font-weight: bold; }
            .scope { background: #e8f4f8; padding: 10px; margin: 10px 0; border-radius: 5px; }
            .total { font-weight: bold; color: #2563eb; font-size: 18px; }
            h1 { color: #333; margin-bottom: 5px; }
            h2 { color: #2563eb; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 30px; }
            h3 { color: #333; margin: 10px 0 5px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${currentWorkspace.name}</h1>
            <h3 style="color: #666; margin-top: 0;">Life Cycle & Economic Assessment Report</h3>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="summary">
            <h2>Executive Summary</h2>
            <p><strong>Total Carbon Footprint:</strong> <span class="total">${total.toFixed(
              2
            )} kg CO₂e</span></p>
            <p><strong>Total Processes:</strong> ${
              currentWorkspace.entries.length
            }</p>
          </div>
          
          <div class="section">
            <h2>Emissions by Scope</h2>
            <div class="scope">
              <h3>Scope 1 (Direct Emissions)</h3>
              <p><strong>${totalEmissionsByScope.scope1.toFixed(
                2
              )} kg CO₂e</strong></p>
            </div>
            <div class="scope">
              <h3>Scope 2 (Energy Indirect Emissions)</h3>
              <p><strong>${totalEmissionsByScope.scope2.toFixed(
                2
              )} kg CO₂e</strong></p>
            </div>
            <div class="scope">
              <h3>Scope 3 (Other Indirect Emissions)</h3>
              <p><strong>${totalEmissionsByScope.scope3.toFixed(
                2
              )} kg CO₂e</strong></p>
            </div>
          </div>
          
          <div class="section">
            <h2>Process Inventory</h2>
           <table class="table">
            <thead>
              <tr>
                <th>Process Name</th>
                <th>Module</th>
                <th>Operation</th>
                <th>Emission Factor</th>
                <th>EPD Type</th>
                <th>Total (kg CO₂e)</th>
                <th>Scope</th>
              </tr>
            </thead>
            <tbody>
              ${currentWorkspace.entries
                .map((entry) => {
                  let epdType = "Uploaded EPD";
                  if (entry.isAlternative) epdType = "Alternative Applied";
                  else if (
                    entry.isCustom ||
                    entry.isUsingUploadedEPD === false ||
                    (!entry.isUsingUploadedEPD && !factors[entry.label])
                  )
                    epdType = "Custom EPD";

                  return `
                <tr>
                  <td>${entry.title || "N/A"}</td>
                  <td>${entry.module || "N/A"}</td>
                  <td>${entry.operation || "Uncategorized"}</td>
                  <td>${entry.ef || "N/A"}</td>
                  <td>${epdType}</td>
                  <td><strong>${(entry.total || 0).toFixed(2)}</strong></td>
                  <td>Scope ${entry.scope || "N/A"}</td>
                </tr>
              `;
                })
                .join("")}
            </tbody>
          </table>
          </div>
          
          ${financialHtml}
          
          <div class="section">
            <h2>Methodology</h2>
            <p>This Life Cycle Assessment follows EN15804 standards for environmental product declarations.</p>
            <p><strong>Assessment Method:</strong> Cradle-to-grave analysis</p>
            <p><strong>System Boundary:</strong> Includes all relevant life cycle stages</p>
          </div>
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const fileName = `${currentWorkspace.name.replace(/\s+/g, "_")}_Report_${
        new Date().toISOString().split("T")[0]
      }.html`;

      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);

      const printWindow = window.open("", "_blank");
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      console.error("Error generating PDF report:", error);
      alert(`Error generating report: ${error.message}`);
    }
  };

  const onModuleClick = (mod) => {
    // FIX: Drop focus to prevent Windows cursor trap
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    if (Object.keys(factors).length === 0) {
      alert(`Please load an EPD database first!`);
      return;
    }
    setActiveModule(mod);
    setActiveIndex(null);
    setForm({
      title: "",
      desc: "",
      mass: "",
      label: "",
      customEpd: "",
      scope: "",
      operation: "",
      newOperation: "",
      isNewOperation: false,
    });
    setShowDialog(true);
  };

  const addOrUpdate = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    saveStateForUndo();
    const {
      title,
      desc,
      mass,
      label,
      customEpd,
      scope,
      operation,
      newOperation,
      isNewOperation,
    } = form;
    if (!mass || (!label && !customEpd) || !scope) {
      alert("Please fill in all required fields including scope");
      return;
    }

    let operationName =
      isNewOperation && newOperation.trim()
        ? newOperation.trim()
        : !isNewOperation && operation
        ? operation
        : "";
    const m = parseFloat(mass);
    const ef = customEpd ? parseFloat(customEpd) : factors[label] || 0;
    const total = +(ef * m).toFixed(3);
    const isUsingUploadedEPD =
      !customEpd && label && factors[label] !== undefined;

    const entry = {
      module: activeModule,
      title,
      desc,
      mass: m,
      label,
      ef,
      total,
      scope,
      operation: operationName,
      isCustom: !!customEpd,
      isAlternative: false,
      isUsingUploadedEPD,
    };

    const newEntries = [...currentWorkspace.entries];
    if (activeIndex !== null) newEntries[activeIndex] = entry;
    else newEntries.push(entry);

    updateWorkspace(currentWorkspace.id, { entries: newEntries });
    setShowDialog(false);
  };

  const deleteEntry = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    if (activeIndex !== null) {
      saveStateForUndo();
      updateWorkspace(currentWorkspace.id, {
        entries: currentWorkspace.entries.filter((_, i) => i !== activeIndex),
      });
      setShowDialog(false);
    }
  };

  const handleNodeClick = (_, node) => {
    if (node.id.startsWith("proc_")) {
      const idx = parseInt(node.id.split("_")[1], 10);
      const e = currentWorkspace.entries[idx];
      setActiveIndex(idx);
      setActiveModule(e.module);
      setForm({
        title: e.title,
        desc: e.desc,
        mass: e.mass.toString(),
        label: e.label,
        customEpd: e.isCustom ? e.ef.toString() : "",
        scope: e.scope || "",
        operation: e.operation || "",
        newOperation: "",
        isNewOperation: false,
      });
      setShowDialog(true);
    }
  };

  const showLifeCycleAssessment = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    setShowPie(false);
    setShowReduction(false);
    setShowEconomicModeling(false);
    setShowCompare(false);
    setSelectedProcess(null);
    setPieChartZoom(1);
  };

  const showPieChart = () => {
    if (document.activeElement) document.activeElement.blur();
    window.focus();

    setShowPie(true);
    setShowReduction(false);
    setShowEconomicModeling(false);
    setShowCompare(false);
    setSelectedProcess(null);
    setPieChartZoom(1);
  };

  useEffect(() => {
    const ns = [],
      es = [],
      mods = Object.keys(EN15804);
    const nodeSpacing = 350;
    const currentEntries = currentWorkspace.entries || [];

    mods.forEach((m, i) => {
      const tot = currentEntries
        .filter((e) => e.module === m)
        .reduce((s, e) => s + e.total, 0)
        .toFixed(2);
      ns.push({
        id: `mod_${m}`,
        type: "default",
        data: {
          label: (
            <div
              className={`${
                darkMode
                  ? "bg-gray-700 text-white"
                  : "bg-indigo-300 text-gray-900"
              } rounded text-xs text-center`}
              style={{
                width: "130px",
                height: "45px",
                fontSize: "10px",
                lineHeight: "1.1",
                padding: "4px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                boxSizing: "border-box",
                margin: "10px",
                overflow: "hidden",
              }}
            >
              <div style={{ fontSize: "9px", marginBottom: "2px" }}>
                {EN15804[m]}
              </div>
              <div style={{ fontWeight: "bold", fontSize: "10px" }}>
                {tot} kgCO₂e
              </div>
            </div>
          ),
        },
        position: { x: nodeSpacing * i, y: 50 },
        style: { width: 150, height: 65, padding: 0 },
      });
    });

    mods.forEach((m, moduleIndex) => {
      const mappedEntries = currentEntries.map((e, index) => ({
        ...e,
        originalIndex: index,
      }));
      const moduleEntries = mappedEntries.filter((e) => e.module === m);
      const operationGroups = {};

      moduleEntries.forEach((entry) => {
        const opName = entry.operation || "Uncategorized";
        if (!operationGroups[opName]) operationGroups[opName] = [];
        operationGroups[opName].push(entry);
      });

      let currentY = 150;

      Object.entries(operationGroups).forEach(([operationName, processes]) => {
        const operationColor = "#8b5cf6";
        if (operationName !== "Uncategorized") {
          const operationTotal = processes
            .reduce((sum, p) => sum + p.total, 0)
            .toFixed(2);
          ns.push({
            id: `op_${m}_${operationName}`,
            type: "default",
            data: {
              label: (
                <div
                  className="rounded text-xs text-center text-white font-semibold"
                  style={{
                    width: "180px",
                    height: "30px",
                    fontSize: "10px",
                    backgroundColor: operationColor,
                    lineHeight: "1.1",
                    padding: "4px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    boxSizing: "border-box",
                    margin: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "9px",
                      marginBottom: "1px",
                    }}
                  >
                    {operationName.length > 25
                      ? operationName.substring(0, 25) + "..."
                      : operationName}
                  </div>
                  <div style={{ fontSize: "8px" }}>{operationTotal} kgCO₂e</div>
                </div>
              ),
            },
            position: { x: nodeSpacing * moduleIndex - 15, y: currentY },
            style: { width: 200, height: 50, padding: 0 },
          });
          es.push({
            source: `mod_${m}`,
            target: `op_${m}_${operationName}`,
            animated: true,
            style: { stroke: operationColor, strokeWidth: 2 },
          });
          currentY += 80;
        }

        processes.forEach((process, processIndex) => {
          const globalIndex = process.originalIndex;
          let processColor = process.isAlternative
            ? "#10b981"
            : process.isCustom || !process.isUsingUploadedEPD
            ? "#FFA500"
            : "#4CAF50";
          const stackOffset = processIndex % 2 === 0 ? 0 : 140;
          const rowOffset = Math.floor(processIndex / 2) * 85;

          ns.push({
            id: `proc_${globalIndex}`,
            type: "default",
            data: {
              label: (
                <div
                  style={{
                    backgroundColor: processColor,
                    color: "#fff",
                    border:
                      operationName !== "Uncategorized"
                        ? `2px solid ${operationColor}`
                        : "none",
                    width: "130px",
                    height: "40px",
                    fontSize: "9px",
                    lineHeight: "1.1",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    padding: "3px",
                    boxSizing: "border-box",
                    margin: "10px",
                    overflow: "hidden",
                    borderRadius: "4px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "bold",
                      marginBottom: "1px",
                      fontSize: "8px",
                    }}
                  >
                    {process.title.length > 20
                      ? process.title.substring(0, 20) + "..."
                      : process.title}
                  </div>
                  <div style={{ fontSize: "8px" }}>
                    {process.total.toFixed(1)} kgCO₂e
                  </div>
                </div>
              ),
            },
            position: {
              x: nodeSpacing * moduleIndex + stackOffset,
              y: currentY + rowOffset,
            },
            style: { width: 150, height: 60, padding: 0 },
          });
          const sourceId =
            operationName !== "Uncategorized"
              ? `op_${m}_${operationName}`
              : `mod_${m}`;
          es.push({
            source: sourceId,
            target: `proc_${globalIndex}`,
            animated: true,
            style: {
              stroke:
                operationName !== "Uncategorized" ? operationColor : "#aaa",
              strokeWidth: operationName !== "Uncategorized" ? 2 : 1,
            },
          });
        });
        currentY += Math.ceil(processes.length / 2) * 85 + 50;
      });
    });

    setNodes(ns);
    setEdges(es);
  }, [currentWorkspace.entries, darkMode, factors]);

  const getOperationColor = (operationName, index) => "#8b5cf6";
  const getModuleColor = (moduleKey) => {
    const moduleColors = {
      A1: "#ff6b6b",
      A2: "#4ecdc4",
      A3: "#45b7d1",
      A4: "#96ceb4",
      A5: "#feca57",
      B1: "#ff9ff3",
      B2: "#54a0ff",
      B3: "#5f27cd",
      B4: "#00d2d3",
      B5: "#ff9f43",
      B6: "#ee5a24",
      B7: "#009432",
      C1: "#0652dd",
      C2: "#9c88ff",
      C3: "#fbc531",
      C4: "#4834d4",
      D: "#dda0dd",
    };
    return moduleColors[moduleKey] || "#888888";
  };

  const generatePieDataEntries = () => {
    return (currentWorkspace.entries || []).map((e) => {
      let baseColor = e.isAlternative
        ? "#10b981"
        : e.isCustom ||
          e.isUsingUploadedEPD === false ||
          (!e.isUsingUploadedEPD && !factors[e.label])
        ? "#FFA500"
        : "#22C55E";
      const operationColor = e.operation
        ? getOperationColor(e.operation, 0)
        : "#666666";
      const moduleColor = getModuleColor(e.module);

      return {
        name: e.title.length > 12 ? e.title.substring(0, 12) + "..." : e.title,
        fullName: e.title,
        value: e.total,
        fill: baseColor,
        stroke: operationColor,
        strokeWidth: 3,
        module: e.module,
        moduleColor: moduleColor,
        operation: e.operation || "Uncategorized",
        operationColor: operationColor,
        epdType: e.isAlternative
          ? "Alternative Applied"
          : e.isCustom ||
            e.isUsingUploadedEPD === false ||
            (!e.isUsingUploadedEPD && !factors[e.label])
          ? "Custom EPD"
          : "Uploaded EPD",
        epdColor: baseColor,
      };
    });
  };

  const pieDataEntries = generatePieDataEntries();

  const organizedPieData = useMemo(() => {
    const operationGroups = {};
    pieDataEntries.forEach((entry, index) => {
      const opName = entry.operation || "Uncategorized";
      if (!operationGroups[opName]) operationGroups[opName] = [];
      operationGroups[opName].push({ ...entry, originalIndex: index });
    });

    const organizedData = [];
    Object.entries(operationGroups).forEach(([opName, processes]) => {
      processes.forEach((process, procIndex) => {
        organizedData.push({
          ...process,
          displayNumber: process.originalIndex + 1,
          operationIndex: procIndex,
          totalInOperation: processes.length,
        });
      });
    });
    return organizedData;
  }, [pieDataEntries]);

  const currentEntries = currentWorkspace.entries || [];
  const summary = Object.keys(EN15804).map((k) => ({
    moduleKey: k,
    emissions: currentEntries
      .filter((e) => e.module === k)
      .reduce((s, e) => s + e.total, 0),
  }));
  const total = summary.reduce((s, x) => s + x.emissions, 0).toFixed(2);
  const totalEmissions = parseFloat(total);

  if (!isAuthenticated)
    return (
      <NZLCALockScreen
        onLogin={(success) => {
          setIsAuthenticated(success);
          if (success) setShowGuide(true);
        }}
      />
    );

  return (
    <div
      className={`p-0 ${darkMode ? "dark" : ""}`}
      style={{ fontFamily: "system-ui, sans-serif" }}
    >
      <AppGuide
        showGuide={showGuide}
        setShowGuide={setShowGuide}
        darkMode={darkMode}
      />

      <style>{`
        body { margin: 0; padding: 0; font-family: system-ui, sans-serif; overflow: hidden; }
        .sidebar-scroll::-webkit-scrollbar { width: 8px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; border-radius: 4px; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: ${
          darkMode ? "#4b5563" : "#cbd5e1"
        }; border-radius: 4px; border: 2px solid transparent; background-clip: content-box; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: ${
          darkMode ? "#6b7280" : "#94a3b8"
        }; border: 2px solid transparent; background-clip: content-box; }
        .react-flow__controls { color: #000 !important; }
        .react-flow__controls:hover { color: #000 !important; }
        .bg-blue-500 { background-color: #3b82f6; }
        .dark { color-scheme: dark; }
        .react-flow-mockup { border: 1px solid ${
          darkMode ? "#4b5563" : "#d1d5db"
        }; border-radius: 4px; background: ${
        darkMode ? "#1a202c" : "#f8fafc"
      }; padding: 1rem; position: relative; min-height: 400px; overflow: hidden; }
        .react-flow-node { cursor: pointer; transition: transform 0.2s; }
        .react-flow-node:hover { transform: scale(1.05); }
        input, select, textarea { background-color: ${
          darkMode ? "#374151" : "#ffffff"
        }; color: ${darkMode ? "#f9fafb" : "#1f2937"}; border-color: ${
        darkMode ? "#6b7280" : "#d1d5db"
      }; }
        input:focus, select:focus, textarea:focus { outline: none; border-color: ${
          darkMode ? "#60a5fa" : "#2563eb"
        }; box-shadow: 0 0 0 3px ${
        darkMode ? "rgba(96, 165, 250, 0.1)" : "rgba(37, 99, 235, 0.1)"
      }; }
        .dark input, .dark select, .dark textarea { background-color: #374151; color: #f9fafb; border-color: #6b7280; }
        .dark .border { border-color: #6b7280; }
        .dark .border-t { border-top-color: #6b7280; }
        .dark .border-r { border-right-color: #6b7280; }
        .bg-blue-500 { background-color: #3b82f6; }
        .bg-green-500 { background-color: #22c55e; }
        .bg-green-600 { background-color: #16a34a; }
        .bg-blue-600 { background-color: #2563eb; }
        .bg-indigo-600 { background-color: #4f46e5; }
        .bg-teal-600 { background-color: #0d9488; }
        .bg-purple-600 { background-color: #9333ea; }
        .bg-orange-600 { background-color: #ea580c; }
        .bg-red-500 { background-color: #ef4444; }
        .bg-red-600 { background-color: #dc2626; }
        .bg-blue-100 { background-color: #dbeafe; }
        .bg-blue-900 { background-color: #1e3a8a; }
        .bg-green-100 { background-color: #dcfce7; }
        .bg-green-900 { background-color: #14532d; }
        .bg-purple-100 { background-color: #f3e8ff; }
        .bg-purple-900 { background-color: #581c87; }
        .bg-orange-100 { background-color: #fed7aa; }
        .bg-orange-900 { background-color: #9a3412; }
        .bg-red-100 { background-color: #fee2e2; }
        .bg-red-900 { background-color: #7f1d1d; }
        .bg-yellow-100 { background-color: #fef3c7; }
        .bg-yellow-900 { background-color: #78350f; }
        .bg-purple-50 { background-color: #faf5ff; }
        .bg-red-50 { background-color: #fef2f2; }
        .bg-orange-50 { background-color: #fff7ed; }
        .bg-green-50 { background-color: #f0fdf4; }
        .bg-blue-50 { background-color: #eff6ff; }
        .bg-yellow-50 { background-color: #fefce8; }
        .bg-gray-50 { background-color: #f9fafb; }
        .bg-gray-100 { background-color: #f3f4f6; }
        .bg-gray-200 { background-color: #e5e7eb; }
        .bg-gray-600 { background-color: #4b5563; }
        .bg-gray-700 { background-color: #374151; }
        .bg-gray-800 { background-color: #1f2937; }
        .bg-gray-900 { background-color: #111827; }
        .text-white { color: white; }
        .text-black { color: black; }
        .text-red-500 { color: #ef4444; }
        .text-green-500 { color: #22c55e; }
        .text-green-600 { color: #16a34a; }
        .text-green-800 { color: #166534; }
        .text-green-200 { color: #bbf7d0; }
        .text-red-600 { color: #dc2626; }
        .text-blue-800 { color: #1e40af; }
        .text-blue-200 { color: #bfdbfe; }
        .text-blue-600 { color: #2563eb; }
        .text-purple-800 { color: #6b21a8; }
        .text-purple-200 { color: #e9d5ff; }
        .text-purple-600 { color: #9333ea; }
        .text-orange-800 { color: #9a3412; }
        .text-orange-200 { color: #fed7aa; }
        .text-red-800 { color: #991b1b; }
        .text-red-200 { color: #fecaca; }
        .text-yellow-800 { color: #92400e; }
        .text-yellow-200 { color: #fde68a; }
        .text-gray-600 { color: #4b5563; }
        .text-gray-400 { color: #9ca3af; }
        .text-gray-500 { color: #6b7280; }
        .text-gray-700 { color: #374151; }
        .text-gray-800 { color: #1f2937; }
        .text-gray-900 { color: #111827; }
        .border { border: 1px solid #e5e7eb; }
        .border-gray-300 { border-color: #d1d5db; }
        .border-gray-600 { border-color: #4b5563; }
        .border-blue-500 { border-color: #3b82f6; }
        .border-purple-500 { border-color: #8b5cf6; }
        .border-red-500 { border-color: #ef4444; }
        .border-orange-500 { border-color: #f97316; }
        .border-green-500 { border-color: #22c55e; }
        .border-t { border-top: 1px solid #e5e7eb; }
        .border-r { border-right: 1px solid #e5e7eb; }
        .border-l-4 { border-left-width: 4px; }
        .rounded { border-radius: 0.25rem; }
        .rounded-lg { border-radius: 0.5rem; }
        .rounded-t { border-top-left-radius: 0.25rem; border-top-right-radius: 0.25rem; }
        .shadow { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06); }
        .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); }
        .dark .border { border-color: #4b5563; }
        .dark .border-t { border-top-color: #4b5563; }
        .dark .border-r { border-right-color: #4b5563; }
        .flex { display: flex; }
        .flex-col { flex-direction: column; }
        .flex-grow { flex-grow: 1; }
        .flex-wrap { flex-wrap: wrap; }
        .flex-1 { flex: 1; }
        .items-center { align-items: center; }
        .justify-between { justify-content: space-between; }
        .justify-center { justify-content: center; }
        .space-y-1 > * + * { margin-top: 0.25rem; }
        .space-y-2 > * + * { margin-top: 0.5rem; }
        .space-y-3 > * + * { margin-top: 0.75rem; }
        .space-y-4 > * + * { margin-top: 1rem; }
        .space-x-1 > * + * { margin-left: 0.25rem; }
        .space-x-2 > * + * { margin-left: 0.5rem; }
        .gap-1 { gap: 0.25rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-4 { gap: 1rem; }
        .grid { display: grid; }
        .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
        .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .p-1 { padding: 0.25rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 0.75rem; }
        .p-4 { padding: 1rem; }
        .p-6 { padding: 1.5rem; }
        .px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
        .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
        .px-4 { padding-left: 1rem; padding-right: 1rem; }
        .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
        .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
        .m-2 { margin: 0.5rem; }
        .m-4 { margin: 1rem; }
        .mb-1 { margin-bottom: 0.25rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 0.75rem; }
        .mb-4 { margin-bottom: 1rem; }
        .mb-6 { margin-bottom: 1.5rem; }
        .ml-1 { margin-left: 0.25rem; }
        .ml-2 { margin-left: 0.5rem; }
        .ml-6 { margin-left: 1.5rem; }
        .mt-3 { margin-top: 0.75rem; }
        .mt-4 { margin-top: 1rem; }
        .mt-6 { margin-top: 1.5rem; }
        .h-64 { height: 16rem; }
        .h-48 { height: 12rem; }
        .h-full { height: 100%; }
        .h-screen { height: 100vh; }
        .w-64 { width: 16rem; }
        .w-80 { width: 20rem; }
        .w-96 { width: 24rem; }
        .w-full { width: 100%; }
        .max-h-32 { max-height: 8rem; }
        .max-h-96 { max-height: 24rem; }
        .overflow-auto { overflow: auto; }
        .overflow-hidden { overflow: hidden; }
        .overflow-x-auto { overflow-x: auto; }
        .overflow-y-auto { overflow-y: auto; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-left { text-align: left; }
        .text-xs { font-size: 0.75rem; }
        .text-sm { font-size: 0.875rem; }
        .text-lg { font-size: 1.125rem; }
        .text-xl { font-size: 1.25rem; }
        .text-2xl { font-size: 1.5rem; }
        .font-bold { font-weight: 700; }
        .font-semibold { font-weight: 600; }
        .font-medium { font-weight: 500; }
        .z-50 { z-index: 50; }
        .relative { position: relative; }
        .fixed { position: fixed; }
        .inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
        .bg-opacity-30 { --tw-bg-opacity: 0.3; }
        .hover\\:bg-gray-100:hover { background-color: #f3f4f6; }
        .hover\\:bg-gray-200:hover { background-color: #e5e7eb; }
        .hover\\:bg-gray-50:hover { background-color: #f9fafb; }
        .hover\\:bg-gray-600:hover { background-color: #4b5563; }
        .hover\\:bg-gray-700:hover { background-color: #374151; }
        .hover\\:bg-red-200:hover { background-color: #fecaca; }
        .hover\\:bg-red-900:hover { background-color: #7f1d1d; }
        .hover\\:bg-red-600:hover { background-color: #dc2626; }
        .hover\\:bg-green-600:hover { background-color: #16a34a; }
        .hover\\:bg-green-700:hover { background-color: #15803d; }
        .hover\\:text-gray-700:hover { color: #374151; }
        .hover\\:text-gray-900:hover { color: #111827; }
        .hover\\:text-gray-200:hover { color: #e5e7eb; }
        .hover\\:text-white:hover { color: #ffffff; }
        button:focus, input:focus, select:focus, textarea:focus { outline: 2px solid ${
          darkMode ? "#60a5fa" : "#2563eb"
        }; outline-offset: 2px; }
        .dark .text-gray-900 { color: #f9fafb; }
        .dark .text-gray-700 { color: #e5e7eb; }
        .dark .text-gray-600 { color: #d1d5db; }
        .dark .text-gray-500 { color: #9ca3af; }
        .dark .bg-white { background-color: #1f2937; }
        .dark .bg-gray-50 { background-color: #374151; }
        .dark .bg-gray-100 { background-color: #4b5563; }
        @media (min-width: 768px) {
          .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .md\\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        .hidden { display: none; }
      `}</style>

      {/* MAIN CONTAINER: FULL SCREEN, NO SCROLL */}
      <div
        className={`flex flex-1 h-screen w-full overflow-hidden ${
          darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
        }`}
      >
        {/* --- FIXED SIDEBAR (WIDER: 290px) --- */}
        <div
          className={`p-5 border-r flex flex-col h-full overflow-hidden ${
            darkMode
              ? "bg-gray-800 border-gray-600"
              : "bg-white border-gray-300"
          }`}
          style={{ width: "290px", flexShrink: 0 }}
        >
          {/* Header - Shrink 0 */}
          <div className="flex items-center justify-between mb-6 shrink-0">
            <h2 className="text-xl font-bold">NZ LCA TOOL</h2>
            <div className="flex space-x-2">
              <button
                onClick={handleUndo}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Undo last action"
              >
                <RotateCcw size={18} />
              </button>
              <button
                onClick={showLifeCycleAssessment}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Home"
              >
                <Home size={18} />
              </button>
            </div>
          </div>

          {/* PROJECT MANAGEMENT & NAVIGATION (Shrink 0 to keep them stationary) */}
          <div className="shrink-0 flex flex-col">
            {/* Project Management */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                Project Data
              </p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <button
                  onClick={handleSaveProject}
                  className={`py-2 px-3 border rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors shadow-sm hover:shadow ${
                    darkMode
                      ? "border-gray-600 bg-gray-700 text-white hover:bg-gray-600"
                      : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <Save size={16} /> Save
                </button>
                <input
                  ref={loadFileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleLoadProject}
                  className="hidden"
                />
                <button
                  onClick={triggerLoadProject}
                  className={`py-2 px-3 border rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors shadow-sm hover:shadow ${
                    darkMode
                      ? "border-gray-600 bg-gray-700 text-white hover:bg-gray-600"
                      : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <FolderOpen size={16} /> Load
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={triggerFileUpload}
                disabled={isUploadingFile}
                className={`w-full py-2 px-4 border rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors shadow-sm hover:shadow ${
                  darkMode
                    ? "border-gray-600 bg-gray-700 text-white hover:bg-gray-600"
                    : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                } ${isUploadingFile ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Upload size={16} />{" "}
                {isUploadingFile ? "Processing..." : "Upload EPD Database"}
              </button>

              {Object.keys(factors).length > 0 && (
                <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/30 rounded-lg text-xs text-center border border-green-200 dark:border-green-800">
                  <span className="text-green-800 dark:text-green-400 font-semibold">
                    ✅ {Object.keys(factors).length} EPDs loaded
                  </span>
                </div>
              )}
            </div>

            {/* Navigation Tabs */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-2.5">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                Analysis Tools
              </p>
              <button
                onClick={showLifeCycleAssessment}
                className={`w-full py-2 px-4 text-sm rounded-lg flex items-center justify-center gap-2 font-medium transition-colors shadow-sm ${
                  !showPie &&
                  !showReduction &&
                  !showEconomicModeling &&
                  !showCompare
                    ? "bg-blue-600 text-white shadow-md hover:bg-blue-700"
                    : darkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Activity size={16} /> LCA Canvas
              </button>
              <button
                onClick={showPieChart}
                className={`w-full py-2 px-4 text-sm rounded-lg flex items-center justify-center gap-2 font-medium transition-colors shadow-sm ${
                  showPie
                    ? "bg-purple-600 text-white shadow-md hover:bg-purple-700"
                    : darkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <BarChart3 size={16} /> Pie Chart
              </button>
              <button
                onClick={() => {
                  if (document.activeElement) document.activeElement.blur();
                  window.focus();

                  setShowReduction(true);
                  setShowPie(false);
                  setShowEconomicModeling(false);
                  setShowCompare(false);
                  setSelectedProcess(null);
                  setPieChartZoom(1);
                }}
                className={`w-full py-2 px-4 text-sm rounded-lg flex items-center justify-center gap-2 font-medium transition-colors shadow-sm ${
                  showReduction
                    ? "bg-indigo-600 text-white shadow-md hover:bg-indigo-700"
                    : darkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <RefreshCw size={16} /> Reduction Analysis
              </button>
              <button
                onClick={() => {
                  if (document.activeElement) document.activeElement.blur();
                  window.focus();

                  setShowEconomicModeling(true);
                  setShowPie(false);
                  setShowReduction(false);
                  setShowCompare(false);
                  setSelectedProcess(null);
                  setPieChartZoom(1);
                }}
                className={`w-full py-2 px-4 text-sm rounded-lg flex items-center justify-center gap-2 font-medium transition-colors shadow-sm ${
                  showEconomicModeling
                    ? "bg-orange-600 text-white shadow-md hover:bg-orange-700"
                    : darkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Calculator size={16} /> Economic Modeling
              </button>
              <button
                onClick={() => {
                  if (document.activeElement) document.activeElement.blur();
                  window.focus();

                  setShowCompare(true);
                  setShowPie(false);
                  setShowReduction(false);
                  setShowEconomicModeling(false);
                  setSelectedProcess(null);
                }}
                className={`w-full py-2 px-4 text-sm border-2 rounded-lg flex items-center justify-center gap-2 font-bold transition-colors shadow-sm ${
                  showCompare
                    ? "border-teal-500 bg-teal-50 text-teal-700 dark:bg-gray-800 dark:text-teal-400"
                    : darkMode
                    ? "border-gray-600 text-gray-400 hover:border-teal-500 hover:text-teal-300"
                    : "border-gray-300 text-gray-600 hover:border-teal-500 hover:text-teal-600"
                }`}
                style={{ borderStyle: "dashed" }}
              >
                ⚖️ Compare Projects
              </button>
            </div>

            {/* Utility Buttons */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-2.5">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                Utilities
              </p>
              <button
                onClick={() => setDarkMode((d) => !d)}
                className={`w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors shadow-sm ${
                  darkMode
                    ? "bg-yellow-600/20 text-yellow-500 hover:bg-yellow-600/30"
                    : "bg-gray-800 text-white hover:bg-gray-700"
                }`}
              >
                {darkMode ? <Sun size={16} /> : <Moon size={16} />}{" "}
                {darkMode ? "Light Mode" : "Dark Mode"}
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShowGuide(true)}
                  className="w-full py-2 px-4 text-sm bg-teal-600 text-white rounded-lg flex items-center justify-center gap-2 font-medium shadow-sm hover:bg-teal-700 transition-colors"
                >
                  <Info size={16} /> Guide
                </button>
                <button
                  onClick={exportPdf}
                  className="w-full py-2 px-4 text-sm bg-green-600 text-white rounded-lg flex items-center justify-center gap-2 font-medium shadow-sm hover:bg-green-700 transition-colors"
                >
                  <Download size={16} /> Export
                </button>
              </div>
            </div>
          </div>

          {/* ACTIVE PROJECTS CONTAINER - Flex-1 so it absorbs all remaining space at bottom */}
          <div className="flex flex-col flex-1 mt-6 overflow-hidden">
            <div
              className={`flex flex-col h-full p-4 rounded-xl border shadow-sm overflow-hidden ${
                darkMode
                  ? "bg-gray-900/50 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-3 shrink-0">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Active Projects
                </p>
                <button
                  onClick={createWorkspace}
                  className="p-1 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-gray-700 transition-colors"
                  title="New Project Window"
                >
                  <Plus size={14} />
                </button>
              </div>

              {/* Strict Overflow for just the list */}
              <div className="flex-1 overflow-y-auto sidebar-scroll pr-2 space-y-2">
                {workspaces.map((w) => (
                  <div
                    key={w.id}
                    onClick={() => setActiveWorkspaceId(w.id)}
                    className={`p-3 flex items-center justify-between rounded-lg cursor-pointer border transition-colors ${
                      w.id === activeWorkspaceId
                        ? darkMode
                          ? "bg-blue-900/30 border-blue-500/50"
                          : "bg-blue-50 border-blue-200"
                        : darkMode
                        ? "bg-gray-800 border-transparent hover:border-gray-600"
                        : "bg-white border-transparent hover:border-gray-200 shadow-sm"
                    }`}
                  >
                    {isRenamingWorkspace === w.id ? (
                      <input
                        type="text"
                        value={workspaceRenameValue}
                        onChange={(e) =>
                          setWorkspaceRenameValue(e.target.value)
                        }
                        onBlur={confirmWorkspaceRename}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") confirmWorkspaceRename();
                          if (e.key === "Escape") cancelWorkspaceRename();
                        }}
                        className={`w-full text-sm px-2 py-1 rounded border border-blue-500 outline-none ${
                          darkMode
                            ? "bg-gray-700 text-white"
                            : "bg-white text-gray-900"
                        }`}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <div className="flex items-center justify-between w-full">
                        <span
                          className={`text-sm truncate pr-2 ${
                            w.id === activeWorkspaceId
                              ? darkMode
                                ? "font-bold text-blue-400"
                                : "font-bold text-blue-700"
                              : ""
                          }`}
                        >
                          📊 {w.name}
                        </span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={(e) =>
                              startRenamingWorkspace(w.id, w.name, e)
                            }
                            className={`p-1.5 rounded-md transition-colors ${
                              darkMode
                                ? "text-gray-400 hover:bg-gray-700 hover:text-blue-400"
                                : "text-gray-500 hover:bg-gray-200 hover:text-blue-600"
                            }`}
                            title="Rename Project"
                          >
                            <span style={{ fontSize: "12px" }}>✏️</span>
                          </button>
                          {workspaces.length > 1 && (
                            <button
                              onClick={(e) => deleteWorkspace(w.id, e)}
                              className={`p-1.5 rounded-md transition-colors ${
                                darkMode
                                  ? "text-gray-400 hover:bg-red-900/50 hover:text-red-400"
                                  : "text-gray-500 hover:bg-red-100 hover:text-red-600"
                              }`}
                              title="Close Project"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* --- MAIN CONTENT AREA --- */}
        <div className="flex-1 flex flex-col h-full overflow-hidden relative">
          {showPie && (
            <div className="flex-1 flex flex-col p-6 overflow-hidden">
              <div
                className={`mb-4 p-4 rounded-xl border ${
                  darkMode
                    ? "bg-gray-800 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
                style={{ flexShrink: 0 }}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold">
                    Carbon Emissions Pie Chart - {currentWorkspace.name}
                  </h3>
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center rounded overflow-hidden border ${
                        darkMode
                          ? "border-gray-600 bg-gray-700"
                          : "border-gray-300 bg-gray-100"
                      }`}
                    >
                      <button
                        onClick={() =>
                          setPieChartZoom((z) => Math.max(0.5, z - 0.2))
                        }
                        className="px-3 py-1 hover:bg-gray-300 dark:hover:bg-gray-600 font-bold transition-colors"
                      >
                        -
                      </button>
                      <span className="px-2 text-sm font-medium w-12 text-center">
                        {Math.round(pieChartZoom * 100)}%
                      </span>
                      <button
                        onClick={() =>
                          setPieChartZoom((z) => Math.min(3, z + 0.2))
                        }
                        className="px-3 py-1 hover:bg-gray-300 dark:hover:bg-gray-600 font-bold transition-colors"
                      >
                        +
                      </button>
                    </div>
                    {selectedProcess !== null && (
                      <button
                        type="button"
                        onClick={() => setSelectedProcess(null)}
                        className="px-4 py-1.5 rounded bg-blue-500 text-white hover:bg-blue-600 text-sm font-medium transition-colors"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 flex" style={{ minHeight: 0 }}>
                <div className="flex-1 overflow-auto sidebar-scroll pr-4">
                  <div
                    style={{
                      width: `${100 * pieChartZoom}%`,
                      height: `${100 * pieChartZoom}%`,
                      minHeight: "500px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "auto",
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart
                        margin={{ top: 40, right: 60, left: 60, bottom: 40 }}
                      >
                        <Pie
                          data={organizedPieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius="75%"
                          innerRadius="40%"
                          labelLine={{
                            stroke: darkMode ? "#9ca3af" : "#6b7280",
                            strokeWidth: 1,
                          }}
                          label={({ name, percent }) => {
                            if (percent < 0.015) return ""; // Hide text for tiny slices to prevent clutter
                            const displayName =
                              name.length > 18
                                ? name.substring(0, 18) + "..."
                                : name;
                            return `${displayName} (${(percent * 100).toFixed(
                              1
                            )}%)`;
                          }}
                          paddingAngle={1}
                          onClick={(data) =>
                            setSelectedProcess(
                              selectedProcess === data.originalIndex
                                ? null
                                : data.originalIndex
                            )
                          }
                        >
                          {organizedPieData.map((entry) => (
                            <Cell
                              key={`cell-${entry.originalIndex}`}
                              fill={entry.fill}
                              stroke={entry.operationColor}
                              strokeWidth={
                                selectedProcess === entry.originalIndex ? 6 : 3
                              }
                              strokeDasharray={
                                entry.operation === "Uncategorized"
                                  ? "5,5"
                                  : "0"
                              }
                              style={{
                                filter:
                                  selectedProcess === entry.originalIndex
                                    ? "brightness(1.2) saturate(1.2)"
                                    : selectedProcess !== null
                                    ? "brightness(0.6)"
                                    : "brightness(1)",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                              }}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name, props) => [
                            `${value.toFixed(2)} kg CO₂e`,
                            `${props.payload.fullName}`,
                          ]}
                          labelFormatter={(label, payload) => {
                            if (payload && payload[0]) {
                              const data = payload[0].payload;
                              return (
                                <div>
                                  <div className="font-semibold">
                                    {data.fullName}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    Operation: {data.operation} | Module:{" "}
                                    {data.module}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {(
                                      (data.value /
                                        pieDataEntries.reduce(
                                          (sum, e) => sum + e.value,
                                          0
                                        )) *
                                      100
                                    ).toFixed(1)}
                                    % of total
                                  </div>
                                </div>
                              );
                            }
                            return label;
                          }}
                          contentStyle={{
                            backgroundColor: darkMode ? "#374151" : "#ffffff",
                            border: `1px solid ${
                              darkMode ? "#6b7280" : "#d1d5db"
                            }`,
                            borderRadius: "8px",
                            color: darkMode ? "#f9fafb" : "#1f2937",
                            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                            fontSize: 16,
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {selectedProcess !== null && (
                    <div
                      className={`mt-4 p-4 rounded-lg border-2 max-w-md ${
                        darkMode
                          ? "bg-gray-800 border-blue-500"
                          : "bg-blue-50 border-blue-500"
                      }`}
                    >
                      <h4 className="font-bold mb-2 text-blue-500">
                        Selected Process #{selectedProcess + 1}
                      </h4>
                      <div className="text-sm">
                        <div className="font-semibold text-lg mb-1">
                          {pieDataEntries[selectedProcess]?.fullName}
                        </div>
                        <div className="mb-2">
                          <span className="font-medium">
                            {pieDataEntries[selectedProcess]?.value.toFixed(2)}{" "}
                            kg CO₂e
                          </span>
                          <span className="mx-2">•</span>
                          <span>
                            {(
                              (pieDataEntries[selectedProcess]?.value /
                                pieDataEntries.reduce(
                                  (sum, e) => sum + e.value,
                                  0
                                )) *
                              100
                            ).toFixed(1)}
                            %
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div>
                            <strong>Operation:</strong>{" "}
                            {pieDataEntries[selectedProcess]?.operation}
                          </div>
                          <div>
                            <strong>Module:</strong>{" "}
                            {pieDataEntries[selectedProcess]?.module}
                          </div>
                          <div>
                            <strong>EPD Type:</strong>{" "}
                            {pieDataEntries[selectedProcess]?.epdType}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className={`w-80 ml-4 shrink-0 rounded-xl border shadow-lg flex flex-col overflow-hidden ${
                    darkMode
                      ? "bg-gray-800 border-gray-600"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <div
                    className={`p-4 border-b shrink-0 ${
                      darkMode
                        ? "border-gray-700 bg-gray-800/50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <h3 className="text-lg font-bold mb-1">Process Legend</h3>
                    <div className="text-xs text-gray-500">
                      {currentEntries.length} processes • Click to highlight
                    </div>
                  </div>
                  <div
                    className="p-4 overflow-y-auto sidebar-scroll space-y-6 pr-3"
                    style={{ flex: 1 }}
                  >
                    {currentEntries.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-3">Operations</h4>
                        <div className="space-y-2">
                          {(() => {
                            const operations = [
                              ...new Set(
                                currentEntries.map(
                                  (e) => e.operation || "Uncategorized"
                                )
                              ),
                            ];
                            return operations.map((op) => (
                              <div
                                key={op}
                                className={`flex items-center justify-between text-xs p-2 rounded border ${
                                  darkMode
                                    ? "bg-gray-700 border-gray-600"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`w-3 h-3 border-2 rounded flex-shrink-0 ${
                                      op === "Uncategorized"
                                        ? "border-dashed"
                                        : ""
                                    }`}
                                    style={{
                                      borderColor:
                                        op === "Uncategorized"
                                          ? "#666666"
                                          : "#8b5cf6",
                                      backgroundColor:
                                        op === "Uncategorized"
                                          ? "transparent"
                                          : "#8b5cf615",
                                    }}
                                  />
                                  <span
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "140px",
                                    }}
                                    title={op}
                                  >
                                    {op}
                                  </span>
                                </div>
                                <span className="font-bold">
                                  {
                                    currentEntries.filter(
                                      (e) =>
                                        (e.operation || "Uncategorized") === op
                                    ).length
                                  }
                                </span>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                    {currentEntries.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-3">EPD Types</h4>
                        <div className="space-y-2">
                          <div
                            className={`flex items-center justify-between text-xs p-2 rounded border ${
                              darkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded flex-shrink-0"
                                style={{ backgroundColor: "#22C55E" }}
                              />
                              <span>Uploaded EPD</span>
                            </div>
                            <span className="font-bold">
                              {
                                pieDataEntries.filter(
                                  (e) => e.epdType === "Uploaded EPD"
                                ).length
                              }
                            </span>
                          </div>
                          <div
                            className={`flex items-center justify-between text-xs p-2 rounded border ${
                              darkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded flex-shrink-0"
                                style={{ backgroundColor: "#FFA500" }}
                              />
                              <span>Custom EPD</span>
                            </div>
                            <span className="font-bold">
                              {
                                pieDataEntries.filter(
                                  (e) => e.epdType === "Custom EPD"
                                ).length
                              }
                            </span>
                          </div>
                          <div
                            className={`flex items-center justify-between text-xs p-2 rounded border ${
                              darkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded flex-shrink-0"
                                style={{ backgroundColor: "#10b981" }}
                              />
                              <span>Alternative Applied</span>
                            </div>
                            <span className="font-bold">
                              {
                                pieDataEntries.filter(
                                  (e) => e.epdType === "Alternative Applied"
                                ).length
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {showReduction && (
            <div className="absolute inset-0 overflow-y-auto p-6">
              <CarbonDashboard
                entries={currentWorkspace.entries}
                setEntries={(newEntries) => {
                  saveStateForUndo();
                  updateWorkspace(currentWorkspace.id, { entries: newEntries });
                }}
                factors={factors}
                darkMode={darkMode}
                onUndo={handleUndo}
                processTabs={currentWorkspace.processTabs}
                setProcessTabs={(tabs) =>
                  updateWorkspace(currentWorkspace.id, {
                    processTabs:
                      typeof tabs === "function"
                        ? tabs(currentWorkspace.processTabs)
                        : tabs,
                  })
                }
                activeProcessTab={currentWorkspace.activeProcessTab}
                setActiveProcessTab={(tab) =>
                  updateWorkspace(currentWorkspace.id, {
                    activeProcessTab: tab,
                  })
                }
                primaryTab={currentWorkspace.primaryTab}
                setPrimaryTab={(tab) =>
                  updateWorkspace(currentWorkspace.id, { primaryTab: tab })
                }
                originalBaselineEntries={
                  currentWorkspace.originalBaselineEntries
                }
              />
            </div>
          )}

          {showEconomicModeling && (
            <div className="absolute inset-0 overflow-y-auto p-6">
              <EconomicModeling
                entries={currentWorkspace.entries}
                factors={factors}
                darkMode={darkMode}
                totalEmissions={totalEmissions}
                onUndo={handleUndo}
                selectedPricingSystem={currentWorkspace.selectedPricingSystem}
                setSelectedPricingSystem={(sys) =>
                  updateWorkspace(currentWorkspace.id, {
                    selectedPricingSystem: sys,
                  })
                }
                carbonPricing={currentWorkspace.carbonPricing}
                setCarbonPricing={(pricing) =>
                  updateWorkspace(currentWorkspace.id, {
                    carbonPricing:
                      typeof pricing === "function"
                        ? pricing(currentWorkspace.carbonPricing)
                        : pricing,
                  })
                }
                economicInputs={currentWorkspace.economicInputs}
                setEconomicInputs={(inputs) =>
                  updateWorkspace(currentWorkspace.id, {
                    economicInputs:
                      typeof inputs === "function"
                        ? inputs(currentWorkspace.economicInputs)
                        : inputs,
                  })
                }
                showPricingDialog={showPricingDialog}
                setShowPricingDialog={setShowPricingDialog}
                setProjectCashFlows={(cf) => {
                  updateWorkspace(currentWorkspace.id, {
                    financialResults: {
                      ...currentWorkspace.financialResults,
                      ...cf,
                    },
                  });
                }}
              />
            </div>
          )}

          {showCompare && (
            <div className="absolute inset-0 overflow-y-auto p-6">
              <div
                className={`mb-6 p-6 rounded-xl border ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <h2 className="text-2xl font-bold mb-2">
                  ⚖️ Cross-Project Comparison
                </h2>
                <p className="text-sm">
                  Compare key environmental and economic metrics across all your
                  active project windows side-by-side.
                </p>
              </div>

              {workspaces.length < 2 ? (
                <div
                  className={`flex flex-col items-center justify-center p-12 border-2 rounded-xl ${
                    darkMode
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-300 bg-gray-50"
                  }`}
                  style={{ borderStyle: "dashed" }}
                >
                  <div className="text-4xl mb-4">🗂️</div>
                  <h3 className="text-xl font-semibold mb-2">
                    Add More Projects to Compare
                  </h3>
                  <p className="text-center">
                    You currently only have one active project window. Click
                    "New Project Window" in the sidebar to create alternative
                    facility designs and compare them here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div
                    className={`p-6 rounded-xl border overflow-hidden ${
                      darkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <h3 className="text-lg font-semibold mb-4">
                      Metric Data Table
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead
                          className={`text-xs uppercase ${
                            darkMode
                              ? "bg-gray-700 text-white"
                              : "bg-gray-200 text-gray-700"
                          }`}
                        >
                          <tr>
                            <th className="p-3">Project Name</th>
                            <th className="p-3 text-center">Total Processes</th>
                            <th className="p-3 text-center">
                              Carbon Footprint
                            </th>
                            <th className="p-3 text-right">
                              Net Present Value
                            </th>
                            <th className="p-3 text-right">IRR</th>
                            <th className="p-3 text-right">Payback</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workspaces.map((w) => (
                            <tr
                              key={w.id}
                              className={`border-b ${
                                darkMode ? "border-gray-700" : "border-gray-200"
                              }`}
                            >
                              <td className="p-3 font-medium">
                                {w.name}{" "}
                                {w.id === activeWorkspaceId ? "(Active)" : ""}
                              </td>
                              <td className="p-3 text-center">
                                {w.entries.length}
                              </td>
                              <td className="p-3 text-center">
                                {w.entries
                                  .reduce((sum, e) => sum + e.total, 0)
                                  .toFixed(1)}{" "}
                                kgCO₂e
                              </td>
                              <td
                                className={`p-3 text-right font-bold ${
                                  w.financialResults.npv < 0
                                    ? "text-red-500"
                                    : "text-green-500"
                                }`}
                              >
                                $
                                {w.financialResults.npv.toLocaleString(
                                  undefined,
                                  { maximumFractionDigits: 0 }
                                )}
                              </td>
                              <td
                                className={`p-3 text-right ${
                                  w.financialResults.irr <
                                  w.economicInputs.discountRate
                                    ? "text-red-500"
                                    : "text-green-500"
                                }`}
                              >
                                {w.financialResults.irr.toFixed(1)}%
                              </td>
                              <td className="p-3 text-right">
                                {w.financialResults.payback === Infinity
                                  ? "Never"
                                  : w.financialResults.payback.toFixed(1) +
                                    " yrs"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`p-4 rounded-xl border ${
                        darkMode
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                        <span className="text-base">🏭</span> Total Carbon
                        Footprint
                      </h3>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={workspaces.map((w) => ({
                              name: w.name,
                              Emissions: w.entries.reduce(
                                (sum, e) => sum + e.total,
                                0
                              ),
                            }))}
                            margin={{ top: 15, right: 10, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke={darkMode ? "#4b5563" : "#e5e7eb"}
                              vertical={false}
                            />
                            <XAxis
                              dataKey="name"
                              tick={{
                                fill: darkMode ? "#e5e7eb" : "#374151",
                                fontSize: 11,
                              }}
                              tickLine={false}
                              axisLine={false}
                            />
                            <YAxis
                              tick={{
                                fill: darkMode ? "#e5e7eb" : "#374151",
                                fontSize: 11,
                              }}
                              tickLine={false}
                              axisLine={false}
                              width={45}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: darkMode
                                  ? "#1f2937"
                                  : "#ffffff",
                                border: `1px solid ${
                                  darkMode ? "#4b5563" : "#e5e7eb"
                                }`,
                                color: darkMode ? "#f3f4f6" : "#111827",
                                fontSize: "12px",
                              }}
                              formatter={(value) => [
                                `${value.toFixed(1)} kgCO₂e`,
                                "Total Emissions",
                              ]}
                            />
                            <Bar
                              dataKey="Emissions"
                              fill="#ef4444"
                              radius={[4, 4, 0, 0]}
                              maxBarSize={60}
                            >
                              <LabelList
                                dataKey="Emissions"
                                position="top"
                                formatter={(val) => val.toFixed(0)}
                                fill={darkMode ? "#e5e7eb" : "#374151"}
                                fontSize={10}
                              />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div
                      className={`p-4 rounded-xl border ${
                        darkMode
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                        <span className="text-base">💰</span> Net Present Value
                        (NPV)
                      </h3>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={workspaces.map((w) => ({
                              name: w.name,
                              NPV: w.financialResults.npv,
                            }))}
                            margin={{ top: 15, right: 10, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke={darkMode ? "#4b5563" : "#e5e7eb"}
                              vertical={false}
                            />
                            <XAxis
                              dataKey="name"
                              tick={{
                                fill: darkMode ? "#e5e7eb" : "#374151",
                                fontSize: 11,
                              }}
                              tickLine={false}
                              axisLine={false}
                            />
                            <YAxis
                              tick={{
                                fill: darkMode ? "#e5e7eb" : "#374151",
                                fontSize: 11,
                              }}
                              tickFormatter={(val) =>
                                `$${(val / 1000).toFixed(0)}k`
                              }
                              tickLine={false}
                              axisLine={false}
                              width={50}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: darkMode
                                  ? "#1f2937"
                                  : "#ffffff",
                                border: `1px solid ${
                                  darkMode ? "#4b5563" : "#e5e7eb"
                                }`,
                                color: darkMode ? "#f3f4f6" : "#111827",
                                fontSize: "12px",
                              }}
                              formatter={(value) => [
                                `$${value.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}`,
                                "NPV",
                              ]}
                            />
                            <Bar
                              dataKey="NPV"
                              radius={[4, 4, 0, 0]}
                              maxBarSize={60}
                            >
                              {workspaces.map((w, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    w.financialResults.npv >= 0
                                      ? "#22c55e"
                                      : "#ef4444"
                                  }
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!showPie &&
            !showReduction &&
            !showEconomicModeling &&
            !showCompare && (
              <div className="flex-1 flex flex-col h-full overflow-hidden">
                <div
                  className={`p-4 flex flex-wrap gap-2 border-b shrink-0 ${
                    darkMode
                      ? "bg-gray-800 border-gray-600"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2 w-full">
                    <h3 className="text-lg font-semibold">
                      Add Process to EN15804 Module:
                    </h3>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium">
                        Process Legend:
                      </span>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1">
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#22C55E",
                              borderRadius: "2px",
                            }}
                          />{" "}
                          <span>Uploaded EPD</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#FFA500",
                              borderRadius: "2px",
                            }}
                          />{" "}
                          <span>Custom EPD</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#10b981",
                              borderRadius: "2px",
                            }}
                          />{" "}
                          <span>Alternative</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(EN15804).map((m) => (
                      <button
                        key={m}
                        onClick={() => onModuleClick(m)}
                        className="p-1.5 px-3 bg-green-600 hover:bg-green-700 transition-colors shadow-sm text-white rounded flex items-center gap-1 text-sm font-medium"
                      >
                        <Plus size={14} /> {m}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex-1 relative" style={{ minHeight: 0 }}>
                  <ReactFlowProvider>
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      fitView
                      panOnScroll
                      zoomOnScroll
                      zoomOnPinch
                      panOnDrag
                      onNodeClick={handleNodeClick}
                    >
                      <MiniMap />
                      <Controls style={{ color: darkMode ? "#fff" : "#000" }} />
                      <Background />
                    </ReactFlow>
                  </ReactFlowProvider>
                </div>

                <div
                  className={`shrink-0 border-t ${
                    darkMode
                      ? "bg-gray-800 border-gray-600"
                      : "bg-white border-gray-300"
                  }`}
                  style={{ height: "300px" }}
                >
                  <div className="flex justify-between items-center px-4 p-2 border-b dark:border-gray-700 border-gray-200">
                    <h3 className="text-base font-semibold">
                      Emissions Summary by EN15804 Module
                    </h3>
                    <div className="text-sm">Total: {total} kgCO₂e</div>
                  </div>
                  <div style={{ height: "calc(100% - 45px)", width: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={summary}
                        margin={{ top: 20, right: 20, left: 20, bottom: 25 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={darkMode ? "#4b5563" : "#e5e7eb"}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="moduleKey"
                          tick={{
                            fill: darkMode ? "#e5e7eb" : "#374151",
                            fontSize: 11,
                          }}
                          angle={0}
                          interval={0}
                        />
                        <YAxis
                          tick={{
                            fill: darkMode ? "#e5e7eb" : "#374151",
                            fontSize: 11,
                          }}
                          width={50}
                          tickFormatter={(val) =>
                            val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val
                          }
                        />
                        <Tooltip
                          formatter={(value) => [
                            `${value.toFixed(2)} kgCO₂e`,
                            "Emissions",
                          ]}
                          labelFormatter={(label) => EN15804[label] || label}
                          contentStyle={{
                            backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                            border: `1px solid ${
                              darkMode ? "#4b5563" : "#e5e7eb"
                            }`,
                            borderRadius: "6px",
                            color: darkMode ? "#ffffff" : "#1f2937",
                            fontSize: "13px",
                          }}
                        />
                        <Bar
                          dataKey="emissions"
                          fill={darkMode ? "#60a5fa" : "#3b82f6"}
                        >
                          <LabelList
                            dataKey="emissions"
                            position="top"
                            formatter={(value) =>
                              value > 0 ? value.toFixed(0) : ""
                            }
                            fill={darkMode ? "#e5e7eb" : "#374151"}
                            fontSize={10}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

          <ProcessDialog
            showDialog={showDialog}
            setShowDialog={(val) => {
              if (document.activeElement) document.activeElement.blur();
              window.focus();
              setShowDialog(val);
            }}
            form={form}
            setForm={setForm}
            factors={factors}
            activeIndex={activeIndex}
            onSave={addOrUpdate}
            onDelete={deleteEntry}
            title={activeIndex !== null ? "Edit Process" : "Add Process"}
            darkMode={darkMode}
            entries={currentWorkspace.entries}
            activeModule={activeModule}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
